import React, { useEffect, useMemo, useRef, useState } from 'react';
import './EmployeeTimesheetReviewConsole.css';
import {
  AlertCircle, AlertTriangle, Activity, ArrowRight, Bell, Calendar, CalendarClock,
  CheckCircle, ChevronDown, ChevronRight, ClipboardList, Clock, Coffee, DollarSign,
  History, Info, LifeBuoy, Loader2, LogIn, LogOut, MapPin, MinusCircle, Moon, Package, Pencil, Printer, RefreshCw, ScrollText, Send, Sparkles, Sun,
  Truck, Unlock, User, UserCheck, UserCog, Wrench, X,
} from 'lucide-react';
import WeekPickerNav from '../components/WeekPickerNav';
import { MapContainer, TileLayer, Circle, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { bluePinIcon, ensureProjectMapMarkerIcon, MAP_TILE_ATTRIBUTION, MAP_TILE_MAX_ZOOM, MAP_TILE_URL } from '../components/projectMapLeaflet';
import { PunchType, TimeEntryType } from '../interfaces/TimesheetInterface';
import {
  Day, Employee, EmployeeOperationalTask, EmployeeSickClaim, GpsException, Project,
  HourlyProject, TimeEntry, EmployeeTask, MiscEmployeeTask,
  EmployeeCustomerServiceWarranty, EmployeeWorkOrderTask,
  TimesheetAnswerEntry, TimesheetApprovalEntry,
} from '../interfaces/EmployeeInterface';
import { TimeOffRequestSummary } from '../interfaces/TimeOffRequest';
import { HolidayCalendarEntry, HolidayTenureVerdict, TenureStatus, WorkedSources } from '../interfaces/TimeOff';
import { DriveTimeEntry } from '../api/timesheetReview';
import { PayPeriod } from '../interfaces/PayPeriodInterface';
import {
  PayrollTimesheetEntryCounts,
  TimesheetPreviewDay,
  TimesheetPreviewEditable,
} from '../interfaces/TimesheetEditInterface';
import InactiveBadge from '../components/payrollEdit/InactiveBadge';
import MiscTaskCodeModal, { MiscTaskCodeTarget } from '../components/v2/MiscTaskCodeModal';
import ProductionSplitModal, { ProductionSplitTarget } from '../components/v2/ProductionSplitModal';
import OnCallPayModal from '../components/v2/OnCallPayModal';
import { onCallSummary, parseOnCallAmount, savedOnCall } from '../components/v2/onCallPayModel';
import DayPunchMapModal from '../components/v2/DayPunchMapModal';
import { dayHasPunchCoords } from '../util/dayPunchMap';
import { describeNpmServiceOrder } from '../util/serviceOrderDisplay';
import { paidApprovedTimeOffHours, splitUnpaid, timeOffCountsTowardHoliday } from '../util/timeOffUnpaid';
import { ProductionPayResult, setPoNumber, upsertProductionEntry } from '../api/productionPay';
import { OnCallPayInfo, OnCallServiceOrderOption, OnCallTrade, setOnCallPay } from '../api/onCallPay';
import HistoryPanel from '../components/payrollEdit/HistoryPanel';
import PunchHistoryModal from '../components/payrollEdit/PunchHistoryModal';
import {
  indexPunchHistory,
  offersPunchHistory,
  overnightBadgeLabel,
  overnightDayLabel,
  overnightHistoryNote,
  punchHistoryKey,
  punchHistoryTitle,
  removedPunchesForDay,
} from '../components/payrollEdit/punchHistory';
import TimesheetEditManager from '../managers/TimesheetEditManager';
import { PunchHistoryItem } from '../interfaces/TimesheetEditInterface';
import AuditLogPanel from '../components/AuditLogPanel';
import AuditManager from '../managers/AuditManager';
import { Audit } from '../interfaces/AuditInterface';
import config from '../configLoader';
import { translator } from '../i18n/localization';
import { printTimesheet } from '../util/PrintUtil';
import { formatHours, formatDecimalHours, formatDecimalHoursNatural } from '../util/hoursFormat';
import { aggregateWeeklyProjectTasks, weeklyProjectTasksTotal, WeeklyProjectTasks } from '../util/weeklyTasks';
import {
  ApprovalSlotRequest,
  approveSlotsSequentially,
  summarizeSlotOutcomes,
} from '../util/slotApprovals';
import { ModifiedDutyApprovalLine, ModifiedDutyApprovalsSection } from '../components/v2/ModifiedDutyApprovals';
import { multiSlotSupers } from '../util/modifiedDutyApprovals';
import type { RequiredSuperSlot } from '../interfaces/EmployeeInterface';

// ── Types ────────────────────────────────────────────────────────────────────

/** `managerPending` (1st Approval Pending) exists only for tenants with
 *  `allowManagerApproval`; every other surface treats it as one more stage. */
export type StatusKey = 'unsubmitted' | 'managerPending' | 'submitted' | 'supervisorApproved' | 'payrollApproved';

/** Translator keys for the stage labels. NPM (languagePrefix `npmech`) overrides the
 *  two approval stages with "1st / 2nd Approval Pending"; every other tenant reads
 *  the default. `supervisorApproved` says "Payroll Pending" for ALL tenants — the
 *  global rename of the old "Supervisor Approved" (design §7.2). */
const BUCKET_LABEL_KEYS: Record<StatusKey, string> = {
  unsubmitted: 'TimesheetBucketUnsubmitted',
  managerPending: 'TimesheetBucketManagerPending',
  submitted: 'TimesheetBucketSubmitted',
  supervisorApproved: 'TimesheetBucketPayrollPending',
  payrollApproved: 'TimesheetBucketPayrollApproved',
};

/** Used when the key is missing from strings.json (findString answers `undefined`
 *  or "N/A"), so a label is never blank on a tenant whose bundle is behind. */
const BUCKET_LABEL_FALLBACKS: Record<StatusKey, string> = {
  unsubmitted: 'Unsubmitted',
  managerPending: 'Manager Pending',
  submitted: 'Pending Approval',
  supervisorApproved: 'Payroll Pending',
  payrollApproved: 'Payroll Approved',
};

/** The one place a stage's display label is resolved — board, console and the v1-style
 *  review page all read it, so the vocabulary can never drift between them. */
export function bucketLabel(status: StatusKey): string {
  const translated = translator.translate(BUCKET_LABEL_KEYS[status]);
  return typeof translated === 'string' && translated && translated !== 'N/A'
    ? translated
    : BUCKET_LABEL_FALLBACKS[status];
}

export interface TimeOffDayEntry {
  requestID: number;
  category: string;
  hours: number;
  status: 'PENDING' | 'APPROVED' | 'DENIED' | 'CANCELLED';
  /** Unpaid Time (TAS-586): shown, never counted in Hol / PTO. Missing = paid. */
  isUnpaid?: boolean;
  /** Whether this day counts as "worked" for the holiday rule. Missing = true. */
  countsForHolidayEligibility?: boolean;
}

type EligibilityStatus = 'pass' | 'fail' | 'outside' | 'na';

interface HolidayEligibility {
  /** Days-employed leg. Comes straight from the server verdict — tenure is a
   *  property of the hire date and the holiday date, never of the loaded week.
   *  'na' = no waiting period configured, so the row is not rendered. */
  tenureStatus: TenureStatus;
  /** Fully-rendered tenure label, e.g. "Hired Mar 8, 2024 — eligible from Sep 4, 2024".
   *  Empty when tenureStatus is 'na'. */
  tenureNote: string;
  beforeStatus: EligibilityStatus;
  beforeDate: string;
  /** Suffix appended after the date in the rendered label, e.g. " — prior week".
   *  Computed here so the render stays dumb. Empty for in-period dates. */
  beforeNote: string;
  afterStatus: EligibilityStatus;
  afterDate: string;
  afterNote: string;
}

interface GpsModalData {
  label: string;
  jobName: string;
  punchLat: number;
  punchLng: number;
  siteLat: number | null;
  siteLng: number | null;
  radiusMiles: number;
  distanceMiles: number | null;
}

type MinWageTrueUpData = NonNullable<Day['minWageTrueUp']>;

interface MinWageModalData {
  trueUp: MinWageTrueUpData;
  dow: string;
  dateLabel: string;
}

type EntryType = 'piece' | 'misc' | 'hourly' | 'holiday' | 'sick' | 'drive' | 'meal' | 'so' | 'warranty' | 'service' | 'opTask' | 'workOrder';
type IssueType = 'meal' | 'missed' | 'gps' | 'edited' | 'drive' | 'manual' | 'minwage' | 'gap' | 'overnight';

interface PunchRow {
  key: string;
  type: EntryType;
  jobName: string;
  jobSub: string;
  soBadge?: string | null;
  /** TAS-514 (NPM) — "Awaiting validation" / "Not validated" on a service-order punch
   *  that has no Vista link yet. Null on a validated punch and on every other tenant. */
  soState?: string | null;
  projectBadge?: string | null;
  inTime: string | null;
  inISO: string | null;
  outTime: string | null;
  isMissingOut: boolean;
  durationMin: number | null;
  isEdited: boolean;
  isManual: boolean;
  /** Overnight on-call shift (2020CA/NV): the "Overnight" badge's tooltip when this punch
   *  ends at 12:00 AM and continues next day, or starts at 12:00 AM continuing from the
   *  previous day. Null/absent otherwise. The badge also opens the punch's history. */
  overnightLabel?: string | null;
  /** The punch history modal's overnight note ("… (12:00 AM split)"); null otherwise. */
  overnightNote?: string | null;
  isMealPenalty: boolean;
  gps: GpsModalData | null;
  issueGps: boolean;
  amount: number | null;
  /** TAS-365: `EntityType:EntityID` of the TimeEntry / TimeEntryHourly row behind a
   *  punch row — the key into the week's punch history. Absent on synthetic rows. */
  historyKey?: string;
  /** Wallcraft production totals only — the job/code the punch sits on, so the row's
   *  pencil can open the same day editor the week grid uses. Hourly rows only. */
  projectID?: number | null;
  accountCodeID?: number | null;
}

interface PieceGroup {
  projectName: string;
  projectID: number;
  altProjectID: string | null;
  totalAmount: number;
  rows: PieceRow[];
}

interface PieceRow {
  key: string;
  type: 'piece' | 'misc';
  code: string;
  description: string;
  phaseLot: string;
  amount: number;
  originalAmount?: number;
  projectName: string;
  /** Misc rows only — what the cost-code editor needs to target the record. */
  miscEmployeeTaskID?: number;
  accountCodeID?: number | null;
}

interface DayCol {
  date: string;
  dow: string;
  dateLabel: string;
  day: Day | null;
  holiday: HolidayCalendarEntry | null;
  holidayElig: HolidayEligibility | null;
  timeOffEntries: TimeOffDayEntry[];
  driveEntries: DriveTimeEntry[];
  issues: IssueType[];
  /** Overnight on-call shift (2020CA/NV) on this day — the moon badge's tooltip, shown in
   *  the Alerts row and day flags. Informational: NOT in `issues`, never counted. */
  overnightLabel: string | null;
  totalWorkMin: number;
  totalOTMin: number;
  totalDTMin: number;
  totalPiece: number;
  /** Work order hour claims $ for the day (server-computed amountImplied — TAS-291). */
  totalWorkOrder: number;
  // Misc claims on the day, counted separately from the piece rollup above so
  // the matrix and the day header can flag them before the card is expanded.
  miscCount: number;
  miscAmount: number;
  holPTOHours: number;
  holPTOEligHours: number;
}

/** One row of the week matrix. Previously inferred from the array literal; named
 *  now that flag-gated rows (the §5 split pair) are spread in, which would
 *  otherwise defeat TypeScript's object-literal normalisation. */
interface MatrixRow {
  key: string;
  label: string;
  subLabel: string;
  icon: React.ReactElement;
  accent: string | null;
  getValue: (col: DayCol) => string | null;
  getWeekTotal: () => string;
  /** Alerts row: cells render issue badges rather than a value. */
  isIssueRow?: boolean;
  /** §5: the Piece/Warehouse ÷ Hourly pair reads as one tinted breakdown. */
  splitRow?: boolean;
  /** §4: small "weekly" tag beside the row label. */
  labelTag?: string;
  /** §4: replaces the plain Week cell (the jump button). */
  getWeekTotalNode?: () => React.ReactNode;
  /** Extra per-cell adornment (the Misc marker on Piece $). */
  getMarker?: (col: DayCol) => React.ReactNode;
  /** Same adornment on the Week cell. The only way a combine-in-week tenant can see a
   *  Misc flag at all: there its day cells carry no piece figure and so no per-day marker. */
  getWeekMarker?: () => React.ReactNode;
  getSubValue?: (col: DayCol) => string | null;
  getWeekSub?: () => string | null;
  /** Wallcraft production (design §4): the cell is an interactive entry block rather
   *  than a value, so it renders its own node and does not jump to the day on click. */
  getCellNode?: (col: DayCol) => React.ReactNode;
  getCellClass?: (col: DayCol) => string;
  noCellJump?: boolean;
}

export interface ConsoleProps {
  timesheetData: Employee;
  driveTimeEntries: DriveTimeEntry[];
  timeOffRequests: TimeOffRequestSummary[];
  timeOffDetailMap: Record<string, TimeOffDayEntry[]>;
  holidays: HolidayCalendarEntry[];
  selectedStatus: StatusKey | null;
  payPeriods: PayPeriod[];
  reviewWeekIdx: number;
  onSelectWeek: (idx: number) => void;
  supervisorName?: string;
  approveLoading: boolean;
  reviewLoading: boolean;
  noTimesheetForWeek: boolean;
  onApproveTimeOff: (id: number) => void;
  onDenyTimeOff: (id: number) => void;
  onSendBack: (timesheetID: number) => void;
  onApproveWeek: () => void;
  onRefresh: () => void;
  /** Approval Access (WP8): whether the caller's role may approve the current stage.
   *  When omitted, defaults to allowed (legacy callers). */
  canApproveStagePerm?: boolean;
  /** Approval Access (WP8): whether the caller's role may unlock the current stage. */
  canUnlockStagePerm?: boolean;
  // ── Payroll Timesheet Modifications (M1-B3) — all optional and additive: legacy
  //    callers and flag-off tenants render byte-identical without them. ──────────
  /** Entry counts from `timesheetEdit/state` — Submit for employee disables at `total === 0`. */
  payrollEntryCounts?: PayrollTimesheetEntryCounts | null;
  /** The `timesheetEdit/preview` week+days (closed decision 1: the unsubmitted
   *  drill-down's Regular/OT/DT numbers are the SERVER's, never client arithmetic). */
  payrollPreview?: TimesheetPreviewEditable | null;
  /** True while the preview request is in flight — the matrix/KPI OT cells dash. */
  payrollPreviewLoading?: boolean;
  /** Present only when the caller may edit entries (feature flag + permission);
   *  navigates to the payroll editor route. */
  onEditAsPayroll?: () => void;
  /** Present only when the caller may submit for the employee; opens the submit wizard. */
  onSubmitForEmployee?: () => void;
  /** Change-history read affordance: the caller's viewTimesheets-or-admin verdict,
   *  mirroring the API guard on the history reads (NOT EditTimesheetEntries). With
   *  the feature flag, shows the Change history button on every drill-down status —
   *  the audit trail must stay reachable after submission, when Edit as payroll
   *  (the only prior door to the panel) no longer renders. */
  canViewHistoryPerm?: boolean;
  /** Present only when the caller may reassign this timesheet's supervisor (the
   *  ReassignTimesheetSupervisor permission, on the submitted / supervisorApproved
   *  statuses). Opens the board's supervisor picker; absent otherwise, so the
   *  button simply does not render. */
  onChangeSupervisor?: () => void;
  /** Misc task cost-code editor (legacy editMiscTaskAccountCodeAndDescription permission):
   *  when true, misc rows in Pieces Claimed get a pencil that opens the code/description
   *  modal. The console additionally hides it on payroll-approved weeks. */
  canEditMiscTaskCodePerm?: boolean;
  // ── NPM multi-tier review (design_handoff_npm_timesheet_review §3) — all
  //    optional: without them the approvals card stays exactly as shipped. ──────
  /** The SIGNED-IN admin's AdminEmployeeID (SiteContext `userID`). Sent as
   *  `adminEmployeeID` on every slot approval — errata item 7: the server records
   *  the body value, so this must never be the slot owner's ID. Also what "your
   *  slot" is matched on. Absent → no slot is ever treated as the caller's. */
  adminEmployeeID?: number;
  /** The signed-in admin's display name, for the on-behalf confirm copy. */
  adminName?: string;
  /** Called once the LAST outstanding slot at the current stage lands, with the
   *  stage it advanced FROM — the board turns that into its optimistic move +
   *  reconcile (`NEXT_BUCKET` already carries managerPending → submitted). */
  onStageAdvanced?: (fromStatus: StatusKey) => void;
  /** Transient message in the board's toast style. */
  onToast?: (message: string) => void;
  /** TAS-679 (WCDP Modified Duty): the caller's role may approve HR's slot on her behalf
   *  (admin || payroll || payrollApproveTimesheets). Used only when the payload carries
   *  `requiredSupers` with 2+ slots; absent/false → no on-behalf button. */
  canApproveOnBehalfModifiedDuty?: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function isoDateOnly(d: Date): string {
  return d.toISOString().split('T')[0];
}

function addDays(dateStr: string, n: number): string {
  const d = new Date(dateStr + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().split('T')[0];
}

function applyOffset(utcISO: string, offsetStr: string): Date {
  const base = new Date(utcISO);
  const m = (offsetStr ?? '').match(/([+-])(\d{2}):(\d{2})/);
  if (!m) return base;
  const sign = m[1] === '+' ? 1 : -1;
  const offMs = sign * (parseInt(m[2]) * 60 + parseInt(m[3])) * 60000;
  return new Date(base.getTime() + offMs);
}

/** Paid minutes on one punch: open punches, meals and inverted pairs count as zero.
 *  Offsets cancel in a same-punch subtraction, so the raw UTC pair is the duration. */
function punchPaidMinutes(entry: { inDateTime?: string | null; outDateTime?: string | null; type?: number }): number {
  if (!entry.inDateTime || !entry.outDateTime) return 0;
  if (entry.type === TimeEntryType.Meal) return 0;
  const inMs = new Date(entry.inDateTime).getTime();
  const outMs = new Date(entry.outDateTime).getTime();
  if (isNaN(inMs) || isNaN(outMs)) return 0;
  const minutes = Math.round((outMs - inMs) / 60000);
  return minutes > 0 ? minutes : 0;
}

/** Piece/Warehouse vs Hourly (Job) minutes for one day, per tenant (design §5).
 *
 *  Off the flag (WCDP/NPM) the API day totals already ARE the split: raw TimeEntry
 *  punches are piece, TimeEntryHourly punches are hourly.
 *
 *  On `pieceHourlySplitByAccountCode` (2020PH only) that table-of-origin
 *  rule is wrong — piece work is clocked into TimeEntryHourly too, and the ONLY marker
 *  is the account code: a group without one is a piece clock, a group with one is job
 *  hourly (drive-time synthetic rows carry an account code and so read as hourly).
 *  2020PH has no rows in TimeEntry at all, so reading `totalHourlyMinutes`
 *  verbatim booked their whole week to "Hourly (Job)".
 *
 *  Mirrors the API's `computePieceHourlySplit` (util/timesheetSnapshot/pieceSplit.ts),
 *  which is what the board's snapshot hours bar already shows, and v1's
 *  `get2020PieceAndHourlyMinutesForDay` (TimesheetPage.tsx). Keep the three in step.
 */
function dayPieceHourlyMinutes(day: Day | null, use2020Rule: boolean): { taskMin: number; hourlyMin: number } {
  if (!day) return { taskMin: 0, hourlyMin: 0 };
  if (!use2020Rule) {
    return { taskMin: day.totalTaskMinutes ?? 0, hourlyMin: day.totalHourlyMinutes ?? 0 };
  }
  let taskMin = 0;
  let hourlyMin = 0;
  for (const hp of day.hourlyProjects || []) {
    let groupMin = 0;
    for (const entry of hp.timeEntriesHourly || []) groupMin += punchPaidMinutes(entry as any);
    // A bare group carries an all-null account object, and 0 means "none".
    const acct = hp.account?.accountCodeID;
    if (acct != null && acct !== 0) hourlyMin += groupMin;
    else taskMin += groupMin;
  }
  // Kiosk / standalone punches live on day.timeEntries and are always piece.
  for (const entry of day.timeEntries || []) taskMin += punchPaidMinutes(entry as any);
  return { taskMin, hourlyMin };
}

function formatClock(d: Date): string {
  const h = d.getUTCHours() % 12 || 12;
  const m = d.getUTCMinutes().toString().padStart(2, '0');
  const ampm = d.getUTCHours() >= 12 ? 'p' : 'a';
  return `${h}:${m}${ampm}`;
}

/** Hours from a MINUTE count, in the tenant's format (TAS-75): 2475 → "41.25" for
 *  2020PH, "41:15" for WCDP/NPM. Zero dashes — the console's long-standing contract
 *  that an empty cell reads as nothing-to-report, which the format flag does not change. */
function fmtHrsOrDash(minutes: number): string {
  if (!minutes) return '—';
  return formatHours(minutes);
}

function fmtDollar(amount: number): string {
  if (!amount) return '—';
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00Z');
  return `${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** "Mar 8, 2024" — hire and eligibility dates are usually years away from the
 *  loaded week, so they always carry the year (unlike the adjacent-day labels). */
function fmtLongDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00Z');
  return `${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

const DOW_ABBR = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

/** "Tue Aug 18 · 2:11 PM" in the timesheet's own zone. `utc: true` means the
 *  offset was missing/malformed, the time shown is UTC, and the caller should
 *  render the small UTC tag (audit/approvals design, edge 6). */
function fmtStamp(utcISO: string, offsetStr: string | null | undefined): { text: string; utc: boolean } {
  const m = (offsetStr ?? '').match(/([+-])(\d{2}):(\d{2})/);
  const d = m ? applyOffset(utcISO, offsetStr as string) : new Date(utcISO);
  let h = d.getUTCHours();
  const ap = h < 12 ? 'AM' : 'PM';
  h = h % 12 || 12;
  return {
    text: `${DOW_ABBR[d.getUTCDay()]} ${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCDate()} · ${h}:${String(d.getUTCMinutes()).padStart(2, '0')} ${ap}`,
    utc: !m,
  };
}

/** Approval entries arrive in two wire shapes: supervisor/payroll rows carry the
 *  datetime top-level (TimesheetApprovalEntry), while manager/super/group rows
 *  nest it inside approvalEmployee. Normalize both; null = no approval yet. */
interface ApprovalVM { name: string; active: boolean; dt: string | null; off: string | null; }
function normalizeApproval(entry: any): ApprovalVM | null {
  const emp = entry?.approvalEmployee;
  if (!emp) return null;
  const dt = entry.approvalDateTime ?? emp.approvalDateTime ?? null;
  const off = entry.approvalDateTimeOffset ?? emp.approvalDateTimeOffset ?? null;
  const name = emp.name ? [emp.name.first, emp.name.last].filter(Boolean).join(' ') : '';
  return { name, active: emp.active !== false, dt: dt ? String(dt) : null, off };
}

function requiredApproverName(entry: any): string | null {
  const req = entry?.requiredEmployee;
  if (!req?.name) return null;
  return [req.name.first, req.name.last].filter(Boolean).join(' ') || null;
}

// ── Certification verdicts ────────────────────────────────────────────────────
// TimesheetQuestions.ExpectedAnswer drives the red/green flagging: 'Yes' or
// 'No' is the answer payroll expects; NULL (or an older API build that doesn't
// send the field) means indifferent — logged, shown neutrally, never flagged.
// Matching mirrors the TAS-123 trigger evaluator: answers arrive in the
// language the employee submitted in ("Sí"), and MealConditionalWaiver answers
// carry dates ("Yes - 07/01, 07/02"), so compare case-/accent-insensitively on
// the prefix before " - ".
const CERT_AFFIRMATIVE = new Set(['yes', 'si', 'y', 's']);
const CERT_NEGATIVE = new Set(['no', 'n']);

function normCertToken(s: string): string {
  return s
    .trim()
    .split(' - ')[0]
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

type CertVerdict = 'ok' | 'flag' | 'neutral';

function certVerdict(entry: TimesheetAnswerEntry): CertVerdict {
  const expected = entry.timesheetQuestion?.expectedAnswer;
  if (expected == null || expected.trim() === '') return 'neutral';
  const e = normCertToken(expected);
  const a = normCertToken(entry.answer ?? '');
  if (CERT_AFFIRMATIVE.has(e)) return CERT_AFFIRMATIVE.has(a) ? 'ok' : 'flag';
  if (CERT_NEGATIVE.has(e)) return CERT_NEGATIVE.has(a) ? 'ok' : 'flag';
  return a === e ? 'ok' : 'flag';
}

/** Same period label the payroll editor shows above the history panel
 *  (PayrollTimesheetEditPage / TimesheetReviewBoardPage convention). */
function formatPayPeriodRange(pp: { StartDate: string | Date; EndDate: string | Date }): string {
  const s = new Date(pp.StartDate);
  const e = new Date(pp.EndDate);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  const start = s.toLocaleDateString('en-US', opts);
  const end = s.getUTCMonth() === e.getUTCMonth()
    ? String(e.getUTCDate())
    : e.toLocaleDateString('en-US', opts);
  return `${start} – ${end}, ${e.getUTCFullYear()}`;
}

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  return phone;
}

function fmtDow(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00Z');
  return d.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
}


function haversineDistanceMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

/** Short descriptor for how the adjacent day was satisfied, for the label suffix.
 *  Plain hourly work needs no descriptor — the label already reads "Worked". */
function describeSource(source: WorkedSources | null | undefined): string {
  if (!source) return '';
  if (source.hourly) return '';
  if (source.piece || source.taskClaim) return 'piece work';
  if (source.timeOff) return 'time off';
  return '';
}

/** The tenure row's label, built purely from the server verdict. */
function describeTenure(tenure: HolidayTenureVerdict | null | undefined): string {
  if (!tenure || tenure.status === 'na') return '';
  if (tenure.status === 'unknown' || !tenure.hireDate) {
    return 'Hire date unknown — eligibility not checked';
  }
  const hired = `Hired ${fmtLongDate(tenure.hireDate)}`;
  const eligible = tenure.eligibleOn ? ` — eligible from ${fmtLongDate(tenure.eligibleOn)}` : '';
  // The waiting period is spelled out only on a failure, where it answers
  // "why not?"; on a pass the eligible-from date already says everything.
  const days = tenure.status === 'fail' && tenure.eligibilityDays != null
    ? ` (${tenure.eligibilityDays} days)`
    : '';
  return `${hired}${eligible}${days}`;
}

/** The banner's phrasing: one phrase per failing rule, each prefixed with the
 *  holiday name. Tenure first — it is the rule the server checks first. */
function holidayIneligibilityPhrases(
  holiday: HolidayCalendarEntry,
  elig: HolidayEligibility | undefined
): string[] {
  if (!elig) return [];
  const out: string[] = [];
  if (elig.tenureStatus === 'fail') {
    const tenure = holiday.eligibility?.tenure;
    const from = tenure?.eligibleOn ? ` (eligible from ${fmtLongDate(tenure.eligibleOn)})` : '';
    out.push(
      tenure?.eligibilityDays != null
        ? `hired less than ${tenure.eligibilityDays} days before the holiday${from}`
        : `hired too recently before the holiday${from}`
    );
  }
  if (elig.beforeStatus === 'fail') {
    out.push(`did not work the required day before (${fmtShortDate(elig.beforeDate)})`);
  }
  if (elig.afterStatus === 'fail') {
    out.push(`did not work the required day after (${fmtShortDate(elig.afterDate)})`);
  }
  return out.map((phrase) => `${holiday.name} — ${phrase}`);
}

function checkHolidayEligibility(
  holiday: HolidayCalendarEntry,
  days: Day[],
  timeOffDetailMap: Record<string, TimeOffDayEntry[]>,
  payPeriodStart: string,
  payPeriodEnd: string
): HolidayEligibility {
  // In-period check stays live so unsaved hour edits are reflected immediately.
  // Kept deliberately aligned with the server/export rule: hourly or piece
  // punches (totalWorkMinutes), a task claim, or an APPROVED *or PENDING*
  // time-off day all count as "worked" — time off only when its category counts
  // for holiday eligibility (TAS-586; missing flag = counts, as before).
  const workedOnDate = (date: string): boolean => {
    const day = days.find((d) => d.date.substring(0, 10) === date);
    if (day && (day.totalWorkMinutes ?? 0) > 0) return true;
    if (day && (day.projectEmployeeTasks ?? []).some((p) => (p.employeeTasks?.length ?? 0) > 0)) return true;
    return timeOffCountsTowardHoliday(timeOffDetailMap[date] ?? []);
  };
  const isInPeriod = (date: string): boolean => date >= payPeriodStart && date <= payPeriodEnd;
  const beforeDate = holiday.requiredWorkDayBeforeDate ?? addDays(holiday.holidayDate, -1);
  const afterDate = holiday.requiredWorkDayAfterDate ?? addDays(holiday.holidayDate, 1);

  // Outside the loaded week we cannot see the days, so we trust the server
  // verdict (computed from the database with the export's own rule). No verdict
  // — an older API, or a probe that failed — degrades to the old '?' state.
  const verdict = holiday.eligibility;
  const outsideLeg = (
    status: 'pass' | 'fail' | 'na' | undefined,
    source: WorkedSources | null | undefined,
    weekWord: string
  ): { status: EligibilityStatus; note: string } => {
    if (status !== 'pass' && status !== 'fail') {
      return { status: 'outside', note: ' — could not check (outside period)' };
    }
    const descriptor = status === 'pass' ? describeSource(source) : '';
    return {
      status,
      note: descriptor ? ` — ${descriptor} (${weekWord})` : ` — ${weekWord}`,
    };
  };

  const before: { status: EligibilityStatus; note: string } = !holiday.requireWorkDayBefore
    ? { status: 'na', note: '' }
    : !isInPeriod(beforeDate)
    ? outsideLeg(verdict?.beforeStatus, verdict?.beforeSource, 'prior week')
    : { status: workedOnDate(beforeDate) ? 'pass' : 'fail', note: '' };

  const after: { status: EligibilityStatus; note: string } = !holiday.requireWorkDayAfter
    ? { status: 'na', note: '' }
    : !isInPeriod(afterDate)
    ? outsideLeg(verdict?.afterStatus, verdict?.afterSource, 'next week')
    : { status: workedOnDate(afterDate) ? 'pass' : 'fail', note: '' };

  // Tenure is never recomputed locally: it depends only on the hire date and the
  // holiday date, both already resolved server-side. A verdict-less (older) API
  // degrades to 'na' — the row is hidden and nothing is blocked.
  const tenure = verdict?.tenure;

  return {
    tenureStatus: tenure?.status ?? 'na',
    tenureNote: describeTenure(tenure),
    beforeStatus: before.status,
    beforeDate,
    beforeNote: before.note,
    afterStatus: after.status,
    afterDate,
    afterNote: after.note,
  };
}

function buildProjectMap(day: Day): Map<number, Project> {
  const map = new Map<number, Project>();
  // The same project can appear under both hourlyProjects and
  // projectEmployeeTasks, and some API shapes (e.g. the unsubmitted
  // timesheet's piece-task projects) omit latitude/longitude entirely.
  // Never let a coordinate-less copy replace one that has site coords.
  const add = (p: Project) => {
    const existing = map.get(p.projectID);
    const existingHasCoords = !!(existing?.latitude && existing?.longitude);
    const incomingHasCoords = !!(p.latitude && p.longitude);
    if (!existing || (incomingHasCoords && !existingHasCoords)) {
      map.set(p.projectID, p);
    }
  };
  for (const hp of day.hourlyProjects ?? []) add(hp.project);
  for (const pet of day.projectEmployeeTasks ?? []) add(pet.project);
  return map;
}

function buildGpsModal(
  gpsExc: GpsException,
  projectMap: Map<number, Project>,
  jobName: string
): GpsModalData | null {
  const isIn = gpsExc.inException;
  const punchLat = isIn ? gpsExc.inLatitude : gpsExc.outLatitude;
  const punchLng = isIn ? gpsExc.inLongitude : gpsExc.outLongitude;
  if (!punchLat && !punchLng) return null;
  const proj = projectMap.get(gpsExc.projectID);
  const hasSite = !!(proj?.latitude && proj?.longitude);
  return {
    label: isIn ? 'Clock In' : 'Clock Out',
    jobName,
    punchLat,
    punchLng,
    siteLat: hasSite ? proj!.latitude! : null,
    siteLng: hasSite ? proj!.longitude! : null,
    radiusMiles: proj?.radiusMiles ?? 0.1,
    distanceMiles: hasSite
      ? haversineDistanceMiles(punchLat, punchLng, proj!.latitude!, proj!.longitude!)
      : null,
  };
}

function buildPunchRows(day: Day, driveEntries: DriveTimeEntry[], isSubmitted: boolean): PunchRow[] {
  const rows: PunchRow[] = [];
  const projectMap = buildProjectMap(day);
  // A TimeEntry punch has no job of its own; the day's first piece task is only a fair
  // stand-in when tasks are dated to the day they were worked. With weekly claiming every
  // task carries the pay period's first date, so the borrowed job is meaningless.
  const defaultPieceJobName = config.hideTimeEntryJobName
    ? '—'
    : (day.projectEmployeeTasks?.[0]?.project?.name ?? '—');
  const defaultPieceAltID = config.hideTimeEntryJobName
    ? null
    : (day.projectEmployeeTasks?.[0]?.project?.altProjectID ?? null);
  const driveMap = new Map<number, DriveTimeEntry>(driveEntries.map((e) => [e.driveTimeEntryID, e]));

  // timeEntries are always Piece (or Meal if type === 2)
  day.timeEntries.forEach((entry, i) => {
    const isMeal = entry.type === TimeEntryType.Meal;
    const inDate = entry.inDateTime ? applyOffset(entry.inDateTime, entry.inDateTimeOffset) : null;
    const outDate = entry.outDateTime ? applyOffset(entry.outDateTime, entry.outDateTimeOffset) : null;
    const durationMin =
      inDate && outDate ? (outDate.getTime() - inDate.getTime()) / 60000 : null;
    const gpsExc = entry.gpsException;
    let gps: GpsModalData | null = null;
    if (gpsExc && (gpsExc.inException || gpsExc.outException)) {
      const jobN = projectMap.get(gpsExc.projectID)?.name ?? defaultPieceJobName;
      gps = buildGpsModal(gpsExc, projectMap, jobN);
    }
    rows.push({
      key: `te-${i}`,
      historyKey: punchHistoryKey('TimeEntry', entry.timeEntryID),
      type: isMeal ? 'meal' : 'piece',
      jobName: isMeal ? 'Meal Break' : defaultPieceJobName,
      jobSub: isMeal && (day.noLunchException || day.fiveHourLunchException) ? 'Meal penalty' : '',
      projectBadge: isMeal ? null : defaultPieceAltID,
      inTime: inDate ? formatClock(inDate) : null,
      inISO: entry.inDateTime,
      outTime: outDate ? formatClock(outDate) : null,
      isMissingOut: !entry.outDateTime,
      durationMin,
      isEdited: entry.punchTypeIn === PunchType.ManualEdit || entry.punchTypeOut === PunchType.ManualEdit,
      isManual: entry.punchTypeIn === PunchType.ManualAdd || entry.punchTypeOut === PunchType.ManualAdd,
      isMealPenalty: isMeal && (day.noLunchException || day.fiveHourLunchException),
      gps,
      issueGps: !!(gpsExc?.inException || gpsExc?.outException),
      amount: null,
    });
  });

  // timeEntriesHourly: Drive (submitted) > Meal > CSW (Warranty/Cust Service) > SO > Hourly > Piece
  (day.hourlyProjects ?? []).forEach((hp, hi) => {
    (hp.timeEntriesHourly ?? []).forEach((entry, ei) => {
      // Submitted drive: TEH linked to a DriveTimeEntry
      if (isSubmitted && entry.driveTimeEntryID != null) {
        const driveEntry = driveMap.get(entry.driveTimeEntryID);
        const inDate = entry.inDateTime ? applyOffset(entry.inDateTime, entry.inDateTimeOffset) : null;
        const outDate = entry.outDateTime ? applyOffset(entry.outDateTime, entry.outDateTimeOffset) : null;
        const durationMin = inDate && outDate ? (outDate.getTime() - inDate.getTime()) / 60000 : null;
        rows.push({
          key: `drive-teh-${hi}-${ei}`,
          historyKey: punchHistoryKey('TimeEntryHourly', entry.timeEntryHourlyID),
          type: 'drive',
          jobName: driveEntry
            ? `${driveEntry.direction === 'BOD' ? 'Beginning' : 'End'} of Day Drive`
            : 'Drive Time',
          jobSub: driveEntry && driveEntry.totalDriveMinutes > 0
            ? `${driveEntry.totalDriveMinutes} min total · ${driveEntry.excessMinutes} min excess`
            : '',
          inTime: inDate ? formatClock(inDate) : null,
          inISO: entry.inDateTime,
          outTime: outDate ? formatClock(outDate) : null,
          isMissingOut: !entry.outDateTime,
          durationMin,
          isEdited: entry.punchTypeIn === PunchType.ManualEdit || entry.punchTypeOut === PunchType.ManualEdit,
          isManual: entry.punchTypeIn === PunchType.ManualAdd || entry.punchTypeOut === PunchType.ManualAdd,
          isMealPenalty: false,
          gps: null,
          issueGps: false,
          amount: null,
        });
        return;
      }

      const isMeal = entry.type === TimeEntryType.Meal;
      // CSW punches carry no account code on the entry — the cost code lives on
      // the CustomerServiceWarranty row. The group object is always present with
      // all-null fields when there's no CSW, and a 0 ID means "none", so test the
      // ID itself. Warranty work additionally carries the warranty project.
      const cswID = hp.customerServiceWarranty?.customerServiceWarrantyID;
      const isCsw = !isMeal && cswID != null && cswID !== 0;
      const isWarranty = isCsw && hp.project?.projectID != null;
      // tehPunchesAreHourly tenants have no CSW rows or ServiceOrder rows at all: their
      // customer-service punch is a TEH row whose only marker is the free-text SO number,
      // and their job punches carry project+phase with no account code — so "no account
      // code = piece" (the other tenants' real pay rule) would mislabel every one of them.
      // TAS-514: an NPM service-order punch now has three shapes and only the first
      // existed when this branch was written — see describeNpmServiceOrder. All three
      // are the SAME fact (customer-service work), so all three take the
      // tehPunchesAreHourly branch. Previously this tested a non-blank ServiceOrderText,
      // which misfiles every RESOLVED punch into the 2020PH `isSO` branch below, because
      // resolving on mobile clears ServiceOrderText. The `isSO` branch itself is
      // 2020PH's (and Wallcraft's) rendering and is deliberately untouched.
      const npmSo = config.tehPunchesAreHourly && !isMeal && !isCsw
        ? describeNpmServiceOrder(hp, entry)
        : null;
      const isCustSvcText = npmSo != null;
      const isSO = !isMeal && !isCsw && !isCustSvcText && (entry.serviceOrderID != null || entry.serviceOrder != null || entry.tempServiceOrderDescription != null);
      const isHourly = !isMeal && !isCsw && !isCustSvcText && !isSO
        && (hp.account?.accountCodeID != null || !!config.tehPunchesAreHourly);
      const entryType: EntryType = isMeal ? 'meal'
        : isCsw ? (isWarranty ? 'warranty' : 'service')
        : isCustSvcText ? 'service'
        : isSO ? 'so' : isHourly ? 'hourly' : 'piece';
      const cswJobName = isCsw
        ? (hp.customerServiceWarranty?.name ?? 'Customer Service')
        : null;
      const cswJobSub = isCsw
        ? [isWarranty ? hp.project?.name : null, hp.locationName, hp.description]
            .filter((v): v is string => v != null && v !== '')
            .join(' · ')
        : null;
      const soBadge = isSO
        ? (entry.serviceOrder?.altServiceOrderID ?? entry.tempServiceOrderDescription ?? 'S.O.')
        : null;
      const soJobName = isSO
        ? (entry.serviceOrder?.name ?? (entry.tempServiceOrderDescription ? 'Awaiting Service Order #' : hp.project.name))
        : null;
      const fmtAcct = (a: typeof hp.account) => a
        ? [a.altAccountCodeID, a.name].filter((v): v is string => v != null && v !== '').join(' ')
        : '';
      const soJobSub = isSO ? fmtAcct(hp.account) : null;
      // The Vista work-order description, falling back to the literal this branch has
      // always shown so a legacy punch — which has no work order to describe — reads
      // exactly as it did before.
      const npmSoJobName = npmSo ? (npmSo.name || 'Customer Service') : null;
      // The phase IS the job's cost code (Vista Job + Phase) — a bare group carries a
      // projectPhase object with all-null fields, so test the fields, not the object.
      const phaseSub = config.tehPunchesAreHourly && !isMeal && hp.projectPhase
        ? [hp.projectPhase.name, hp.projectPhase.altProjectPhaseID?.trim()]
            .filter((v): v is string => v != null && v !== '')
            .join(' - ')
        : '';
      const inDate = entry.inDateTime ? applyOffset(entry.inDateTime, entry.inDateTimeOffset) : null;
      const outDate = entry.outDateTime ? applyOffset(entry.outDateTime, entry.outDateTimeOffset) : null;
      const durationMin =
        inDate && outDate ? (outDate.getTime() - inDate.getTime()) / 60000 : null;
      const gpsExc = entry.gpsException;
      let gps: GpsModalData | null = null;
      if (gpsExc && (gpsExc.inException || gpsExc.outException)) {
        gps = buildGpsModal(gpsExc, projectMap, isCsw ? (cswJobName ?? hp.project.name) : isCustSvcText ? (npmSoJobName ?? 'Customer Service') : isSO ? (soJobName ?? hp.project.name) : hp.project.name);
      }
      rows.push({
        key: `hp-${hi}-${ei}`,
        historyKey: punchHistoryKey('TimeEntryHourly', entry.timeEntryHourlyID),
        type: entryType,
        jobName: isMeal ? 'Meal Break' : isCsw ? (cswJobName ?? hp.project.name) : isCustSvcText ? (npmSoJobName ?? 'Customer Service') : (isSO ? (soJobName ?? hp.project.name) : hp.project.name),
        jobSub: isMeal && (day.noLunchException || day.fiveHourLunchException)
          ? 'Meal penalty'
          : isCsw ? (cswJobSub ?? '')
          : npmSo ? npmSo.scope
          : (isSO ? (soJobSub ?? '')
            : [phaseSub, !isMeal && hp.account ? fmtAcct(hp.account) : '']
                .filter((v) => v !== '')
                .join(' · ')),
        soState: npmSo?.state ?? null,
        // The SO number moves out of the sub-line and into the badge slot 2020PH's
        // service-order rows already use, so both tenants read the same way.
        soBadge: npmSo ? npmSo.number : soBadge,
        projectBadge: isMeal || isSO || isCustSvcText ? null
          : isCsw ? (isWarranty ? (hp.project?.altProjectID ?? null) : null)
          : (hp.project.altProjectID ?? null),
        inTime: inDate ? formatClock(inDate) : null,
        inISO: entry.inDateTime,
        outTime: outDate ? formatClock(outDate) : null,
        isMissingOut: !entry.outDateTime,
        durationMin,
        isEdited: entry.punchTypeIn === PunchType.ManualEdit || entry.punchTypeOut === PunchType.ManualEdit,
        isManual: entry.punchTypeIn === PunchType.ManualAdd || entry.punchTypeOut === PunchType.ManualAdd,
        overnightLabel: overnightBadgeLabel(entry),
        overnightNote: overnightHistoryNote(entry),
        isMealPenalty: isMeal && (day.noLunchException || day.fiveHourLunchException),
        gps,
        issueGps: !!(gpsExc?.inException || gpsExc?.outException),
        amount: null,
        // Wallcraft production totals: only a plain job-costed hourly punch can be
        // re-coded from the day editor. Meals, CSW, SO and bare punches carry null.
        projectID: isHourly ? (hp.project?.projectID ?? null) : null,
        accountCodeID: isHourly ? (hp.account?.accountCodeID ?? null) : null,
      });
    });
  });

  // Sort by inISO (meals may be interleaved)
  rows.sort((a, b) => {
    if (!a.inISO) return 1;
    if (!b.inISO) return -1;
    return new Date(a.inISO).getTime() - new Date(b.inISO).getTime();
  });

  // Unsubmitted drive entries: BOD first, EOD last (no in/out times yet)
  if (!isSubmitted) {
    const makeDriveRow = (entry: DriveTimeEntry, idx: number): PunchRow => ({
      key: `drive-${idx}`,
      type: 'drive',
      jobName: `${entry.direction === 'BOD' ? 'Beginning' : 'End'} of Day Drive`,
      jobSub: entry.totalDriveMinutes > 0
        ? `${entry.totalDriveMinutes} min total · ${entry.excessMinutes} min excess`
        : '',
      inTime: null,
      inISO: null,
      outTime: null,
      isMissingOut: false,
      durationMin: entry.excessMinutes,
      isEdited: false,
      isManual: false,
      isMealPenalty: false,
      gps: null,
      issueGps: false,
      amount: null,
    });
    const bodRows = driveEntries.filter((e) => e.direction === 'BOD').map((e, i) => makeDriveRow(e, i));
    const eodRows = driveEntries.filter((e) => e.direction === 'EOD').map((e, i) => makeDriveRow(e, bodRows.length + i));
    rows.splice(0, 0, ...bodRows);
    rows.push(...eodRows);
  }

  // Sick claims
  (day.employeeSickClaims ?? []).forEach((sc: EmployeeSickClaim, si) => {
    rows.push({
      key: `sick-${si}`,
      type: 'sick',
      jobName: sc.description || 'Sick Time',
      jobSub: '',
      inTime: null,
      inISO: null,
      outTime: null,
      isMissingOut: false,
      durationMin: sc.minutesClaimed,
      isEdited: false,
      isManual: false,
      isMealPenalty: false,
      gps: null,
      issueGps: false,
      amount: sc.amountClaimed,
    });
  });

  return rows;
}

function buildPieceGroups(day: Day): PieceGroup[] {
  return (day.projectEmployeeTasks ?? []).map((pet, pi) => {
    const rows: PieceRow[] = [];
    pet.employeeTasks.forEach((et: EmployeeTask, ei) => {
      rows.push({
        key: `et-${pi}-${ei}`,
        type: 'piece',
        code: et.task.taskInfo.name,
        description: et.task.taskInfo.description,
        phaseLot: et.intermediateName ?? '',
        amount: et.amountClaimed,
        originalAmount: et.task.originalAmount,
        projectName: pet.project.name,
      });
    });
    pet.miscEmployeeTasks.forEach((mt: MiscEmployeeTask, mi) => {
      rows.push({
        key: `mt-${pi}-${mi}`,
        type: 'misc',
        code: mt.accountCode
          ? [mt.accountCode.altAccountCodeID, mt.accountCode.name].filter(Boolean).join(' ')
          : '—',
        description: mt.miscDescription,
        phaseLot: mt.intermediateName ?? '',
        amount: mt.amountClaimed,
        projectName: pet.project.name,
        miscEmployeeTaskID: mt.miscEmployeeTaskID,
        accountCodeID: mt.accountCode?.accountCodeID ?? null,
      });
    });
    return {
      projectName: pet.project.name,
      projectID: pet.project.projectID,
      altProjectID: pet.project.altProjectID ?? null,
      totalAmount: rows.reduce((s, r) => s + r.amount, 0),
      rows,
    };
  }).filter((g) => g.rows.length > 0);
}

interface OpTaskRow {
  key: string;
  name: string;
  description: string;
  account: string;
  projectBadge: string | null;
  minutes: number;
  amount: number;
}

// Operational task claims ("Meetings / Other Tasks"). The unsubmitted-path API
// mapper sends operationalTask.project/accountCode as null when the FK is null,
// despite the non-nullable TS types — guard every access.
function buildOpTaskRows(day: Day): OpTaskRow[] {
  return (day.employeeOperationalTasks ?? []).map((t: EmployeeOperationalTask, i) => {
    const acct = t.operationalTask?.accountCode;
    return {
      key: `op-${t.employeeOperationalTaskID ?? i}`,
      name: t.operationalTask?.name ?? 'Operational Task',
      description: t.description ?? '',
      account: acct
        ? [acct.altAccountCodeID, acct.name].filter((v): v is string => v != null && v !== '').join(' ')
        : '',
      projectBadge: t.operationalTask?.project?.altProjectID ?? null,
      minutes: t.minutesClaimed ?? 0,
      amount: t.amountClaimed ?? 0,
    };
  });
}

/**
 * Customer-service / warranty claims and work-order claims, both shaped like an
 * op-task row (hours + dollars, no punches).
 *
 * These are the *claim* flavor, which lives only in EmployeeCustomerServiceWarranty /
 * EmployeeWorkOrderTask — typically minutesClaimed 0 with a flat dollar amount. The
 * punched flavor of CSW arrives instead as an hourlyProjects group carrying a
 * customerServiceWarrantyID and is handled by buildPunchRows. Only the punched flavor
 * used to render here, so a dollar-only warranty claim was invisible on this page
 * while still counting toward payroll (TimesheetSummaryBuilder sums both).
 */
interface ClaimRow extends OpTaskRow {
  chip: EntryType;
  /**
   * The jobsite the warranty call was performed at — Project.AltProjectID and Project.Name,
   * reached through EmployeeCustomerServiceWarranty.ProjectID. Its own column, never merged
   * with the warranty (see buildCswRows). Null when the claim carries no ProjectID.
   */
  jobBadge: string | null;
  jobName: string;
}

function buildCswRows(day: Day): ClaimRow[] {
  return (day.employeeCustomerServiceWarrantyTasks ?? []).map((t: EmployeeCustomerServiceWarranty, i) => {
    const acct = t.customerServiceWarranty?.accountCode;
    return {
      key: `csw-${t.employeeCustomerServiceWarrantyID ?? i}`,
      name: t.customerServiceWarranty?.name ?? 'Customer Service',
      // The employee types a free-text location alongside the description; both are
      // the only identifying detail on a claim with no project, so keep both.
      description: [t.locationName, t.description]
        .map((v) => v?.trim())
        .filter((v): v is string => !!v)
        .join(' · '),
      account: acct
        ? [acct.altAccountCodeID, acct.name].filter((v): v is string => v != null && v !== '').join(' ')
        : '',
      // A claim carries TWO distinct job identities and they must never fall back to one
      // another — an earlier `project?.altProjectID ?? csw.job` badge rendered the jobsite's
      // number beside the warranty's name, e.g. "[14880] DRYWALL WARRANTY (226)".
      //
      // The warranty bucket the money is costed to (CustomerServiceWarranty.Job) — pairs
      // with `name`, which is that same catalog row's Name.
      projectBadge: t.customerServiceWarranty?.job ?? null,
      // The jobsite the work actually happened at (EmployeeCustomerServiceWarranty.ProjectID
      // → Project). Usually a real job such as 14880 BELLEZA PH 1; occasionally the warranty
      // bucket's own project, in which case both columns legitimately read the same number.
      jobBadge: t.project?.altProjectID ?? null,
      jobName: t.project?.name ?? '',
      // WCDP always claims a flat dollar amount, so this is 0 on every row and the section
      // renders no HRS column. Kept because OpTaskRow requires it.
      minutes: t.minutesClaimed ?? 0,
      amount: t.amountClaimed ?? 0,
      // Same split buildPunchRows uses: a claim with a project is warranty work,
      // one without is general customer service. Tested on the object rather than
      // projectID — the warranty's ProjectRequired flag decides whether the mapper
      // attaches a project at all.
      chip: t.project != null ? 'warranty' : 'service',
    };
  });
}

/** DENIED/CANCELLED time off is never surfaced on the day card. */
const visibleTimeOff = (entries: TimeOffDayEntry[]) =>
  entries.filter((e) => e.status !== 'DENIED' && e.status !== 'CANCELLED');

/** A day card renders at all only if it has a day record, a holiday, or visible time off. */
function dayCardIsRendered(col: DayCol): boolean {
  return !!col.day || !!col.holiday || visibleTimeOff(col.timeOffEntries).length > 0;
}

/**
 * The sections that make up the expanded `.etrc-day-body`. `hasDetail` false means
 * there is nothing to expand, so the card shows no chevron and its header is inert.
 */
function dayBodySections(col: DayCol, isSubmitted: boolean) {
  const punchRows = col.day ? buildPunchRows(col.day, col.driveEntries, isSubmitted) : [];
  const pieceGroups = col.day ? buildPieceGroups(col.day) : [];
  const opTaskRows = col.day ? buildOpTaskRows(col.day) : [];
  const cswRows = col.day ? buildCswRows(col.day) : [];
  const visibleTimeOffEntries = visibleTimeOff(col.timeOffEntries);
  const workOrderClaims: EmployeeWorkOrderTask[] = col.day?.employeeWorkOrderTasks ?? [];
  return {
    punchRows,
    pieceGroups,
    opTaskRows,
    cswRows,
    visibleTimeOffEntries,
    workOrderClaims,
    hasDetail:
      punchRows.length > 0 || pieceGroups.length > 0 || opTaskRows.length > 0 ||
      cswRows.length > 0 || workOrderClaims.length > 0 ||
      visibleTimeOffEntries.length > 0 || !!col.holiday,
  };
}

// ── Wallcraft production totals (design_handoff_wallcraft_production_totals) ──
//
// Everything in this block is inert unless `config.productionPay?.enabled === true`.
// The model is derived CLIENT-SIDE from the week already in the payload — the server
// re-derives and re-validates all of it (Rules 3/4/6), so nothing here is truth, only
// feedback. A "segment" is one (day · job · account code) worth of punched hours; the
// `Total $` the foreman types is stored as a MiscEmployeeTask with CreatedSource
// 'PAYROLL', matched back onto its segment by (projectID, accountCodeID).

/** Source marker that distinguishes a supervisor-entered production total from an
 *  employee-claimed misc task. Never match on the description. */
const PRODUCTION_SOURCE = 'PAYROLL';

interface ProdSegment {
  /** `date|projectID|accountCodeID` — the stable identity behind the ref map, the
   *  input drafts and the keyboard run (Rule 7's "focus by key, not by node"). */
  key: string;
  date: string;
  dow: string;
  dateLabel: string;
  projectID: number;
  altProjectID: string | null;
  projectName: string;
  /** "02100 · Bldg A L2", or '' when the group carries no phase. */
  phaseName: string;
  accountCodeID: number;
  accountCodeName: string;
  altAccountCodeID: string;
  minutes: number;
  hours: number;
  inTime: string | null;
  outTime: string | null;
  /** Rule 6 — the Purchase Orders code, read from config, never matched by name. */
  isPO: boolean;
  poNumber: string | null;
  /** The stored Total $; null = not entered. */
  amount: number | null;
  miscEmployeeTaskID: number | null;
  enteredBy: string | null;
  enteredAt: string | null;
  /** Rule 3 — hours × the employee's effective rate, straight time only. */
  base: number;
  requiresAmount: boolean;
  needsEntry: boolean;
  belowBase: boolean;
  poMissing: boolean;
  poInvalid: boolean;
}

interface ProdDay {
  date: string;
  dow: string;
  dateLabel: string;
  /** Every worked (job × account code) segment on the day. */
  segments: ProdSegment[];
  /** The subset this employee's role actually enters against: all of them when
   *  `requireProductionPay` is on, PO segments only when it is off. */
  visible: ProdSegment[];
  /** Day total across every account code — what Rule 4's cap is measured on. */
  entryTotal: number;
  /** True when any production entry exists on the day, matched to a segment or not. */
  hasEntries: boolean;
}

type ProdFieldKind = 'amt' | 'po';

interface ProdField { segKey: string; kind: ProdFieldKind; }

interface ProdModel {
  days: ProdDay[];
  byDate: Record<string, ProdDay>;
  segmentByKey: Record<string, ProdSegment>;
  weekTotal: number;
  entriesWithoutTotal: number;
  totalsBelowBase: number;
  posWithoutNumber: number;
  /** Missing/below-base totals + missing rate — binds RequireProductionPay roles only,
   *  and is only surfaced once the week is submitted. */
  productionBlockers: string[];
  /** Purchase orders without a valid PO # — binds every role. */
  poBlockers: string[];
  /** Dot-separated sentences for the KPI subline and the action-bar note. */
  blockerList: string[];
  hasBlockers: boolean;
  /** The employee has something to enter on this week at all — the banner, the KPI
   *  subline and the approve gate all stay quiet when false. */
  applies: boolean;
  /** No effective rate on the payload: base pay cannot be computed (Rule 3). */
  rateMissing: boolean;
  /** Every enterable field, in day order, `Total $` before `PO #` within a day. */
  fieldOrder: ProdField[];
}

const prodRound = (n: number) => Math.round(n * 100) / 100;

function prodMoney(n: number | null): string {
  if (n == null) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Whole dollars — the cap sentence reads "$600", never "$600.00". */
function prodMoney0(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

const prodHours = (h: number) => h.toFixed(2);

/** "8/11 7:44a" in the timesheet's own zone — the provenance line's stamp. */
function fmtProdStamp(utcISO: string | null | undefined, offsetStr: string | null | undefined): string | null {
  if (!utcISO) return null;
  const m = (offsetStr ?? '').match(/([+-])(\d{2}):(\d{2})/);
  const d = m ? applyOffset(utcISO, offsetStr as string) : new Date(utcISO);
  if (isNaN(d.getTime())) return null;
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()} ${formatClock(d)}`;
}

/** A value the server just accepted, applied locally so a commit does not reload the
 *  whole timesheet. A key's PRESENCE makes it authoritative over the payload (null =
 *  cleared); any real reload — week change, split save, Refresh — wipes the overlay. */
interface ProdOverride {
  amount?: number | null;
  poNumber?: string | null;
  miscEmployeeTaskID?: number | null;
  enteredBy?: string | null;
  enteredAt?: string | null;
}

interface ProdSegAcc {
  projectID: number;
  altProjectID: string | null;
  projectName: string;
  phaseName: string;
  accountCodeID: number;
  accountCodeName: string;
  altAccountCodeID: string;
  minutes: number;
  firstIn: Date | null;
  lastOut: Date | null;
  poNumber: string | null;
}

/**
 * Derives the whole production model from the loaded week.
 *
 * Exempt from requiredness, exactly as the server gate is: bare / no-job groups,
 * groups with no account code, meal and drive rows, and zero-minute segments (an
 * open punch contributes nothing, so it drops out here as well).
 */
function buildProductionModel(
  cols: DayCol[],
  rate: number,
  roleRequires: boolean,
  poAccountCodeID: number | null,
  resolveAdminName: (id: number | null | undefined) => string | null,
  overrides: Record<string, ProdOverride>
): ProdModel {
  const days: ProdDay[] = [];
  const byDate: Record<string, ProdDay> = {};
  const segmentByKey: Record<string, ProdSegment> = {};
  const fieldOrder: ProdField[] = [];
  let weekTotal = 0;
  let entriesWithoutTotal = 0;
  let totalsBelowBase = 0;
  let posWithoutNumber = 0;
  const rateMissing = !(rate > 0);

  cols.forEach((col) => {
    const day = col.day;
    const accs = new Map<string, ProdSegAcc>();

    (day?.hourlyProjects ?? []).forEach((hp) => {
      const projectID = hp.project?.projectID ?? null;
      const accountCodeID = hp.account?.accountCodeID ?? null;
      if (projectID == null || accountCodeID == null) return;    // bare / uncoded — exempt
      const k = `${projectID}|${accountCodeID}`;
      let acc = accs.get(k);
      if (!acc) {
        acc = {
          projectID,
          altProjectID: hp.project?.altProjectID ?? null,
          projectName: hp.project?.name ?? '—',
          phaseName: hp.projectPhase
            ? [hp.projectPhase.altProjectPhaseID, hp.projectPhase.name].filter(Boolean).join(' · ')
            : '',
          accountCodeID,
          accountCodeName: hp.account?.name ?? '—',
          altAccountCodeID: hp.account?.altAccountCodeID ?? '',
          minutes: 0,
          firstIn: null,
          lastOut: null,
          poNumber: null,
        };
        accs.set(k, acc);
      }
      const groupPo = (hp.serviceOrderText ?? '').trim();
      if (groupPo && !acc.poNumber) acc.poNumber = groupPo;

      (hp.timeEntriesHourly ?? []).forEach((entry) => {
        if (entry.type === TimeEntryType.Meal) return;           // meals are never re-coded
        if (entry.driveTimeEntryID != null) return;              // drive is its own thing
        const entryPo = (entry.serviceOrderText ?? '').trim();
        if (entryPo && !acc!.poNumber) acc!.poNumber = entryPo;
        if (!entry.inDateTime || !entry.outDateTime) return;     // open punch — zero minutes
        const inD = applyOffset(entry.inDateTime, entry.inDateTimeOffset);
        const outD = applyOffset(entry.outDateTime, entry.outDateTimeOffset);
        const mins = (outD.getTime() - inD.getTime()) / 60000;
        if (!(mins > 0)) return;
        acc!.minutes += mins;
        if (!acc!.firstIn || inD < acc!.firstIn) acc!.firstIn = inD;
        if (!acc!.lastOut || outD > acc!.lastOut) acc!.lastOut = outD;
      });
    });

    // Production entries on the day: PAYROLL-sourced misc rows, keyed the same way.
    // The day total is summed AFTER the segment merge below so a committed-value
    // override counts instead of the payload row it superseded.
    const entries = new Map<string, MiscEmployeeTask>();
    let looseTotal = 0; // rows that cannot map onto a worked segment
    (day?.projectEmployeeTasks ?? []).forEach((pet) => {
      (pet.miscEmployeeTasks ?? []).forEach((mt) => {
        if (mt.createdSource !== PRODUCTION_SOURCE) return;
        const accountCodeID = mt.accountCode?.accountCodeID ?? null;
        const projectID = pet.project?.projectID ?? mt.projectID ?? null;
        if (projectID == null || accountCodeID == null) {
          looseTotal += mt.amountClaimed ?? 0;
          return;
        }
        entries.set(`${projectID}|${accountCodeID}`, mt);
      });
    });

    const segments: ProdSegment[] = Array.from(accs.entries())
      .filter(([, a]) => a.minutes > 0)
      .map(([k, a]) => {
        const mt = entries.get(k) ?? null;
        const ov = overrides[`${col.date}|${k}`];
        const hours = prodRound(a.minutes / 60);
        let amount = mt ? prodRound(mt.amountClaimed ?? 0) : null;
        let miscEmployeeTaskID = mt?.miscEmployeeTaskID ?? null;
        let enteredBy = mt
          ? (mt.createdByAdminName ?? resolveAdminName(mt.createdByAdminEmployeeID))
          : null;
        let enteredAt = mt ? fmtProdStamp(mt.insertDateTime, mt.insertDateTimeOffset) : null;
        let poNumber = a.poNumber && a.poNumber.length > 0 ? a.poNumber : null;
        // Committed-value overlay: a present key beats the payload; null = cleared.
        if (ov) {
          if ('amount' in ov) {
            amount = ov.amount ?? null;
            if (amount == null) {
              miscEmployeeTaskID = null;
              enteredBy = null;
              enteredAt = null;
            } else {
              if (ov.miscEmployeeTaskID !== undefined) miscEmployeeTaskID = ov.miscEmployeeTaskID;
              enteredBy = ov.enteredBy ?? enteredBy;
              enteredAt = ov.enteredAt ?? enteredAt;
            }
          }
          if ('poNumber' in ov) poNumber = ov.poNumber ?? null;
        }
        const base = prodRound(hours * (rate > 0 ? rate : 0));
        const isPO = poAccountCodeID != null && a.accountCodeID === poAccountCodeID;
        const seg: ProdSegment = {
          key: `${col.date}|${k}`,
          date: col.date,
          dow: col.dow,
          dateLabel: col.dateLabel,
          projectID: a.projectID,
          altProjectID: a.altProjectID,
          projectName: a.projectName,
          phaseName: a.phaseName,
          accountCodeID: a.accountCodeID,
          accountCodeName: a.accountCodeName,
          altAccountCodeID: a.altAccountCodeID,
          minutes: a.minutes,
          hours,
          inTime: a.firstIn ? formatClock(a.firstIn) : null,
          outTime: a.lastOut ? formatClock(a.lastOut) : null,
          isPO,
          poNumber,
          amount,
          miscEmployeeTaskID,
          enteredBy,
          enteredAt,
          base,
          requiresAmount: roleRequires,
          needsEntry: roleRequires && amount == null,
          // Rule 3: an amount is compared against base only when base can be computed.
          belowBase: roleRequires && amount != null && !rateMissing && amount < base - 0.005,
          poMissing: isPO && poNumber == null,
          poInvalid: isPO && poNumber != null && !/^[0-9]+$/.test(poNumber),
        };
        return seg;
      });

    // Chronological, falling back to the account code so the order is stable.
    segments.sort((x, y) => {
      const ax = accs.get(`${x.projectID}|${x.accountCodeID}`)?.firstIn?.getTime() ?? 0;
      const ay = accs.get(`${y.projectID}|${y.accountCodeID}`)?.firstIn?.getTime() ?? 0;
      if (ax !== ay) return ax - ay;
      return x.accountCodeID - y.accountCodeID;
    });

    const visible = roleRequires ? segments : segments.filter((s) => s.isPO);

    // The day total: merged segment amounts, plus payload rows no worked segment
    // claims (loose rows above, and entries whose code has no hours today).
    const segKeySet = new Set(segments.map((s) => `${s.projectID}|${s.accountCodeID}`));
    let entryTotal = looseTotal;
    entries.forEach((emt, k) => { if (!segKeySet.has(k)) entryTotal += emt.amountClaimed ?? 0; });
    segments.forEach((s) => { entryTotal += s.amount ?? 0; });
    entryTotal = prodRound(entryTotal);

    segments.forEach((s) => { segmentByKey[s.key] = s; });
    visible.forEach((s) => {
      if (roleRequires) fieldOrder.push({ segKey: s.key, kind: 'amt' });
      if (s.isPO) fieldOrder.push({ segKey: s.key, kind: 'po' });
      if (s.needsEntry) entriesWithoutTotal += 1;
      if (s.belowBase) totalsBelowBase += 1;
      if (s.poMissing || s.poInvalid) posWithoutNumber += 1;
    });

    weekTotal += entryTotal;

    const prodDay: ProdDay = {
      date: col.date,
      dow: col.dow,
      dateLabel: col.dateLabel,
      segments,
      visible,
      entryTotal,
      hasEntries: entries.size > 0 || entryTotal > 0,
    };
    days.push(prodDay);
    byDate[col.date] = prodDay;
  });

  const applies = days.some((d) => d.visible.length > 0);
  // Totals and PO #s are separate obligations with separate banners: totals only
  // ever bind a RequireProductionPay role, PO #s bind every role.
  const productionBlockers: string[] = [];
  if (entriesWithoutTotal > 0) {
    productionBlockers.push(`${entriesWithoutTotal} entr${entriesWithoutTotal > 1 ? 'ies' : 'y'} without a total`);
  }
  if (totalsBelowBase > 0) {
    productionBlockers.push(`${totalsBelowBase} total${totalsBelowBase > 1 ? 's' : ''} below base pay`);
  }
  const poBlockers: string[] = [];
  if (posWithoutNumber > 0) {
    poBlockers.push(`${posWithoutNumber} purchase order${posWithoutNumber > 1 ? 's' : ''} without a PO #`);
  }
  // Rule 3: without a rate the floor cannot be computed, so the week is blocked and
  // routed to rate setup rather than passed silently.
  if (applies && roleRequires && rateMissing) {
    productionBlockers.push('no hourly rate on file — base pay cannot be checked');
  }
  const blockerList = [...productionBlockers, ...poBlockers];

  return {
    days,
    byDate,
    segmentByKey,
    weekTotal: prodRound(weekTotal),
    entriesWithoutTotal,
    totalsBelowBase,
    posWithoutNumber,
    productionBlockers,
    poBlockers,
    blockerList,
    hasBlockers: applies && blockerList.length > 0,
    applies,
    rateMissing,
    fieldOrder,
  };
}

/** Rule 3's in-place error sentence, or null when the amount is fine. */
function prodSegError(seg: ProdSegment, rate: number): string | null {
  if (!seg.belowBase) return null;
  return `Below base pay — needs at least ${prodMoney(seg.base)} (${prodHours(seg.hours)} h × ${prodMoney(rate)})`;
}

/** The day-card header chip: the day total, or the reason it cannot be trusted yet. */
function prodDayChip(day: ProdDay, rate: number, roleRequires: boolean): { kind: 'total' | 'need' | 'bad'; text: string } | null {
  // Nothing this employee enters against on this day — no chip, no section (a
  // role-OFF employee on a week with no purchase orders sees no production UI at
  // all beyond the Total $ row).
  if (day.visible.length === 0) return null;
  if (day.visible.some((s) => prodSegError(s, rate) != null)) return { kind: 'bad', text: 'below base' };
  if (day.visible.some((s) => s.needsEntry)) return { kind: 'need', text: 'needs total' };
  // Role-OFF employees never owe a total, so a money chip is noise — no chip.
  return roleRequires ? { kind: 'total', text: prodMoney(day.entryTotal) } : null;
}

/** Everything the day card needs to render the Production Totals section. The console
 *  keeps the draft + commit state, so the inputs themselves come back as nodes. */
interface DayProductionProps {
  day: ProdDay;
  roleRequires: boolean;
  rate: number;
  chip: { kind: 'total' | 'need' | 'bad'; text: string } | null;
  renderAmount: (seg: ProdSegment, size: 'sm' | 'lg') => React.ReactNode;
  renderPo: (seg: ProdSegment, size: 'sm' | 'lg') => React.ReactNode;
  renderFlag: (seg: ProdSegment, size: 'sm' | 'md') => React.ReactNode;
  renderProvenance: (seg: ProdSegment) => React.ReactNode;
  /** Opens the day/job editor (design §6). Absent when the week is locked. */
  onSplit?: (projectID: number) => void;
}

// ── Sub-components ────────────────────────────────────────────────────────────

// Inline OT bar chart icon matching the design prototype
function OTBarIcon({ size = 15, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18"/>
      <path d="M18 17V9"/>
      <path d="M13 17V5"/>
      <path d="M8 17v-3"/>
    </svg>
  );
}

const ENTRY_TYPE_CONFIG: Record<EntryType, { color: string; bg: string; label: string; icon: React.ReactElement }> = {
  piece:   { color: '#2f6fed', bg: 'rgba(47,111,237,.11)',  label: 'Piece',   icon: <Package size={13} /> },
  misc:    { color: '#d8852f', bg: 'rgba(216,133,47,.13)',  label: 'Misc',    icon: <Sparkles size={13} /> },
  hourly:  { color: '#1f9d6b', bg: 'rgba(31,157,107,.12)',  label: 'Hourly',  icon: <Clock size={13} /> },
  holiday: { color: '#7a5cf0', bg: 'rgba(122,92,240,.12)',  label: 'Holiday', icon: <Sun size={13} /> },
  sick:    { color: '#2fa3c2', bg: 'rgba(47,163,194,.12)',  label: 'Sick',    icon: <Activity size={13} /> },
  drive:   { color: '#1f9488', bg: 'rgba(31,148,136,.12)',  label: 'Drive',   icon: <Truck size={13} /> },
  meal:    { color: '#e0533f', bg: 'rgba(224,83,63,.12)',   label: 'Meal',    icon: <Coffee size={13} /> },
  so:      { color: '#1f9d6b', bg: 'rgba(31,157,107,.12)',  label: 'Service Order', icon: <Wrench size={13} /> },
  warranty:{ color: '#1f9d6b', bg: 'rgba(31,157,107,.12)',  label: 'Warranty', icon: <Wrench size={13} /> },
  service: { color: '#1f9d6b', bg: 'rgba(31,157,107,.12)',  label: 'Cust Service', icon: <LifeBuoy size={13} /> },
  opTask:  { color: '#d8852f', bg: 'rgba(216,133,47,.13)',  label: 'Meeting / Task', icon: <ClipboardList size={13} /> },
  workOrder:{color: '#2f6fed', bg: 'rgba(47,111,237,.12)',  label: 'Work Order', icon: <ScrollText size={13} /> },
};

/**
 * A claim table shaped like the "Meetings / Other Tasks" section. Only the customer
 * service / warranty section uses it — work orders moved to their own table once the
 * claimed rate became the point of that section.
 *
 * Task and Job are separate columns because a claim carries two unrelated job identities:
 * the warranty bucket it costs to, and the jobsite it was performed at. The per-row `chip`
 * lets one section mix warranty and service rows. No HRS column — these claims are flat
 * dollar amounts and always carry minutesClaimed 0.
 */
function ClaimSection({ title, icon, rows }: {
  title: string;
  icon: React.ReactElement;
  rows: ClaimRow[];
}) {
  return (
    <>
      <div className="etrc-section-label bordered">
        {icon}
        <span>{title}</span>
        <span className="etrc-section-sub">
          {fmtDollar(rows.reduce((s, r) => s + r.amount, 0))}
        </span>
      </div>
      <div className="etrc-table-wrap">
        <table className="etrc-piece-table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Job</th>
              <th>Description</th>
              <th>Account</th>
              <th>Type</th>
              <th className="right">Claimed $</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td style={{ fontWeight: 700 }}>
                  <div>
                    {row.projectBadge && (
                      <span className="etrc-project-badge" style={{ marginRight: 6 }}>{row.projectBadge}</span>
                    )}
                    {row.name}
                  </div>
                </td>
                {/* The jobsite, when the claim names one. A customer-service claim carries no
                    ProjectID, so it shows a dash rather than borrowing the warranty's number. */}
                <td style={{ fontWeight: 700 }}>
                  {row.jobBadge ? (
                    <>
                      <div><span className="etrc-project-badge">{row.jobBadge}</span></div>
                      {row.jobName && <span className="etrc-job-sub">{row.jobName}</span>}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td>{row.description || '—'}</td>
                <td className="etrc-text-3">{row.account || '—'}</td>
                <td><EntryTypeChip type={row.chip} /></td>
                <td className="etrc-num right">{fmtDollar(row.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function EntryTypeChip({ type }: { type: EntryType }) {
  const { color, bg, label, icon } = ENTRY_TYPE_CONFIG[type];
  return (
    <span className="etrc-type-chip" style={{ color, background: bg }}>
      {React.cloneElement(icon, { color })}
      {label}
    </span>
  );
}

/** One approvals-card cell: approved (tinted tile + name + stamp) or pending
 *  (hollow tile + muted copy). Display-only — no click targets by design. */
function ApprHop({ role, tile, vm, pendingName }: {
  role: string;
  tile: 'sup' | 'payroll';
  vm: ApprovalVM | null;
  pendingName?: string | null;
}) {
  const Icon = tile === 'sup' ? User : CheckCircle;
  if (!vm) {
    return (
      <div className="etrc-hop is-pending">
        <span className="etrc-hop-tile pending"><Icon size={19} /></span>
        <div className="etrc-hop-main">
          <div className="etrc-hop-role">{role}</div>
          <div className="etrc-hop-name">
            {pendingName ?? `Awaiting ${tile === 'sup' ? 'supervisor' : 'payroll'} approval`}
          </div>
          <div className="etrc-hop-dt pending">Pending</div>
        </div>
      </div>
    );
  }
  const stamp = vm.dt ? fmtStamp(vm.dt, vm.off) : null;
  return (
    <div className="etrc-hop">
      <span className={`etrc-hop-tile ${tile}`}><Icon size={19} /></span>
      <div className="etrc-hop-main">
        <div className="etrc-hop-role">{role}</div>
        <div className="etrc-hop-name">
          {vm.name}
          {!vm.active && <span className="etrc-inact-tag">Inactive</span>}
        </div>
        {stamp && (
          <div className="etrc-hop-dt">
            {stamp.text}
            {stamp.utc && <span className="etrc-utc-tag">UTC</span>}
          </div>
        )}
      </div>
    </div>
  );
}

/** One approval SLOT row (design_handoff_npm_timesheet_review §3). Three states:
 *  - complete → check mark + "Approved by X · stamp" (+ "on behalf of Y" when the
 *    recorded approver is not the slot's owner — a client derivation, errata 5);
 *  - outstanding, yours, in-stage → checkbox row, pre-checked, cleared by the
 *    card-level "Approve N selected";
 *  - outstanding, someone else's, in-stage → "Approve on behalf" (confirmed);
 *  - outstanding, out of stage → plain "Pending", exactly as read-only as today.
 *
 *  TAS-447: an outstanding slot whose owner has no approver login is flagged. It is
 *  never "mine" (there is no adminEmployeeID to match), so it always lands in the
 *  third state — the "Approve on behalf" button is already the right and only way
 *  out, and the flag is what tells payroll that. */
function SlotRow({
  ownerName, mine, vm, onBehalf, noLogin, actionable, selected, busy, onToggleSelect, onApproveOnBehalf,
}: {
  ownerName: string;
  mine: boolean;
  vm: ApprovalVM | null;
  onBehalf: boolean;
  noLogin: 'no-account' | 'inactive' | null;
  actionable: boolean;
  selected: boolean;
  busy: boolean;
  onToggleSelect: () => void;
  onApproveOnBehalf: () => void;
}) {
  if (vm) {
    const stamp = vm.dt ? fmtStamp(vm.dt, vm.off) : null;
    // Prod has duplicate AdminEmployee records for the same person, so the IDs can
    // differ while both names render identically — "on behalf of themselves" is noise.
    const behalfAddsInfo =
      onBehalf && (vm.name ?? '').trim().toLowerCase() !== ownerName.trim().toLowerCase();
    return (
      <div className="etrc-slot done">
        <span className="etrc-slot-mark"><CheckCircle size={20} /></span>
        <span className="etrc-slot-name">
          {ownerName}
          {mine && <span className="etrc-slot-youtag">you</span>}
        </span>
        <span className="etrc-slot-meta">
          Approved by <b>{vm.name || '—'}</b>
          {behalfAddsInfo && <> on behalf of <b>{ownerName}</b></>}
          {!vm.active && <span className="etrc-inact-tag">Inactive</span>}
          {stamp && (
            <> · {stamp.text}{stamp.utc && <span className="etrc-utc-tag">UTC</span>}</>
          )}
        </span>
      </div>
    );
  }

  if (actionable && mine) {
    return (
      <label className={`etrc-slot sel${selected ? ' on' : ''}`}>
        <input type="checkbox" checked={selected} disabled={busy} onChange={onToggleSelect} />
        <span className="etrc-slot-name">
          {ownerName}
          <span className="etrc-slot-youtag">you</span>
        </span>
        <span className="etrc-slot-meta">yours to approve — included in <b>Approve</b></span>
      </label>
    );
  }

  const noLoginNote =
    noLogin === 'no-account'
      ? 'no admin account — only payroll can clear this'
      : 'approver login is inactive — only payroll can clear this';

  return (
    <div className={`etrc-slot${noLogin ? ' nologin' : ''}`}>
      <span className="etrc-slot-mark" />
      <span className="etrc-slot-name">
        <span className="etrc-slot-nameline">
          {ownerName}
          {mine && <span className="etrc-slot-youtag">you</span>}
          {noLogin && (
            <span
              className="etrc-warn-tag"
              title={
                noLogin === 'no-account'
                  ? 'This approver has no AdminEmployee record, so they cannot sign in to approve.'
                  : 'This approver’s AdminEmployee account is inactive, so they cannot sign in to approve.'
              }
            >
              <AlertTriangle size={10} />No approver login
            </span>
          )}
        </span>
        {noLogin && <span className="etrc-slot-note">{noLoginNote}</span>}
      </span>
      {actionable ? (
        <button
          type="button"
          className="etrc-btn small etrc-slot-action"
          disabled={busy}
          onClick={onApproveOnBehalf}
        >
          <User size={13} />Approve on behalf
        </button>
      ) : (
        <span className="etrc-slot-await">Pending</span>
      )}
    </div>
  );
}

function IconClipboardPlus({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="2" width="8" height="4" rx="1"/>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
      <path d="M12 11v6"/><path d="M9 14h6"/>
    </svg>
  );
}
function IconDollarSign({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 1 0 0 7h5a3.5 3.5 0 1 1 0 7H6"/>
    </svg>
  );
}
function IconClockBreak({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <path d="M12 7v5l3 3"/>
      <line x1="3" y1="3" x2="6" y2="6"/>
      <line x1="18" y1="18" x2="21" y2="21"/>
    </svg>
  );
}

const ISSUE_CONFIG: Record<IssueType, { color: string; label: string; icon: React.ReactElement }> = {
  meal:    { color: '#e0533f', label: 'Meal Penalty',        icon: <Coffee        size={14} color="white" /> },
  missed:  { color: '#e0533f', label: 'Missing Punch',       icon: <AlertCircle   size={14} color="white" /> },
  gps:     { color: '#1f9488', label: 'GPS Outside Job Area', icon: <MapPin        size={14} color="white" /> },
  edited:  { color: '#d4881a', label: 'Edited Time',          icon: <Pencil        size={14} color="white" /> },
  drive:   { color: '#1f9488', label: 'Drive Time',           icon: <Truck         size={14} color="white" /> },
  manual:  { color: '#d4881a', label: 'Manual Time Entry',    icon: <IconClipboardPlus size={14} color="white" /> },
  minwage: { color: 'var(--c-minwage)', label: 'Min Wage True-up', icon: <IconDollarSign size={14} color="white" /> },
  gap:     { color: '#e0533f', label: 'Gap in Time',          icon: <IconClockBreak    size={14} color="white" /> },
  overnight: { color: 'var(--c-alert)', label: 'Overnight shift',  icon: <Moon          size={14} color="white" /> },
};

/** How long a tapped-open tooltip stays up before it hides on its own (ms). */
const TOUCH_TOOLTIP_MS = 2500;

function IssueFlagBadge({ issue, showLabel, onClick, labelOverride }: {
  issue: IssueType;
  showLabel?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  /** Replaces the issue's fixed tooltip / aria label (the Overnight badge says which side). */
  labelOverride?: string | null;
}) {
  const { color, icon } = ISSUE_CONFIG[issue];
  const label = labelOverride || ISSUE_CONFIG[issue].label;
  // Touch devices (iPad) have no hover, so the CSS :hover tooltip never shows and the tap
  // falls through to the parent cell/header click. On a touch tap we open the tooltip
  // ourselves and swallow the tap; mouse clicks keep their existing behaviour.
  const [touchOpen, setTouchOpen] = useState(false);
  const touchedRef = useRef(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!touchOpen) return;
    const timer = window.setTimeout(() => setTouchOpen(false), TOUCH_TOOLTIP_MS);
    const onOutside = (e: Event) => {
      if (wrapRef.current && e.target instanceof Node && wrapRef.current.contains(e.target)) return;
      setTouchOpen(false);
    };
    document.addEventListener('touchstart', onOutside, true);
    document.addEventListener('pointerdown', onOutside, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('touchstart', onOutside, true);
      document.removeEventListener('pointerdown', onOutside, true);
    };
  }, [touchOpen]);

  if (showLabel) {
    return (
      <span className="etrc-flag-badge labeled" style={{ background: color }}>
        {icon} {label}
      </span>
    );
  }

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) { onClick(e); return; }
    const isTouch = touchedRef.current
      || (e.nativeEvent as PointerEvent).pointerType === 'touch';
    touchedRef.current = false;
    if (!isTouch) return;
    e.stopPropagation();
    setTouchOpen((open) => !open);
  };

  return (
    <span ref={wrapRef} className={`etrc-flag-badge-wrap${touchOpen ? ' touch-open' : ''}`}>
      <span
        className={`etrc-flag-badge${onClick ? ' clickable' : ''}`}
        style={{ background: color }}
        onTouchStart={onClick ? undefined : () => { touchedRef.current = true; }}
        onClick={handleClick}
        role={onClick ? 'button' : undefined}
        aria-label={label}
      >
        {icon}
      </span>
      <span className="etrc-flag-tooltip" role="tooltip">{label}</span>
    </span>
  );
}

// Leaflet red pin for punch location
const redPinIcon = L.divIcon({
  html: '<div style="width:13px;height:13px;border-radius:50%;background:#e0533f;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>',
  className: '',
  iconSize: [13, 13],
  iconAnchor: [6, 6],
});

function MapBoundsAdjuster({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length >= 2) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds.pad(0.35));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function GpsMapModal({ data, onClose }: { data: GpsModalData; onClose: () => void }) {
  useEffect(() => {
    ensureProjectMapMarkerIcon();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const hasSite = data.siteLat != null && data.siteLng != null;
  const isFar = hasSite && data.distanceMiles != null && data.distanceMiles > data.radiusMiles;
  const radiusMeters = data.radiusMiles * 1609.34;
  const mapCenter: [number, number] = hasSite
    ? [data.siteLat!, data.siteLng!]
    : [data.punchLat, data.punchLng];
  const mapPoints: [number, number][] = hasSite
    ? [[data.siteLat!, data.siteLng!], [data.punchLat, data.punchLng]]
    : [[data.punchLat, data.punchLng]];

  return (
    <div className="etrc-overlay" onClick={onClose}>
      <div className="etrc-gps-modal" onClick={(e) => e.stopPropagation()}>
        <div className="etrc-gps-header">
          <div className="etrc-gps-header-left">
            <div className="etrc-gps-icon-wrap">
              <MapPin size={17} color="#e0533f" />
            </div>
            <div>
              <div className="etrc-gps-title">GPS Location</div>
              <div className="etrc-gps-subtitle">{data.jobName}</div>
            </div>
          </div>
          <button type="button" className="etrc-icon-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <MapContainer
          center={mapCenter}
          zoom={14}
          style={{ height: 460 }}
          scrollWheelZoom={true}
        >
          <TileLayer
            url={MAP_TILE_URL}
            maxZoom={MAP_TILE_MAX_ZOOM}
            attribution={MAP_TILE_ATTRIBUTION}
          />
          {hasSite && (
            <Circle
              center={[data.siteLat!, data.siteLng!]}
              radius={radiusMeters}
              pathOptions={{ color: '#2f6fed', weight: 2, dashArray: '6 4', fillOpacity: 0.06 }}
            />
          )}
          {hasSite && <Marker position={[data.siteLat!, data.siteLng!]} icon={bluePinIcon} />}
          <Marker position={[data.punchLat, data.punchLng]} icon={redPinIcon} />
          <MapBoundsAdjuster points={mapPoints} />
        </MapContainer>

        <div className="etrc-gps-footer">
          <span>{data.label}</span>
          <span className="etrc-mono">{data.punchLat.toFixed(5)}, {data.punchLng.toFixed(5)}</span>
          {hasSite && data.distanceMiles != null && (
            <span style={{ color: isFar ? '#e0533f' : '#1f9d6b', fontWeight: 600 }}>
              {data.distanceMiles.toFixed(2)} mi from site
            </span>
          )}
          {!hasSite && (
            <span style={{ color: 'var(--text-3)', fontSize: 12 }}>No site coordinates configured</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Min Wage True-Up Modal (read-only explainer) ─────────────────────────────

function fmtMwMoney(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function MwLedgerRow({ label, value, sub, isFloor }: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  isFloor?: boolean;
}) {
  return (
    <div className={`etrc-mw-row${isFloor ? ' floor' : ''}`}>
      <div className="etrc-mw-row-lbl">
        <b>{label}</b>
        {sub && <span className="etrc-mw-row-sub">{sub}</span>}
      </div>
      <div className="etrc-mw-row-val etrc-mono">{value}</div>
    </div>
  );
}

function MinWageModal({ data, onClose }: { data: MinWageModalData; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const m = data.trueUp;
  const state = m.stateAbbr || 'State';
  const restMinutes = m.pieceRestMinutes ?? 0;
  const punchedHrs = m.pieceHours + restMinutes / 60;
  const hasRate = !!m.minimumWageRate;
  const owed = m.needed && m.trueUpAmount > 0;

  return (
    <div className="etrc-overlay" onClick={onClose}>
      <div className="etrc-mw-modal" onClick={(e) => e.stopPropagation()}>
        <div className="etrc-mw-header">
          <div className="etrc-mw-icon-wrap">
            <IconDollarSign size={22} color="var(--c-minwage)" />
          </div>
          <div>
            <div className="etrc-mw-title">Minimum Wage True-Up</div>
            <div className="etrc-mw-subtitle">
              {data.dow}, {data.dateLabel}{m.stateAbbr ? ` · ${m.stateAbbr}` : ''}
            </div>
          </div>
          <button type="button" className="etrc-mw-close" onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>

        <div className="etrc-mw-body">
          {!hasRate ? (
            <>
              <p className="etrc-mw-lead">
                This day includes piece-rate time, but no effective minimum-wage rate was found
                for {state} on this date, so the floor can’t be calculated.
              </p>
              <div className="etrc-mw-norate">
                <AlertTriangle size={18} color="#d4881a" />
                <div>
                  <b>No minimum-wage rate configured.</b> Add a rate for {state} effective this
                  date, then re-run the true-up to verify this day.
                </div>
              </div>
              <div className="etrc-mw-ledger">
                <MwLedgerRow label="Piece time" value={`${m.pieceHours.toFixed(2)} hrs`} />
                <MwLedgerRow label="Piece earnings" value={fmtMwMoney(m.pieceEarnings)} />
              </div>
            </>
          ) : (
            <>
              <p className="etrc-mw-lead">
                {owed
                  ? `Piece-rate earnings for this day, divided by piece-rate hours, fell below the ${state} minimum wage. A make-up amount is added to bring the employee to the legal floor.`
                  : `Piece-rate earnings for this day exceed the ${state} minimum-wage floor, so no true-up is owed.`}
              </p>
              <div className="etrc-mw-ledger">
                <MwLedgerRow
                  label="Piece time"
                  value={`${m.pieceHours.toFixed(2)} hrs`}
                  sub={restMinutes > 0
                    ? `${punchedHrs.toFixed(2)} hrs punched − ${restMinutes} min paid rest`
                    : undefined}
                />
                <MwLedgerRow
                  label={`${state} minimum wage`}
                  value={<><span className="etrc-mw-op">×</span>{fmtMwMoney(m.minimumWageRate)}/hr</>}
                />
                <MwLedgerRow label="Minimum-wage floor" value={fmtMwMoney(m.minimumWageFloor)} isFloor />
                <MwLedgerRow
                  label="Piece earnings"
                  value={<><span className="etrc-mw-op">−</span>{fmtMwMoney(m.pieceEarnings)}</>}
                />
                {owed ? (
                  <div className="etrc-mw-total">
                    <span className="etrc-mw-total-lbl">True-up owed</span>
                    <span className="etrc-mw-total-val etrc-mono">{fmtMwMoney(m.trueUpAmount)}</span>
                  </div>
                ) : (
                  <div className="etrc-mw-total surplus">
                    <span className="etrc-mw-total-lbl">No true-up needed</span>
                    <span className="etrc-mw-total-val etrc-mono">
                      +{fmtMwMoney(m.pieceEarnings - m.minimumWageFloor)}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
          <div className="etrc-mw-note">
            <Info size={14} />
            <span>
              Piece earnings count task, operational, and customer-service claims. Paid 10-minute
              rest breaks are excluded from piece time and compensated separately.
            </span>
          </div>
        </div>

        <div className="etrc-mw-footer">
          <button className="etrc-btn primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDialog({
  issues,
  onConfirm,
  onCancel,
  loading,
}: {
  issues: string[];
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}) {
  return (
    <div className="etrc-overlay" onClick={onCancel}>
      <div className="etrc-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="etrc-confirm-header">
          <AlertTriangle size={20} color="#d4881a" />
          <span>Approve with open issues?</span>
        </div>
        <p className="etrc-confirm-body">
          The following issues were found on this timesheet:
        </p>
        <ul className="etrc-confirm-issues">
          {issues.map((issue, i) => <li key={i}>{issue}</li>)}
        </ul>
        <div className="etrc-confirm-actions">
          <button className="etrc-btn" onClick={onCancel} disabled={loading}>Cancel</button>
          <button className="etrc-btn primary" onClick={onConfirm} disabled={loading}>
            {loading ? 'Approving…' : 'Approve Anyway'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Day Card ──────────────────────────────────────────────────────────────────

interface DayCardProps {
  col: DayCol;
  isExpanded: boolean;
  isActive: boolean;
  canSendBack: boolean;
  isSubmitted: boolean;
  onToggle: () => void;
  onGpsClick: (data: GpsModalData) => void;
  onMinWageClick: () => void;
  /** Opens the day punch map. The button that calls it is hidden unless the day has
   *  at least one recorded coordinate, so this never opens an empty map. */
  onDayMapClick: () => void;
  onSendBack: () => void;
  /** Present only when the caller may edit misc task cost codes. */
  onEditMiscTask?: (row: PieceRow, group: PieceGroup) => void;
  /** `combineTasksInWeek` (design §4): pieces belong to the week, so this card's
   *  per-day piece / misc lists and its `$` chip are replaced by a note pointing at
   *  the Weekly Tasks Summary. Punches / hours / meals / GPS / time off / sick are
   *  untouched. */
  weeklyPieces?: boolean;
  /** Scrolls to the Weekly Tasks Summary; only passed with `weeklyPieces`. */
  onJumpToWeekly?: () => void;
  /** Wallcraft production totals (design §5). Passed for EVERY day once the tenant
   *  flag is on — its presence is what replaces Pieces Claimed with Production
   *  Totals and swaps the piece figures out of the header. Absent everywhere else. */
  production?: DayProductionProps;
  /** TAS-365: the week's punch history keyed `EntityType:EntityID`. Present only when
   *  the caller may read timesheet history; absent → no history affordances at all. */
  punchHistory?: Map<string, PunchHistoryItem>;
  /** TAS-365: punches removed from this day (they no longer appear as rows). */
  removedPunches?: PunchHistoryItem[];
  /** TAS-365: opens the per-punch history modal. */
  onPunchHistory?: (items: PunchHistoryItem[], title: string, subtitle: string, note?: string | null) => void;
  cardRef: (el: HTMLDivElement | null) => void;
}

const DayCard: React.FC<DayCardProps> = ({
  col, isExpanded, isActive, canSendBack, isSubmitted,
  onToggle, onGpsClick, onMinWageClick, onDayMapClick, onSendBack, onEditMiscTask,
  weeklyPieces = false, onJumpToWeekly, production, punchHistory, removedPunches = [], onPunchHistory, cardRef,
}) => {
  const { day, holiday, holidayElig, issues, overnightLabel, totalWorkMin: colTotalWorkMin } = col;

  // Body sections — also tells us whether there is anything to expand at all
  const { punchRows, pieceGroups, opTaskRows, cswRows, workOrderClaims, visibleTimeOffEntries, hasDetail } = useMemo(
    () => dayBodySections(col, isSubmitted),
    [col, isSubmitted]
  );

  // Whether the day map has anything to plot. Memoised because it runs for every day
  // card on every render; a day whose punches were all keyed in by hand has no map.
  const hasPunchCoords = useMemo(() => dayHasPunchCoords(day), [day]);
  const gpsFlaggedCount = useMemo(() => punchRows.filter((r) => r.issueGps).length, [punchRows]);

  // Combine timeEntries (piece) and timeEntriesHourly (hourly/piece) for first-in/out/meal computation
  const allDayEntries = [
    ...(day?.timeEntries ?? []),
    ...(day?.hourlyProjects ?? []).flatMap(hp => hp.timeEntriesHourly ?? []),
  ];
  const regularEntries = allDayEntries.filter(e => e.type !== TimeEntryType.Meal);
  const mealEntry = allDayEntries.find(e => e.type === TimeEntryType.Meal);

  const firstInEntry = regularEntries.length > 0
    ? regularEntries.reduce((a, b) => new Date(a.inDateTime) < new Date(b.inDateTime) ? a : b)
    : null;
  const lastOutEntry = regularEntries.length > 0
    ? regularEntries.reduce((a, b) => {
        if (!b.outDateTime) return a;
        if (!a.outDateTime) return b;
        return new Date(a.outDateTime) > new Date(b.outDateTime) ? a : b;
      })
    : null;

  const firstInTime = firstInEntry?.inDateTime
    ? formatClock(applyOffset(firstInEntry.inDateTime, firstInEntry.inDateTimeOffset))
    : null;
  const lastOutTime = lastOutEntry?.outDateTime
    ? formatClock(applyOffset(lastOutEntry.outDateTime, lastOutEntry.outDateTimeOffset))
    : null;
  const isMissingOut = regularEntries.length > 0 && !lastOutEntry?.outDateTime;

  const mealInTime = mealEntry?.inDateTime
    ? formatClock(applyOffset(mealEntry.inDateTime, mealEntry.inDateTimeOffset))
    : null;
  const mealOutTime = mealEntry?.outDateTime
    ? formatClock(applyOffset(mealEntry.outDateTime, mealEntry.outDateTimeOffset))
    : null;
  // Name the violation rather than calling every penalty "short": the five-hour flag
  // fires on a full-length meal taken too late, which is a different problem entirely.
  const mealPenaltyReason =
    day?.noLunchException ? 'no meal'
    : day?.fiveHourLunchException ? 'late'
    : day?.fullLunchException ? 'short'
    : null;
  const hasMealPenalty = !!mealPenaltyReason;
  const noMeal = regularEntries.length > 0 && !mealEntry;

  const isHolidayDay = !!holiday;
  // Unpaid Time gets its own chip; the paid chip keeps exactly what it showed before.
  const { paid: paidTimeOffEntries, unpaid: unpaidTimeOffEntries } = splitUnpaid(visibleTimeOffEntries);
  const isPTODay = paidTimeOffEntries.length > 0;
  // Task-based sick claims (WCDP) have no timeOffRequestID; TOR-sourced ones (2020PH)
  // already show via the time-off chip above. See TODO(WCDP sick migration) in dayColumns.
  const sickTaskHrs = (day?.employeeSickClaims ?? [])
    .filter((sc) => sc.timeOffRequestID == null)
    .reduce((s, sc) => s + (sc.minutesClaimed ?? 0), 0) / 60;

  if (!dayCardIsRendered(col)) return null;

  return (
    <div
      className={`etrc-day-card${isExpanded ? ' open' : ''}${isActive ? ' active' : ''}`}
      ref={cardRef}
    >
      {/* Collapsed header — nothing to expand means no chevron and no click target */}
      <div
        className={`etrc-day-header${hasDetail ? '' : ' inert'}`}
        onClick={hasDetail ? onToggle : undefined}
      >
        {hasDetail ? (
          <ChevronRight
            size={18}
            className="etrc-chevron"
            color="#8a93a2"
          />
        ) : (
          <span className="etrc-chevron-spacer" aria-hidden />
        )}

        <div className="etrc-day-label">
          <span className="etrc-dow">{col.dow}</span>
          <span className="etrc-date-lbl">{col.dateLabel}</span>
        </div>

        {/* Day punch map — restores the v1 ability to see where every punch was
            recorded, not just the ones that raised a GPS exception. The header
            itself toggles the card, so the click must not bubble. */}
        {hasPunchCoords && (
          <button
            type="button"
            className="etrc-day-mapbtn"
            title="See where every punch was recorded"
            onClick={(e) => { e.stopPropagation(); onDayMapClick(); }}
          >
            <MapPin size={13} />
            Map
            {gpsFlaggedCount > 0 && (
              <span className="flagged"> · {gpsFlaggedCount} flagged</span>
            )}
          </button>
        )}

        <div className="etrc-day-chips">
          {isHolidayDay && (
            <span className="etrc-chip holiday">
              <Sun size={13} color="#7a5cf0" />
              <span><b>{holiday!.name}</b> · {formatHours(holiday!.paidHoursIfNotWorked, 1)} hrs</span>
            </span>
          )}
          {isPTODay && (() => {
            const approvedHrs = paidTimeOffEntries.filter((e) => e.status === 'APPROVED').reduce((s, e) => s + e.hours, 0);
            return (
              <span className="etrc-chip holiday">
                <Sun size={13} color="#7a5cf0" />
                <span>
                  <b>{paidTimeOffEntries[0]?.category}</b>
                  {approvedHrs > 0 ? ` · ${formatDecimalHoursNatural(approvedHrs)} hrs` : ''}
                </span>
              </span>
            );
          })()}
          {unpaidTimeOffEntries.length > 0 && (() => {
            const approvedHrs = unpaidTimeOffEntries.filter((e) => e.status === 'APPROVED').reduce((s, e) => s + e.hours, 0);
            return (
              <span
                className="etrc-chip unpaid"
                title={`${unpaidTimeOffEntries[0]?.category} — unpaid, not counted in Hol / PTO`}
              >
                <span>
                  <b>Unpaid</b>
                  {approvedHrs > 0 ? ` · ${formatDecimalHoursNatural(approvedHrs)} hrs` : ' · pending'}
                </span>
              </span>
            );
          })()}
          {sickTaskHrs > 0 && (
            <span className="etrc-chip holiday">
              <Sun size={13} color="#7a5cf0" />
              <span><b>Sick</b> · {formatDecimalHoursNatural(sickTaskHrs)} hrs</span>
            </span>
          )}
          {/* §5: on a production tenant the split is announced as account codes, and
              the piece/misc vocabulary never appears. */}
          {production && production.day.segments.length > 1 && (
            <span className="etrc-chip etrc-prod-sumchip">
              <Package size={13} color="#0e7c86" />
              <span><b>{production.day.segments.length}</b> codes</span>
            </span>
          )}
          {/* Misc dollars roll up weekly on a combine-in-week tenant — no per-day chip. */}
          {!production && !weeklyPieces && col.miscCount > 0 && (
            <span className="etrc-chip misc">
              <Sparkles size={13} color="#d8852f" />
              <span><b>{col.miscCount} Misc</b> · {fmtDollar(col.miscAmount)}</span>
            </span>
          )}
          {firstInTime && (
            <>
              <span className="etrc-chip">
                <LogIn size={13} color="#8a93a2" />
                <span><b>In</b> {firstInTime}</span>
              </span>
              <span className={`etrc-chip${hasMealPenalty || noMeal ? ' warn' : ''}`}>
                <Coffee size={13} color={hasMealPenalty || noMeal ? '#e0533f' : '#8a93a2'} />
                <span>
                  <b>Meal</b>{' '}
                  {mealInTime
                    ? `${mealInTime} – ${mealOutTime ?? '?'}${mealPenaltyReason ? ` · ${mealPenaltyReason}` : ''}`
                    : 'none'}
                </span>
              </span>
              <span className={`etrc-chip${isMissingOut ? ' warn' : ''}`}>
                <LogOut size={13} color={isMissingOut ? '#e0533f' : '#8a93a2'} />
                <span><b>Out</b> {isMissingOut ? '— missing' : lastOutTime}</span>
              </span>
            </>
          )}
        </div>

        <div className="etrc-day-metrics">
          {col.totalWorkMin > 0 && (
            <span><b>{fmtHrsOrDash(col.totalWorkMin)}</b> hrs</span>
          )}
          {col.totalOTMin > 0 && (
            <> · <span style={{ color: '#d4881a', fontWeight: 600 }}>{fmtHrsOrDash(col.totalOTMin)} OT</span></>
          )}
          {col.totalDTMin > 0 && (
            <> · <span style={{ color: '#b3471c', fontWeight: 700 }}>{fmtHrsOrDash(col.totalDTMin)} DT</span></>
          )}
          {!production && !weeklyPieces && col.totalPiece > 0 && (
            <> · <span><b>{fmtDollar(col.totalPiece)}</b></span></>
          )}
          {/* Work order hour claims are counted apart from the piece total (TAS-291),
              so they get their own trailing figure rather than folding into it. */}
          {col.totalWorkOrder > 0 && (
            <> · <span title="Work order hour claims"><b>{fmtDollar(col.totalWorkOrder)}</b> WO</span></>
          )}
          {/* §5: the trailing metric is the production chip — the day total, or the
              reason it is not yet trustworthy. */}
          {production?.chip && (
            <span className={`etrc-prod-chip ${production.chip.kind}`}>
              {production.chip.kind === 'total'
                ? <DollarSign size={13} />
                : <AlertTriangle size={13} />}
              {production.chip.text}
            </span>
          )}
          {day?.minWageTrueUp?.needed && day.minWageTrueUp.trueUpAmount > 0 && (
            <button
              type="button"
              className="etrc-trueup-chip"
              title="View minimum wage true-up"
              onClick={(e) => { e.stopPropagation(); onMinWageClick(); }}
            >
              <IconDollarSign size={13} color="#5a4fb0" />
              {fmtDollar(day.minWageTrueUp.trueUpAmount)} true-up
            </button>
          )}
        </div>

        <div className="etrc-day-flags">
          {issues.map((issue) => <IssueFlagBadge key={issue} issue={issue} />)}
          {overnightLabel && <IssueFlagBadge issue="overnight" labelOverride={overnightLabel} />}
          {onPunchHistory && removedPunches.length > 0 && (
            <button
              type="button"
              className="etrc-removed-chip"
              title="Show punches removed from this day"
              onClick={(e) => {
                e.stopPropagation();
                onPunchHistory(
                  removedPunches,
                  removedPunches.length === 1 ? 'Removed punch' : `${removedPunches.length} removed punches`,
                  `${col.dow}, ${col.dateLabel}`
                );
              }}
            >
              <History size={12} />
              {removedPunches.length} removed
            </button>
          )}
        </div>
      </div>

      {/* Expanded body */}
      {isExpanded && (
        <div className="etrc-day-body">
          {/* Time Punches table */}
          {punchRows.length > 0 && (
            <>
              <div className="etrc-section-label">
                <Clock size={15} color="#8a93a2" />
                <span>Time Punches</span>
                <span className="etrc-section-sub">
                  {day ? fmtHrsOrDash(colTotalWorkMin) + ' hrs total' : ''}
                </span>
              </div>
              <div className="etrc-table-wrap">
                <table className="etrc-punch-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Job / Code</th>
                      <th>In</th>
                      <th>Out</th>
                      <th className="right">HRS</th>
                      <th></th>
                      <th></th>
                      {/* §5: the punch row's pencil opens the same day/job editor the
                          week grid's pencil does. Production tenants only. */}
                      {production?.onSplit && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {punchRows.map((row) => {
                      const historyItem = row.historyKey ? punchHistory?.get(row.historyKey) : undefined;
                      const openHistory = onPunchHistory && historyItem
                        && offersPunchHistory(historyItem, { ...row, isOvernight: !!row.overnightLabel })
                        ? (e: React.MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onPunchHistory(
                              [historyItem],
                              punchHistoryTitle(row),
                              `${col.dow}, ${col.dateLabel} · ${row.inTime ?? '—'} – ${row.isMissingOut ? 'open' : (row.outTime ?? '—')}`,
                              row.overnightNote ?? null
                            );
                          }
                        : undefined;
                      return (
                      <tr
                        key={row.key}
                        className={row.isMissingOut || row.issueGps || row.isMealPenalty ? 'flagged' : ''}
                      >
                        <td><EntryTypeChip type={row.type} /></td>
                        <td>
                          <div className={row.soBadge || row.projectBadge ? 'etrc-job-so-line' : undefined}>
                            {row.projectBadge && (
                              <span className="etrc-project-badge">{row.projectBadge}</span>
                            )}
                            {row.soBadge && (
                              <span className="etrc-so-badge">{row.soBadge}</span>
                            )}
                            <span className="etrc-job-name">{row.jobName}</span>
                          </div>
                          {row.jobSub && (
                            <span className={`etrc-job-sub${row.isMealPenalty ? ' error' : ''}`}>
                              {row.jobSub}
                            </span>
                          )}
                          {row.soState && (
                            <span
                              className="etrc-so-state"
                              title="This service order has not been matched to a Vista work order, so it carries no scope."
                            >
                              {row.soState}
                            </span>
                          )}
                        </td>
                        <td className="etrc-mono">
                          {row.inTime ?? '—'}
                        </td>
                        <td className={`etrc-mono${row.isMissingOut ? ' missing' : ''}`}>
                          {row.isMissingOut ? '✗' : (row.outTime ?? '—')}
                        </td>
                        <td className="etrc-num right">
                          {row.durationMin != null ? fmtHrsOrDash(row.durationMin) : '—'}
                        </td>
                        <td>
                          {row.gps && (
                            <button
                              type="button"
                              className="etrc-map-btn"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onGpsClick(row.gps!); }}
                            >
                              <MapPin size={12} />Map
                            </button>
                          )}
                        </td>
                        <td>
                          <div className="etrc-punch-flags">
                            {row.isMissingOut && <IssueFlagBadge issue="missed" />}
                            {row.issueGps && !row.isMissingOut && <IssueFlagBadge issue="gps" />}
                            {/* TAS-365: the Edited / Manual badge itself opens the punch's
                                change history (original → each change, who, when). */}
                            {row.isEdited && <IssueFlagBadge issue="edited" onClick={openHistory} />}
                            {row.isManual && <IssueFlagBadge issue="manual" onClick={openHistory} />}
                            {/* Overnight on-call shift (2020CA/NV): the SYSTEM split at 12:00 AM
                                keeps PunchType 1, so this badge is its history entry point. */}
                            {row.overnightLabel && (
                              <IssueFlagBadge issue="overnight" labelOverride={row.overnightLabel} onClick={openHistory} />
                            )}
                            {row.isMealPenalty && <IssueFlagBadge issue="meal" />}
                          </div>
                        </td>
                        {production?.onSplit && (
                          <td>
                            {row.projectID != null && (
                              <button
                                type="button"
                                className="etrc-prod-edit"
                                title="Edit account code, hours or split"
                                aria-label="Edit account codes and hours for this day"
                                onClick={(e) => { e.stopPropagation(); production.onSplit!(row.projectID!); }}
                              >
                                <Pencil size={14} />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Weekly pieces (design §4): the per-day list is replaced by a pointer to
              the one place the claim actually lives. Sick claims stay per-day. */}
          {weeklyPieces && pieceGroups.length > 0 && (
            <div className="etrc-weekly-note">
              <Info size={15} />
              <span>
                Pieces on this timesheet are claimed <b>for the week</b> — see the Weekly Tasks
                Summary below.
              </span>
              {onJumpToWeekly && (
                <button type="button" className="etrc-weekly-notelink" onClick={onJumpToWeekly}>
                  Go to summary
                </button>
              )}
            </div>
          )}

          {/* ── Production Totals (design §5) — replaces Pieces Claimed on a
              production tenant. Role-OFF employees see PO-code rows only: they owe a
              PO #, never a total. */}
          {production && production.day.visible.length > 0 && (
            <>
              <div className="etrc-section-label bordered">
                <DollarSign size={15} color="#0e7c86" />
                <span>Production Totals</span>
                <span className="etrc-section-sub">
                  {production.day.visible.length} account code
                  {production.day.visible.length === 1 ? '' : 's'}
                  {production.roleRequires && <> · {prodMoney(production.day.entryTotal)}</>}
                </span>
              </div>
              <div className="etrc-table-wrap">
                <table className="etrc-piece-table etrc-prod-table">
                  <thead>
                    <tr>
                      <th>Job / Phase</th>
                      <th>Account Code</th>
                      <th>In / Out</th>
                      <th className="right">Hrs</th>
                      <th>Base Pay</th>
                      <th>Total $</th>
                      <th>PO #</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {production.day.visible.map((seg) => (
                      <tr key={seg.key}>
                        <td>
                          <span className="etrc-prod-jobcell">
                            <span className="jn">
                              {seg.altProjectID ? `#${seg.altProjectID} ` : ''}{seg.projectName}
                            </span>
                            {seg.phaseName && <span className="jm">{seg.phaseName}</span>}
                          </span>
                        </td>
                        <td>
                          <span className="etrc-prod-codecell">
                            <span className="cn">
                              {seg.accountCodeName}
                              {seg.isPO && <span className="etrc-prod-pobadge">PO</span>}
                            </span>
                            <span className="cc">{seg.altAccountCodeID}</span>
                          </span>
                        </td>
                        <td className="etrc-mono">
                          {seg.inTime ?? '—'} – {seg.outTime ?? '—'}
                        </td>
                        <td className="etrc-num right">{prodHours(seg.hours)}</td>
                        <td className="etrc-prod-basecell">
                          {production.rate > 0 ? prodMoney(seg.base) : '—'}
                          <span className="sub">
                            {production.rate > 0
                              ? `${prodHours(seg.hours)} h × ${prodMoney(production.rate)}`
                              : 'no rate on file'}
                          </span>
                        </td>
                        <td className="etrc-prod-amtcell">
                          {production.roleRequires ? (
                            <>
                              {production.renderAmount(seg, 'lg')}
                              {production.renderFlag(seg, 'md')}
                              {production.renderProvenance(seg)}
                            </>
                          ) : (
                            <span className="etrc-text-3">—</span>
                          )}
                        </td>
                        <td className="etrc-prod-amtcell">
                          {seg.isPO
                            ? production.renderPo(seg, 'lg')
                            : <span className="etrc-text-3">—</span>}
                        </td>
                        <td className="right" style={{ width: 54 }}>
                          {production.onSplit && (
                            <button
                              type="button"
                              className="etrc-prod-edit lg"
                              title="Edit account code, hours or split"
                              aria-label="Edit account codes and hours for this day"
                              onClick={(e) => { e.stopPropagation(); production.onSplit!(seg.projectID); }}
                            >
                              <Pencil size={15} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {production.roleRequires && (
                <div className="etrc-prod-daybar">
                  <Info size={15} />
                  <span>
                    Day total <b>{prodMoney(production.day.entryTotal)}</b> across{' '}
                    {production.day.visible.length} account code
                    {production.day.visible.length === 1 ? '' : 's'}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Pieces Claimed — grouped by job */}
          {!production && !weeklyPieces && pieceGroups.length > 0 && (
            <>
              <div className="etrc-section-label bordered">
                <Package size={15} color="#8a93a2" />
                <span>Pieces Claimed</span>
                <span className="etrc-section-sub">{fmtDollar(pieceGroups.reduce((s, g) => s + g.totalAmount, 0))}</span>
              </div>
              <div className="etrc-table-wrap">
                <table className="etrc-piece-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Description</th>
                      <th>Phase / Lot</th>
                      <th>Type</th>
                      <th className="right">Claimed $</th>
                      <th className="right">% Claimed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pieceGroups.map((group) => (
                      <React.Fragment key={group.projectID}>
                        <tr className="etrc-piece-jobgroup-row">
                          <td colSpan={6}>
                            <div className="etrc-piece-jobgroup">
                              <Package size={12} color="#2f6fed" />
                              {group.altProjectID && (
                                <span className="etrc-project-badge">{group.altProjectID}</span>
                              )}
                              <span>{group.projectName}</span>
                              <span className="etrc-piece-jobgroup-total">{fmtDollar(group.totalAmount)}</span>
                            </div>
                          </td>
                        </tr>
                        {group.rows.map((row) => {
                          const pct = row.type === 'piece' && row.originalAmount && row.originalAmount > 0
                            ? Math.round((row.amount / row.originalAmount) * 100)
                            : null;
                          return (
                            <tr key={row.key} className="etrc-piece-indented">
                              <td
                                className={`etrc-mono${row.type === 'misc' ? ' etrc-code-misc' : ''}`}
                                style={{ fontWeight: 700 }}
                              >
                                <span className="etrc-code-val">{row.code}</span>
                                {row.type === 'misc' && onEditMiscTask && row.miscEmployeeTaskID != null && (
                                  <button
                                    type="button"
                                    className="etrc-mtc-edit-btn"
                                    title="Edit cost code / description"
                                    aria-label="Edit misc task cost code or description"
                                    onClick={(e) => { e.stopPropagation(); onEditMiscTask(row, group); }}
                                  >
                                    <Pencil size={13} />
                                  </button>
                                )}
                              </td>
                              <td>{row.description}</td>
                              <td className="etrc-text-3">{row.phaseLot || '—'}</td>
                              <td><EntryTypeChip type={row.type} /></td>
                              <td className="etrc-num right">{fmtDollar(row.amount)}</td>
                              <td className="etrc-num right">{pct != null ? `${pct}%` : '—'}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Meetings / Other Tasks — operational task claims (hours × rate, no punches) */}
          {opTaskRows.length > 0 && (
            <>
              <div className="etrc-section-label bordered">
                <ClipboardList size={15} color="#8a93a2" />
                <span>Meetings / Other Tasks</span>
                <span className="etrc-section-sub">
                  {fmtDollar(opTaskRows.reduce((s, r) => s + r.amount, 0))}
                </span>
              </div>
              <div className="etrc-table-wrap">
                <table className="etrc-piece-table">
                  <thead>
                    <tr>
                      <th>Task</th>
                      <th>Description</th>
                      <th>Account</th>
                      <th>Type</th>
                      <th className="right">HRS</th>
                      <th className="right">Claimed $</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opTaskRows.map((row) => (
                      <tr key={row.key}>
                        <td style={{ fontWeight: 700 }}>
                          {row.projectBadge && (
                            <span className="etrc-project-badge" style={{ marginRight: 6 }}>{row.projectBadge}</span>
                          )}
                          {row.name}
                        </td>
                        <td>{row.description || '—'}</td>
                        <td className="etrc-text-3">{row.account || '—'}</td>
                        <td><EntryTypeChip type="opTask" /></td>
                        <td className="etrc-num right">{fmtHrsOrDash(row.minutes)}</td>
                        <td className="etrc-num right">{fmtDollar(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Customer Service / Warranty — claim-flavored CSW (dollar amount, no punches).
              Punched CSW appears in the Punches section above instead. */}
          {cswRows.length > 0 && (
            <ClaimSection
              title="Customer Service / Warranty"
              icon={<LifeBuoy size={15} color="#8a93a2" />}
              rows={cswRows}
            />
          )}

          {/* Work Orders — hour claims paid at the rate snapshotted on the claim (TAS-291).
              Its own table rather than a ClaimSection: the rate and the supervisor rate
              override are the point of the section, and ClaimSection has no column for them. */}
          {workOrderClaims.length > 0 && (
            <>
              <div className="etrc-section-label bordered">
                <ClipboardList size={15} color="#8a93a2" />
                <span>Work Orders</span>
                <span className="etrc-section-sub">
                  {formatDecimalHoursNatural(workOrderClaims.reduce((s, c) => s + (c.hoursClaimed ?? 0), 0))} hrs · {fmtDollar(col.totalWorkOrder)}
                </span>
              </div>
              <div className="etrc-table-wrap">
                <table className="etrc-piece-table">
                  <thead>
                    <tr>
                      <th>Work Order</th>
                      <th>Job</th>
                      <th className="right">Rate</th>
                      <th className="right">Hours</th>
                      <th className="right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workOrderClaims.map((c) => {
                      const rate = c.hourlyRate ?? c.workOrderTask?.laborPerHour ?? 0;
                      return (
                        <tr key={c.employeeWorkOrderTaskID}>
                          <td className="etrc-mono" style={{ fontWeight: 700 }}>{c.workOrderTask?.name ?? '—'}</td>
                          <td>
                            <div className={c.project?.altProjectID ? 'etrc-job-so-line' : undefined}>
                              {c.project?.altProjectID && (
                                <span className="etrc-project-badge">{c.project.altProjectID}</span>
                              )}
                              <span className="etrc-job-name">{c.project?.name ?? '—'}</span>
                            </div>
                            {c.workOrderTask?.description && (
                              <span className="etrc-job-sub">{c.workOrderTask.description}</span>
                            )}
                          </td>
                          <td className="etrc-num right">
                            {fmtDollar(rate)}/hr
                            {c.rateSource === 'override' && (
                              <span className="etrc-text-3" title="Work order rate override set by a supervisor"> · override</span>
                            )}
                          </td>
                          <td className="etrc-num right">{formatDecimalHoursNatural(c.hoursClaimed ?? 0)}</td>
                          <td className="etrc-num right">{fmtDollar(c.amountImplied ?? 0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Time Off section — display only; actions are in the dedicated time-off block above */}
          {visibleTimeOffEntries.length > 0 && (
            <>
              <div className="etrc-section-label bordered">
                <Sun size={15} color="#8a93a2" />
                <span>Time Off</span>
              </div>
              <div className="etrc-to-list">
                {visibleTimeOffEntries.map((entry, i) => (
                  <div key={i} className="etrc-to-row">
                    <span className={`etrc-status-pill ${entry.status.toLowerCase()}`}>
                      {entry.status}
                    </span>
                    <span>{entry.category}{entry.isUnpaid === true ? ' (unpaid)' : ''}</span>
                    <span>{entry.hours} hrs</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Holiday section */}
          {holiday && (
            <>
              <div className="etrc-section-label bordered">
                <Sun size={15} color="#7a5cf0" />
                <span style={{ color: '#7a5cf0' }}>{holiday.name}</span>
                <span className="etrc-section-sub">
                  {formatHours(holiday.paidHoursIfNotWorked, 1)} hrs if eligible
                </span>
              </div>
              {holidayElig && (
                <div className="etrc-hol-checks">
                  {holidayElig.tenureStatus !== 'na' && (
                    <div className={`etrc-hol-check ${holidayElig.tenureStatus === 'unknown' ? 'outside' : holidayElig.tenureStatus}`}>
                      {holidayElig.tenureStatus === 'pass' && '✓'}
                      {holidayElig.tenureStatus === 'fail' && '✗'}
                      {holidayElig.tenureStatus === 'unknown' && '?'}
                      <span>{holidayElig.tenureNote}</span>
                    </div>
                  )}
                  {holiday.requireWorkDayBefore && (
                    <div className={`etrc-hol-check ${holidayElig.beforeStatus}`}>
                      {holidayElig.beforeStatus === 'pass' && '✓'}
                      {holidayElig.beforeStatus === 'fail' && '✗'}
                      {holidayElig.beforeStatus === 'outside' && '?'}
                      <span>
                        {holidayElig.beforeStatus === 'fail' ? 'Did not work' : 'Worked'} day before ({fmtShortDate(holidayElig.beforeDate)})
                        {holidayElig.beforeNote}
                      </span>
                    </div>
                  )}
                  {holiday.requireWorkDayAfter && (
                    <div className={`etrc-hol-check ${holidayElig.afterStatus}`}>
                      {holidayElig.afterStatus === 'pass' && '✓'}
                      {holidayElig.afterStatus === 'fail' && '✗'}
                      {holidayElig.afterStatus === 'outside' && '?'}
                      <span>
                        {holidayElig.afterStatus === 'fail' ? 'Did not work' : 'Worked'} day after ({fmtShortDate(holidayElig.afterDate)})
                        {holidayElig.afterNote}
                      </span>
                    </div>
                  )}
                  {(holidayElig.tenureStatus === 'fail' ||
                    holidayElig.beforeStatus === 'fail' ||
                    holidayElig.afterStatus === 'fail') && (
                    <div className="etrc-hol-ineligible">
                      Employee ineligible for holiday pay — 0.0 hrs payable
                    </div>
                  )}
                </div>
              )}
            </>
          )}

        </div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

export const EmployeeTimesheetReviewConsole: React.FC<ConsoleProps> = ({
  timesheetData,
  driveTimeEntries,
  timeOffRequests,
  timeOffDetailMap,
  holidays,
  selectedStatus,
  payPeriods,
  reviewWeekIdx,
  onSelectWeek,
  supervisorName,
  approveLoading,
  reviewLoading,
  noTimesheetForWeek,
  onApproveTimeOff,
  onDenyTimeOff,
  onSendBack,
  onApproveWeek,
  onRefresh,
  canApproveStagePerm = true,
  canUnlockStagePerm = true,
  payrollEntryCounts = null,
  payrollPreview = null,
  payrollPreviewLoading = false,
  onEditAsPayroll,
  onSubmitForEmployee,
  canViewHistoryPerm = false,
  onChangeSupervisor,
  canEditMiscTaskCodePerm = false,
  adminEmployeeID,
  adminName,
  onStageAdvanced,
  onToast,
  canApproveOnBehalfModifiedDuty = false,
}) => {
  const currentPayPeriod = payPeriods[reviewWeekIdx];
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [activeCol, setActiveCol] = useState<string | null>(null);
  const [gpsModal, setGpsModal] = useState<GpsModalData | null>(null);
  /** The day whose punch map is open. Holds the whole column so the modal gets the
   *  day/date labels for its title without a second lookup. */
  const [dayMapCol, setDayMapCol] = useState<DayCol | null>(null);
  const [minWageModal, setMinWageModal] = useState<MinWageModalData | null>(null);
  const [miscTaskEdit, setMiscTaskEdit] = useState<MiscTaskCodeTarget | null>(null);
  const [timeOffBlockOpen, setTimeOffBlockOpen] = useState(false);
  const [certOpen, setCertOpen] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [printResult, setPrintResult] = useState<'success' | 'failure' | null>(null);
  // One shared side-panel slot — Change history and Audit log are mutually
  // exclusive; opening either closes the other (design: two buttons, one slot).
  const [openPanel, setOpenPanel] = useState<'audit' | 'change' | null>(null);
  // TAS-365 — per-punch change history (one read per employee week) + its modal.
  const [punchHistoryItems, setPunchHistoryItems] = useState<PunchHistoryItem[] | null>(null);
  const [punchHistoryModal, setPunchHistoryModal] = useState<{
    items: PunchHistoryItem[];
    title: string;
    subtitle: string;
    note?: string | null;
  } | null>(null);
  const [auditRows, setAuditRows] = useState<Audit[] | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(false);
  const dayCardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const days = timesheetData.days ?? [];

  const payPeriodStart = currentPayPeriod ? isoDateOnly(new Date(currentPayPeriod.StartDate)) : '';
  const payPeriodEnd = currentPayPeriod ? isoDateOnly(new Date(currentPayPeriod.EndDate)) : '';

  // Build holiday eligibility map
  const holidayEligMap = useMemo(() => {
    const map: Record<number, HolidayEligibility> = {};
    holidays.forEach((h) => {
      map[h.holidayCalendarID] = checkHolidayEligibility(
        h, days, timeOffDetailMap, payPeriodStart, payPeriodEnd
      );
    });
    return map;
  }, [holidays, days, timeOffDetailMap, payPeriodStart, payPeriodEnd]);

  // Build 7-day columns
  const dayColumns = useMemo((): DayCol[] => {
    if (!currentPayPeriod) return [];
    return Array.from({ length: 7 }, (_, i) => {
      const date = addDays(payPeriodStart, i);
      const day = days.find((d) => d.date.substring(0, 10) === date) ?? null;
      const holiday = holidays.find((h) => h.holidayDate === date) ?? null;
      const holidayElig = holiday ? holidayEligMap[holiday.holidayCalendarID] : null;
      const timeOffEntries = timeOffDetailMap[date] ?? [];
      const driveEntries = driveTimeEntries.filter((e) => e.workDate.substring(0, 10) === date);

      const issues: IssueType[] = [];
      let overnightLabel: string | null = null;
      if (day) {
        const hourlyEntries = (day.hourlyProjects ?? []).flatMap((hp) => hp.timeEntriesHourly ?? []);
        const allEntries = [...(day.timeEntries ?? []), ...hourlyEntries];
        overnightLabel = overnightDayLabel(hourlyEntries);
        if (allEntries.some((e) => e.type !== TimeEntryType.Meal && !!e.inDateTime && !e.outDateTime)) issues.push('missed');
        if (day.fiveHourLunchException || day.fullLunchException || day.noLunchException) issues.push('meal');
        if (allEntries.some((e) => e.gpsException?.inException || e.gpsException?.outException)) issues.push('gps');
        if (allEntries.some((e) => e.punchTypeIn === PunchType.ManualEdit || e.punchTypeOut === PunchType.ManualEdit)) issues.push('edited');
        if (allEntries.some((e) => e.punchTypeIn === PunchType.ManualAdd || e.punchTypeOut === PunchType.ManualAdd)) issues.push('manual');
        if (day.minWageTrueUp?.needed === true) issues.push('minwage');
        const completePunches = allEntries
          .filter((e) => !!e.inDateTime && !!e.outDateTime && !('driveTimeEntryID' in e && e.driveTimeEntryID != null))
          .sort((a, b) => new Date(a.inDateTime).getTime() - new Date(b.inDateTime).getTime());
        if (
          completePunches.length > 1 &&
          completePunches.some((e, i) =>
            i < completePunches.length - 1 &&
            new Date(e.outDateTime).getTime() !== new Date(completePunches[i + 1].inDateTime).getTime()
          )
        ) issues.push('gap');
      }
      if (driveEntries.length > 0) issues.push('drive');

      // Unpaid Time (TAS-586) is never paid, so it stays out of Hol / PTO, the week total and
      // the KPI; the day still shows it, with its own "Unpaid" chip.
      const approvedTOHours = paidApprovedTimeOffHours(timeOffEntries);
      // Sick hours claimed via the mobile "sick task" (WCDP today). Claims that were
      // materialized from a time-off request (2020PH, and WCDP once it migrates to
      // sick time requests) carry a timeOffRequestID and are already counted in
      // approvedTOHours above, so exclude them here to avoid double counting.
      // TODO(WCDP sick migration): once WCDP moves to time-off requests, the
      // task-based claims should disappear and this term can be removed.
      const sickTaskHours = (day?.employeeSickClaims ?? [])
        .filter((sc) => sc.timeOffRequestID == null)
        .reduce((s, sc) => s + (sc.minutesClaimed ?? 0), 0) / 60;
      const holPTOHours = approvedTOHours + sickTaskHours + (holiday ? holiday.paidHoursIfNotWorked / 60 : 0);
      const holIneligible = holiday && holidayElig && (
        holidayElig.tenureStatus === 'fail' ||
        holidayElig.beforeStatus === 'fail' ||
        holidayElig.afterStatus === 'fail'
      );
      const holPTOEligHours = holIneligible
        ? holPTOHours - (holiday ? holiday.paidHoursIfNotWorked / 60 : 0)
        : holPTOHours;

      return {
        date,
        dow: fmtDow(date),
        dateLabel: fmtShortDate(date),
        day,
        holiday,
        holidayElig,
        timeOffEntries,
        driveEntries,
        issues,
        overnightLabel,
        totalWorkMin: (day?.totalWorkMinutes ?? 0) + (selectedStatus === 'unsubmitted' ? driveEntries.reduce((s, e) => s + e.excessMinutes, 0) : 0),
        totalOTMin: day?.overTime ?? 0,
        totalDTMin: day?.doubleTime ?? 0,
        // The server's totalClaimedAmount covers projectEmployeeTasks only, so the
        // other claim tables are added here. Matches what TimesheetSummaryBuilder
        // sums server-side, minus sick claims (surfaced separately as a Sick chip)
        // and work order claims (their own totalWorkOrder figure below).
        totalPiece: (day?.totalClaimedAmount ?? 0)
          + (day?.employeeOperationalTasks ?? []).reduce((s, t) => s + (t.amountClaimed ?? 0), 0)
          + (day?.employeeCustomerServiceWarrantyTasks ?? []).reduce((s, t) => s + (t.amountClaimed ?? 0), 0),
        totalWorkOrder: (day?.employeeWorkOrderTasks ?? []).reduce((s, c) => s + (c.amountImplied ?? 0), 0),
        // Same source buildPieceGroups() reads, so the flag always agrees with
        // the misc rows shown in the expanded day card.
        miscCount: (day?.projectEmployeeTasks ?? []).reduce(
          (s, pet) => s + (pet.miscEmployeeTasks?.length ?? 0), 0),
        miscAmount: (day?.projectEmployeeTasks ?? []).reduce(
          (s, pet) => s + (pet.miscEmployeeTasks ?? []).reduce((ms, mt) => ms + (mt.amountClaimed ?? 0), 0), 0),
        holPTOHours,
        holPTOEligHours,
      };
    });
  }, [currentPayPeriod, payPeriodStart, days, holidays, holidayEligMap, timeOffDetailMap, driveTimeEntries, selectedStatus]);

  // Week totals
  const totalWorkMin = dayColumns.reduce((s, c) => s + c.totalWorkMin, 0);
  const totalOTMin = dayColumns.reduce((s, c) => s + c.totalOTMin, 0);
  const totalDTMin = dayColumns.reduce((s, c) => s + c.totalDTMin, 0);
  const totalPiece = dayColumns.reduce((s, c) => s + c.totalPiece, 0);
  const totalWorkOrder = dayColumns.reduce((s, c) => s + c.totalWorkOrder, 0);
  const totalHolPTOHrs = dayColumns.reduce((s, c) => s + c.holPTOEligHours, 0);

  const totalPieceCount = dayColumns.reduce((s, c) => {
    if (!c.day) return s;
    return s + (c.day.projectEmployeeTasks ?? []).reduce(
      (ps, pet) => ps + pet.employeeTasks.length + pet.miscEmployeeTasks.length, 0
    );
  }, 0);

  // Misc claims across the whole week. On a combine-in-week tenant the per-day piece
  // figures do not exist, so this is the only place a reviewer can be told the week
  // carries misc tasks at all.
  const totalMiscCount = dayColumns.reduce((s, c) => s + c.miscCount, 0);
  const totalMiscAmount = dayColumns.reduce((s, c) => s + c.miscAmount, 0);

  // TAS-514 — the detail behind the board's "Cust. service" chip, one line per service
  // order for the week. The chip and its count are computed server-side
  // (TimesheetSummarySnapshot.HasCustomerService, whose NPM rule was already widened to
  // all three SO states in TAS-507), so what the console owes the reviewer is WHICH
  // orders those were, what scope each one is on, and which are not matched to Vista yet.
  //
  // NPM only, via the same tehPunchesAreHourly gate the punch rows use — on Wallcraft
  // ServiceOrderText holds a purchase order number, and WCDP / 2020PH light this chip
  // from CSW claims and linked ServiceOrders respectively, neither of which has a scope.
  const serviceOrderSummary = useMemo(() => {
    if (!config.tehPunchesAreHourly) return [];

    // Keyed by number + scope: one service order worked on two scopes is two lines,
    // which is exactly the distinction NPM asked to be able to see.
    const byKey = new Map<string, { number: string; name: string | null; scope: string; state: string | null; punches: number }>();

    dayColumns.forEach((col) => {
      (col.day?.hourlyProjects ?? []).forEach((hp) => {
        (hp.timeEntriesHourly ?? []).forEach((entry) => {
          if (entry.type === TimeEntryType.Meal) return;
          const so = describeNpmServiceOrder(hp, entry);
          if (!so) return;
          const key = `${so.number}|${so.scope}`;
          const existing = byKey.get(key);
          if (existing) existing.punches += 1;
          else byKey.set(key, { ...so, punches: 1 });
        });
      });
    });

    // Unvalidated orders first — they are the ones a reviewer may need to chase — then
    // by number so the list is stable between refreshes.
    return [...byKey.values()].sort((a, b) =>
      (a.state ? 0 : 1) - (b.state ? 0 : 1) || a.number.localeCompare(b.number)
    );
  }, [dayColumns]);

  const totalIssues = dayColumns.reduce((s, c) => s + c.issues.length, 0);
  const ineligibleHolidays = holidays.filter((h) => {
    const e = holidayEligMap[h.holidayCalendarID];
    return e && (e.tenureStatus === 'fail' || e.beforeStatus === 'fail' || e.afterStatus === 'fail');
  });
  const totalAlertsWithHoliday = totalIssues + ineligibleHolidays.length;

  // Only requests whose dates overlap the current pay period matter for the approve gate
  const relevantTimeOffRequests = useMemo(() => {
    if (!payPeriodStart || !payPeriodEnd) return [];
    return timeOffRequests.filter(
      (r) => r.startDate.substring(0, 10) <= payPeriodEnd && r.endDate.substring(0, 10) >= payPeriodStart
    );
  }, [timeOffRequests, payPeriodStart, payPeriodEnd]);

  const hasPendingTimeOff = relevantTimeOffRequests.some((r) => r.status === 'PENDING');

  // ── Wallcraft production totals (design_handoff_wallcraft_production_totals) ──
  // One tenant flag decides which row and which section render — same page, same
  // components, no fork. With it off, every const below is inert and the console is
  // byte-identical to the shipped one for every other client.
  const prodCfg = config.productionPay;
  const productionPayOn = prodCfg?.enabled === true;
  const prodDailyMax = prodCfg?.dailyMax ?? 600;
  const prodPoAccountCodeID = prodCfg?.poAccountCodeID ?? null;
  /** Per-employee requiredness comes off the Role that already ships in the payload. */
  const prodRoleRequires = productionPayOn && timesheetData.role?.requireProductionPay === true;
  /** Rule 3's base is measured against the same rate the header prints. */
  const prodRate = typeof timesheetData.hourlyRate === 'number' ? timesheetData.hourlyRate : 0;
  /** Rule 8 — entries, PO #s and splits are editable until payroll approves. */
  const prodLocked = selectedStatus === 'payrollApproved';

  const [prodDrafts, setProdDrafts] = useState<Record<string, string>>({});
  const [prodBusy, setProdBusy] = useState<Set<string>>(new Set());
  const [prodShake, setProdShake] = useState<string | null>(null);
  /** Field that just saved — drives the brief green "updated" pulse. */
  const [prodSaved, setProdSaved] = useState<string | null>(null);
  const [prodToast, setProdToast] = useState<{ msg: string; bad: boolean; n: number } | null>(null);
  const [prodSplit, setProdSplit] = useState<ProductionSplitTarget | null>(null);
  const [prodBlockOpen, setProdBlockOpen] = useState(false);
  const prodRefs = useRef<Record<string, HTMLInputElement | null>>({});
  /** Set by the keyboard run so focus survives the refresh that follows a commit. */
  const prodFocusAfter = useRef<string | null>(null);
  /** Enter/Tab commit + programmatic focus fires a blur on the field we just left;
   *  this stops it committing the same value twice (the mock's `dataset.esc`). */
  const prodSkipBlur = useRef<string | null>(null);

  // The only "entered by" name the console can resolve without a new read is the
  // signed-in admin; anything else falls back to the name the API supplies, and to
  // nothing at all when neither is available (design: omit gracefully).
  const resolveProdAdminName = React.useCallback(
    (id: number | null | undefined): string | null =>
      (id != null && adminEmployeeID != null && id === adminEmployeeID ? (adminName ?? null) : null),
    [adminEmployeeID, adminName]
  );

  // Committed-value overlay — a commit re-derives the model from here instead of
  // reloading the whole timesheet (see ProdOverride).
  const [prodOverrides, setProdOverrides] = useState<Record<string, ProdOverride>>({});

  const productionModel = useMemo(
    () => buildProductionModel(
      productionPayOn ? dayColumns : [],
      prodRate,
      prodRoleRequires,
      prodPoAccountCodeID,
      resolveProdAdminName,
      prodOverrides
    ),
    [productionPayOn, dayColumns, prodRate, prodRoleRequires, prodPoAccountCodeID, resolveProdAdminName, prodOverrides]
  );

  /** Approve is gated only when this employee actually owes production data. */
  const prodGateActive = productionPayOn && productionModel.applies && !prodLocked;
  /** Blockers binding in the CURRENT context: missing/below-base totals only nag once
   *  the week is submitted (and only for a RequireProductionPay role); PO #s always. */
  const prodWeekSubmitted = selectedStatus !== 'unsubmitted' && selectedStatus !== null;
  const prodActiveBlockers = [
    ...(prodWeekSubmitted && prodRoleRequires ? productionModel.productionBlockers : []),
    ...productionModel.poBlockers,
  ];
  const prodBlocked = prodGateActive && prodActiveBlockers.length > 0;

  // ── Field identity: one key for the draft map, one per surface for the ref map.
  const prodFieldKey = (segKey: string, kind: ProdFieldKind) => `${segKey}|${kind}`;
  const prodRefKey = (where: 'grid' | 'card', segKey: string, kind: ProdFieldKind) =>
    `${where}|${segKey}|${kind}`;

  const clearProdDraft = (fk: string) =>
    setProdDrafts((prev) => {
      if (!(fk in prev)) return prev;
      const next = { ...prev };
      delete next[fk];
      return next;
    });

  const showProdToast = (msg: string, bad = false) => setProdToast({ msg, bad, n: Date.now() });

  const shakeProdField = (fk: string) => {
    setProdShake(fk);
    window.setTimeout(() => setProdShake((cur) => (cur === fk ? null : cur)), 360);
  };

  const flashProdSaved = (fk: string) => {
    setProdSaved(fk);
    window.setTimeout(() => setProdSaved((cur) => (cur === fk ? null : cur)), 1400);
  };

  useEffect(() => {
    if (!prodToast) return;
    const t = window.setTimeout(() => setProdToast(null), 4200);
    return () => window.clearTimeout(t);
  }, [prodToast]);

  // Drafts belong to one week; a nav or a different employee must not carry them over.
  useEffect(() => {
    setProdDrafts({});
    setProdBusy(new Set());
    setProdSplit(null);
    setProdBlockOpen(false);
    setProdOverrides({});
  }, [timesheetData.employeeID, currentPayPeriod?.PayPeriodID]);

  // A real reload (week change, split save, Refresh) supersedes the overlay — the
  // committed values are in the fresh payload now, or someone else's edit wins.
  // Commits themselves never change `timesheetData`, so they never wipe their own
  // overlay.
  useEffect(() => {
    setProdOverrides({});
  }, [timesheetData]);

  // Rule 7's "re-focus by key after render" — the refresh that follows a commit can
  // remount the body, so the field the run moved to is restored rather than lost.
  useEffect(() => {
    const k = prodFocusAfter.current;
    if (!k) return;
    prodFocusAfter.current = null;
    const el = prodRefs.current[k];
    if (el && document.activeElement !== el) {
      el.focus();
      el.select();
    }
  }, [timesheetData]);

  /** The provenance stamp for a value committed this second. Browser-local time —
   *  the reviewer's clock, which is what "you just entered this" should read as;
   *  the next real reload swaps in the server's tenant-zone stamp. */
  const prodStampNow = (): string => {
    const d = new Date();
    const h = d.getHours() % 12 || 12;
    const m = d.getMinutes().toString().padStart(2, '0');
    return `${d.getMonth() + 1}/${d.getDate()} ${h}:${m}${d.getHours() >= 12 ? 'p' : 'a'}`;
  };

  /** One write, one outcome: revert + shake + toast on refusal; on success the
   *  accepted value is applied locally (committed-value overlay) — no reload. */
  const runProdWrite = async (
    fk: string,
    call: () => Promise<ProductionPayResult>,
    successToast: string | null,
    onSuccess?: (r: ProductionPayResult) => void
  ): Promise<boolean> => {
    setProdBusy((prev) => new Set(prev).add(fk));
    try {
      const result = await call();
      clearProdDraft(fk);
      if (!result.success) {
        shakeProdField(fk);
        showProdToast(result.errorMessage ?? 'Could not save — please try again.', true);
        return false;
      }
      onSuccess?.(result);
      flashProdSaved(fk);
      if (successToast) showProdToast(successToast);
      return true;
    } finally {
      setProdBusy((prev) => {
        const next = new Set(prev);
        next.delete(fk);
        return next;
      });
    }
  };

  const commitProdAmount = async (seg: ProdSegment, raw: string) => {
    if (prodLocked || !currentPayPeriod) return;
    const fk = prodFieldKey(seg.key, 'amt');
    const text = String(raw).trim().replace(/[$,\s]/g, '');
    const body = {
      employeeID: timesheetData.employeeID,
      payPeriodID: currentPayPeriod.PayPeriodID,
      entryDate: seg.date,
      projectID: seg.projectID,
      accountCodeID: seg.accountCodeID,
    };

    if (text === '') {
      if (seg.amount == null) { clearProdDraft(fk); return; }
      await runProdWrite(
        fk,
        () => upsertProductionEntry({ ...body, amount: null }),
        `${seg.accountCodeName} total cleared · ${seg.dow}`,
        () => setProdOverrides((prev) => ({
          ...prev,
          [seg.key]: { ...prev[seg.key], amount: null },
        }))
      );
      return;
    }

    const parsed = Number(text);
    if (!isFinite(parsed) || parsed < 0) {
      clearProdDraft(fk);
      shakeProdField(fk);
      showProdToast('Enter a dollar amount — numbers only, no negatives.', true);
      return;
    }
    const value = prodRound(parsed);
    if (seg.amount != null && value === seg.amount) { clearProdDraft(fk); return; }

    // Rule 4 — the cap is on the DAY across every account code, and it is refused
    // outright rather than saved and flagged. The server re-checks it.
    const others = prodRound((productionModel.byDate[seg.date]?.entryTotal ?? 0) - (seg.amount ?? 0));
    if (prodRound(others + value) > prodDailyMax + 0.005) {
      const left = Math.max(0, prodRound(prodDailyMax - others));
      clearProdDraft(fk);
      shakeProdField(fk);
      showProdToast(
        `Over the ${prodMoney0(prodDailyMax)} daily cap — ${seg.dow} already has ${prodMoney(others)} `
        + `on other codes, so ${prodMoney(left)} is the most this entry can take.`,
        true
      );
      return;
    }

    const ok = await runProdWrite(
      fk,
      () => upsertProductionEntry({ ...body, amount: value }),
      null,
      (r) => setProdOverrides((prev) => ({
        ...prev,
        [seg.key]: {
          ...prev[seg.key],
          amount: value,
          miscEmployeeTaskID: r.miscEmployeeTaskID ?? seg.miscEmployeeTaskID,
          enteredBy: adminName ?? null,
          enteredAt: prodStampNow(),
        },
      }))
    );
    if (!ok) return;
    // Rule 3 — below base is SAVED, shown in the error state and blocks approval.
    if (!productionModel.rateMissing && prodRoleRequires && value < seg.base - 0.005) {
      showProdToast(
        `Below base pay — ${seg.accountCodeName} needs at least ${prodMoney(seg.base)} for `
        + `${prodHours(seg.hours)} h × ${prodMoney(prodRate)}.`,
        true
      );
    } else {
      showProdToast(
        `${seg.accountCodeName} set to ${prodMoney(value)} · ${seg.dow} total ${prodMoney(prodRound(others + value))}`
      );
    }
  };

  const commitProdPo = async (seg: ProdSegment, raw: string) => {
    if (prodLocked || !currentPayPeriod) return;
    const fk = prodFieldKey(seg.key, 'po');
    const text = String(raw).trim();
    const body = {
      employeeID: timesheetData.employeeID,
      payPeriodID: currentPayPeriod.PayPeriodID,
      entryDate: seg.date,
      projectID: seg.projectID,
    };

    if (text === '') {
      if (seg.poNumber == null) { clearProdDraft(fk); return; }
      await runProdWrite(
        fk,
        () => setPoNumber({ ...body, poNumber: null }),
        `PO # cleared on ${seg.dow}`,
        () => setProdOverrides((prev) => ({
          ...prev,
          [seg.key]: { ...prev[seg.key], poNumber: null },
        }))
      );
      return;
    }
    // Rule 6 — anything with letters, spaces or dashes is refused on commit.
    if (!/^[0-9]+$/.test(text)) {
      clearProdDraft(fk);
      shakeProdField(fk);
      showProdToast('PO # must be a numeric value — digits only, no letters or dashes.', true);
      return;
    }
    if (text === seg.poNumber) { clearProdDraft(fk); return; }
    await runProdWrite(
      fk,
      () => setPoNumber({ ...body, poNumber: text }),
      `PO #${text} recorded on ${seg.dow} · ${seg.accountCodeName}`,
      () => setProdOverrides((prev) => ({
        ...prev,
        [seg.key]: { ...prev[seg.key], poNumber: text },
      }))
    );
  };

  // ── Rule 7: the keyboard run ────────────────────────────────────────────────
  // Boxes already filled and valid are skipped; boxes in error count as needing a
  // value, so the run comes back to them.
  const prodFieldNeeds = (f: ProdField): boolean => {
    const seg = productionModel.segmentByKey[f.segKey];
    if (!seg) return false;
    if (f.kind === 'amt') return seg.requiresAmount && (seg.amount == null || seg.belowBase);
    return seg.isPO && (seg.poMissing || seg.poInvalid);
  };

  const prodFirstNeeded = (): ProdField | null =>
    productionModel.fieldOrder.find(prodFieldNeeds) ?? null;

  /** First PO # box that still needs a value — the PO banner's Start-entering target. */
  const prodFirstNeededPo = (): ProdField | null =>
    productionModel.fieldOrder.find((f) => f.kind === 'po' && prodFieldNeeds(f)) ?? null;

  const prodFocusField = (f: ProdField | null, where: 'grid' | 'card'): boolean => {
    if (!f) return false;
    const el =
      prodRefs.current[prodRefKey(where, f.segKey, f.kind)]
      ?? prodRefs.current[prodRefKey('grid', f.segKey, f.kind)]
      ?? prodRefs.current[prodRefKey('card', f.segKey, f.kind)];
    if (!el) return false;
    prodFocusAfter.current = prodRefKey(where, f.segKey, f.kind);
    el.focus();
    el.select();
    const r = el.getBoundingClientRect();
    if (r.top < 90 || r.bottom > window.innerHeight - 90) {
      window.scrollTo({ top: r.top + window.scrollY - window.innerHeight / 2, behavior: 'smooth' });
    }
    return true;
  };

  const prodAdvance = (
    segKey: string,
    kind: ProdFieldKind,
    back: boolean,
    where: 'grid' | 'card',
    announceEnd: boolean
  ) => {
    const list = productionModel.fieldOrder;
    const i = list.findIndex((f) => f.segKey === segKey && f.kind === kind);
    let target: ProdField | null = null;
    if (i < 0) {
      target = prodFirstNeeded();
    } else {
      const dir = back ? -1 : 1;
      for (let k = 1; k <= list.length; k += 1) {
        const j = ((i + dir * k) % list.length + list.length) % list.length;
        if (j === i) continue;
        if (prodFieldNeeds(list[j])) { target = list[j]; break; }
      }
    }
    // The end-of-run message belongs to a genuine entry run (the field just left
    // was needy). Updating an already-valid value on a complete week says nothing
    // here — the commit's own confirmation and the saved pulse carry it.
    if (!target) { if (announceEnd) showProdToast('Nothing left to enter on this week.'); return; }
    prodFocusField(target, where);
  };

  const onProdKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    seg: ProdSegment,
    kind: ProdFieldKind,
    where: 'grid' | 'card'
  ) => {
    if (prodLocked) return;
    const fk = prodFieldKey(seg.key, kind);
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      prodSkipBlur.current = fk;
      const raw = e.currentTarget.value;
      // Whether this box was part of the entry run, judged before the commit lands —
      // only a real run earns the "Nothing left to enter" sign-off.
      const wasNeedy = prodFieldNeeds({ segKey: seg.key, kind });
      if (kind === 'po') void commitProdPo(seg, raw); else void commitProdAmount(seg, raw);
      prodAdvance(seg.key, kind, e.shiftKey, where, wasNeedy);
      return;
    }
    if (e.key === 'Escape') {
      // Abandon the edit — the stored value comes back the moment the draft goes.
      e.preventDefault();
      prodSkipBlur.current = fk;
      clearProdDraft(fk);
      e.currentTarget.blur();
    }
  };

  const onProdBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    seg: ProdSegment,
    kind: ProdFieldKind
  ) => {
    const fk = prodFieldKey(seg.key, kind);
    if (prodSkipBlur.current === fk) { prodSkipBlur.current = null; return; }
    if (prodLocked) return;
    if (kind === 'po') void commitProdPo(seg, e.target.value); else void commitProdAmount(seg, e.target.value);
  };

  // ── 2020CA / 2020NV weekly On Call $ (TAS-475) ──────────────────────────────
  // One optional amount per employee per week, entered by whoever approves the week.
  // It never gates Approve Week. Inert unless `config.onCallPay?.enabled === true`.
  //
  // Sage job-costing: the amount exports as pay type 9 against the chosen service order's
  // job with the on-call cost code for the trade (Plumbing / HVAC). The SO + trade are
  // picked in OnCallPayModal: an empty tile takes the amount and opens it pre-filled; a
  // saved tile opens it from Change (View once locked). Amount + SO + trade go out
  // together in one PUT, and nothing is written until the modal's Save.
  const onCallCfg = config.onCallPay;
  const onCallPayOn = onCallCfg?.enabled === true;
  const onCallMax = onCallCfg?.max ?? 300;
  const onCallShown = onCallPayOn && timesheetData.role?.allowOnCallPay === true && prodWeekSubmitted;
  const onCallLocked = selectedStatus === 'payrollApproved';
  const ONCALL_FK = 'oncall|week';
  /** Preview-only cost codes per trade (the server's `costCode` wins once saved). The API's
   *  configured codes come first, then the AdminApp config, then the defaults. */
  const serverCostCodes = timesheetData.onCallPay?.costCodes;
  const ONCALL_COST_CODES: Record<OnCallTrade, string> = {
    PLUMBING: serverCostCodes?.PLUMBING ?? onCallCfg?.costCodes?.PLUMBING ?? '40500',
    HVAC: serverCostCodes?.HVAC ?? onCallCfg?.costCodes?.HVAC ?? '4150',
  };
  const ONCALL_PAY_TYPE = 9;
  /** The empty tile's typed amount, until it opens the modal (or is rejected / cancelled). */
  const [onCallDraft, setOnCallDraft] = useState<string | undefined>(undefined);
  /** Committed-value overlay: `undefined` = use the payload; otherwise the saved state, so the
   *  tile shows a save at once. A real reload (week/employee change, Refresh) supersedes it. */
  const [onCallOverride, setOnCallOverride] = useState<OnCallPayInfo | undefined>(undefined);
  /** The open modal and the amount it opened pre-filled with; null = closed. */
  const [onCallModal, setOnCallModal] = useState<{ amount: number | null } | null>(null);
  /** Enter/Escape blur the field themselves; this stops that blur committing again. */
  const onCallSkipBlur = useRef(false);
  const onCallBusy = prodBusy.has(ONCALL_FK);

  useEffect(() => {
    setOnCallDraft(undefined);
    setOnCallOverride(undefined);
  }, [timesheetData]);

  const onCallState: OnCallPayInfo = onCallOverride
    ?? timesheetData.onCallPay
    ?? { amount: null, updatedAt: null, updatedByName: null };
  const onCallSaved = savedOnCall(onCallState);

  /** The week's SOs, plus the stored SO when it has dropped off the list (so the modal can show it). */
  const onCallPayloadOptions = timesheetData.onCallServiceOrderOptions;
  const onCallSoOptions = useMemo<OnCallServiceOrderOption[]>(() => {
    const base = onCallPayloadOptions ?? [];
    const sid = onCallState.serviceOrderID;
    if (sid != null && !base.some((o) => o.serviceOrderID === sid)) {
      return [...base, {
        serviceOrderID: sid,
        altServiceOrderID: onCallState.altServiceOrderID ?? String(sid),
        name: null,
        jobAltID: onCallState.jobAltID ?? null,
        jobName: onCallState.jobName ?? null,
        derivedTrade: null,
        lastWorkDate: '',
      }];
    }
    return base;
  }, [onCallPayloadOptions, onCallState.serviceOrderID, onCallState.altServiceOrderID,
    onCallState.jobAltID, onCallState.jobName]);

  const onCallStamp = (iso: string | null): string | null => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const h = d.getHours() % 12 || 12;
    const m = d.getMinutes().toString().padStart(2, '0');
    return `${d.getMonth() + 1}/${d.getDate()} ${h}:${m}${d.getHours() >= 12 ? 'p' : 'a'}`;
  };

  /** Empty tile, Enter / blur: a valid amount opens the modal pre-filled — nothing saves here. */
  const commitOnCallDraft = (raw: string) => {
    if (onCallLocked || onCallBusy) return;
    const parsed = parseOnCallAmount(raw, onCallMax);
    if (!parsed.ok) {
      setOnCallDraft(undefined);
      shakeProdField(ONCALL_FK);
      showProdToast(parsed.error, true);
      return;
    }
    if (parsed.amount == null) { setOnCallDraft(undefined); return; }
    setOnCallModal({ amount: parsed.amount });
  };

  /** Cancel / close / saved: the tile's typed amount goes with the modal. */
  const closeOnCallModal = () => {
    setOnCallModal(null);
    setOnCallDraft(undefined);
  };

  /**
   * The modal's Save / Remove: amount + SO + trade in one PUT (`amount: null` clears the week).
   * Resolves to an error message for the modal to show inline, or null on success.
   */
  const saveOnCall = async (
    amount: number | null,
    so: number | null,
    trade: OnCallTrade | null,
  ): Promise<string | null> => {
    if (onCallLocked || !currentPayPeriod) return 'This week can no longer be changed.';
    setProdBusy((prev) => new Set(prev).add(ONCALL_FK));
    try {
      const result = await setOnCallPay({
        employeeID: timesheetData.employeeID,
        payPeriodID: currentPayPeriod.PayPeriodID,
        amount,
        ...(amount == null ? {} : { serviceOrderID: so, trade }),
      });
      if (!result.success) return result.errorMessage ?? 'Could not save the On Call amount.';
      const opt = so == null ? undefined : onCallSoOptions.find((o) => o.serviceOrderID === so);
      const now = new Date().toISOString();
      setOnCallOverride(
        result.onCallPay
          ?? (amount == null
            ? { amount: null, updatedAt: now, updatedByName: adminName ?? null }
            : {
              amount,
              updatedAt: now,
              updatedByName: adminName ?? null,
              serviceOrderID: so,
              altServiceOrderID: opt?.altServiceOrderID ?? null,
              trade,
              jobAltID: opt?.jobAltID ?? null,
              jobName: opt?.jobName ?? null,
              costCode: trade ? ONCALL_COST_CODES[trade] : null,
              payType: ONCALL_PAY_TYPE,
            })
      );
      flashProdSaved(ONCALL_FK);
      showProdToast(amount == null ? 'On Call $ removed' : `On Call ${prodMoney(amount)} saved`);
      // On Call $ feeds the weekly regular rate, so OT/DT $, rest $, avg rate and gross
      // all move — reload the week (the overlay above covers the gap until it lands).
      onRefresh();
      return null;
    } finally {
      setProdBusy((prev) => {
        const next = new Set(prev);
        next.delete(ONCALL_FK);
        return next;
      });
    }
  };

  /** The weekly On Call $ KPI tile — the amount input while empty, the saved entry after. */
  const renderOnCallTile = () => {
    const entered = onCallSaved != null;
    const stamp = onCallStamp(onCallState.updatedAt);
    const by = entered ? [onCallState.updatedByName, stamp].filter(Boolean).join(' · ') : '';
    const summary = onCallSaved ? onCallSummary(onCallSaved) : null;
    return (
      <div className={`etrc-kpi-tile etrc-oncall-tile${onCallBusy ? ' is-saving' : entered ? ' is-entered' : ' is-empty'}`}>
        <div className="etrc-kpi-label" style={{ color: '#0e7c86' }}>
          <DollarSign size={13} />On Call $
          <span
            className="etrc-oncall-status"
            aria-live="polite"
            title={onCallBusy ? 'Saving On Call $' : entered ? 'On Call $ entered' : 'No On Call $ entered'}
          >
            {onCallBusy
              ? <><Loader2 size={14} className="etrc-oncall-spin" />Saving…</>
              : entered
                ? <><CheckCircle size={14} />Entered</>
                : <><MinusCircle size={14} />None</>}
          </span>
        </div>
        {/* Every state fills the same two fixed-height slots (value row, summary row), so
            entering / saving / removing never changes the tile's height or nudges the strip. */}
        <div className="etrc-oncall-row">
          {onCallSaved ? (
            <>
              <div className={`etrc-kpi-value etrc-oncall-value${prodSaved === ONCALL_FK ? ' saved' : ''}`}>
                {prodMoney(onCallSaved.amount)}
              </div>
              <button
                type="button"
                className="etrc-btn small etrc-oncall-change"
                disabled={onCallBusy}
                onClick={() => setOnCallModal({ amount: onCallSaved.amount })}
              >
                {onCallLocked ? 'View' : 'Change'}
              </button>
            </>
          ) : onCallLocked ? (
            <div className="etrc-kpi-value etrc-oncall-value is-none">—</div>
          ) : (
            <span className={`etrc-prod-amt lg${prodShake === ONCALL_FK ? ' shake' : ''}`}>
              <span className="cur">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={onCallDraft ?? ''}
                disabled={onCallBusy || onCallModal != null}
                aria-label="On Call dollars for the week"
                title="Enter the week's On Call $, then pick the service order and trade"
                onChange={(e) => setOnCallDraft(e.target.value)}
                onFocus={(e) => e.currentTarget.select()}
                onBlur={(e) => {
                  if (onCallSkipBlur.current) { onCallSkipBlur.current = false; return; }
                  commitOnCallDraft(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onCallSkipBlur.current = true;
                    commitOnCallDraft(e.currentTarget.value);
                    e.currentTarget.blur();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    onCallSkipBlur.current = true;
                    setOnCallDraft(undefined);
                    e.currentTarget.blur();
                  }
                }}
              />
            </span>
          )}
        </div>
        <div className="etrc-oncall-summary" title={summary ?? undefined}>
          {onCallSaved && (summary ?? (
            <span
              className="etrc-oncall-badge"
              title="Saved before trades existed — pick Plumbing or HVAC before the Sage export"
            >
              <AlertTriangle size={11} />Needs coding
            </span>
          ))}
        </div>
        <div className="etrc-kpi-sub etrc-oncall-by">
          {by || (onCallLocked ? 'none entered' : 'optional')}
        </div>
      </div>
    );
  };

  // ── Field renderers (design §4) ─────────────────────────────────────────────
  const renderProdAmount = (seg: ProdSegment, size: 'sm' | 'lg', where: 'grid' | 'card') => {
    const fk = prodFieldKey(seg.key, 'amt');
    const draft = prodDrafts[fk];
    const value = draft !== undefined ? draft : (seg.amount != null ? seg.amount.toFixed(2) : '');
    const state = seg.belowBase ? 'err' : seg.amount == null ? 'empty' : 'filled';
    return (
      <span
        className={`etrc-prod-amt ${state}${size === 'lg' ? ' lg' : ''}`
          + `${prodLocked ? ' ro' : ''}${prodShake === fk ? ' shake' : ''}${prodSaved === fk ? ' saved' : ''}`}
      >
        <span className="cur">$</span>
        <input
          type="text"
          inputMode="decimal"
          ref={(el) => { prodRefs.current[prodRefKey(where, seg.key, 'amt')] = el; }}
          value={value}
          readOnly={prodLocked}
          disabled={prodBusy.has(fk)}
          placeholder="—"
          aria-label={`Total dollars — ${seg.accountCodeName}, ${prodHours(seg.hours)} hours`}
          onChange={(e) => { const v = e.target.value; setProdDrafts((p) => ({ ...p, [fk]: v })); }}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={(e) => onProdBlur(e, seg, 'amt')}
          onKeyDown={(e) => onProdKeyDown(e, seg, 'amt', where)}
        />
      </span>
    );
  };

  const renderProdPo = (seg: ProdSegment, size: 'sm' | 'lg', where: 'grid' | 'card') => {
    const fk = prodFieldKey(seg.key, 'po');
    const draft = prodDrafts[fk];
    const value = draft !== undefined ? draft : (seg.poNumber ?? '');
    const state = seg.poInvalid ? 'err' : seg.poMissing ? 'need' : 'ok';
    return (
      <>
        <span
          className={`etrc-prod-po ${state}${size === 'lg' ? ' lg' : ''}`
            + `${prodLocked ? ' ro' : ''}${prodShake === fk ? ' shake' : ''}${prodSaved === fk ? ' saved' : ''}`}
        >
          <span className="lb">PO #</span>
          <input
            type="text"
            inputMode="numeric"
            maxLength={10}
            ref={(el) => { prodRefs.current[prodRefKey(where, seg.key, 'po')] = el; }}
            value={value}
            readOnly={prodLocked}
            disabled={prodBusy.has(fk)}
            placeholder="required"
            aria-label="Purchase order number"
            onChange={(e) => { const v = e.target.value; setProdDrafts((p) => ({ ...p, [fk]: v })); }}
            onFocus={(e) => e.currentTarget.select()}
            onBlur={(e) => onProdBlur(e, seg, 'po')}
            onKeyDown={(e) => onProdKeyDown(e, seg, 'po', where)}
          />
        </span>
        {seg.poInvalid && (
          <span className={`etrc-prod-flag bad${size === 'lg' ? ' md' : ''}`}>
            <AlertTriangle size={size === 'lg' ? 14 : 12} />
            <span>PO # must be numeric — digits only</span>
          </span>
        )}
        {!seg.poInvalid && seg.poMissing && (
          <span className={`etrc-prod-flag need${size === 'lg' ? ' md' : ''}`}>
            <AlertTriangle size={size === 'lg' ? 14 : 12} />
            <span>PO # required before approval</span>
          </span>
        )}
      </>
    );
  };

  const renderProdFlag = (seg: ProdSegment, size: 'sm' | 'md') => {
    const err = prodSegError(seg, prodRate);
    if (err) {
      return (
        <span className={`etrc-prod-flag bad${size === 'md' ? ' md' : ''}`}>
          <AlertTriangle size={size === 'md' ? 14 : 12} /><span>{err}</span>
        </span>
      );
    }
    if (seg.needsEntry) {
      return (
        <span className={`etrc-prod-flag need${size === 'md' ? ' md' : ''}`}>
          <AlertTriangle size={size === 'md' ? 14 : 12} /><span>needs a total</span>
        </span>
      );
    }
    return null;
  };

  /** Green check line under a filled amount. Omitted when the API cannot say who. */
  const renderProdProvenance = (seg: ProdSegment) => {
    if (seg.amount == null || seg.belowBase || !seg.enteredBy) return null;
    return (
      <span className="etrc-prod-flag ok prov">
        <CheckCircle size={12} />
        <span>{seg.enteredBy}{seg.enteredAt ? ` · ${seg.enteredAt}` : ''}</span>
      </span>
    );
  };

  // ── Split / re-code modal (design §6) ───────────────────────────────────────
  const openProdSplit = (date: string, projectID: number) => {
    if (!currentPayPeriod || prodLocked) return;
    const pd = productionModel.byDate[date];
    if (!pd) return;
    const rows = pd.segments.filter((s) => s.projectID === projectID);
    if (rows.length === 0) return;
    setProdSplit({
      employeeID: timesheetData.employeeID,
      payPeriodID: currentPayPeriod.PayPeriodID,
      entryDate: date,
      dow: pd.dow,
      dateLabel: pd.dateLabel,
      projectID,
      altProjectID: rows[0].altProjectID,
      projectName: rows[0].projectName,
      phaseName: rows[0].phaseName,
      punchedHours: prodRound(rows.reduce((s, r) => s + r.hours, 0)),
      poAccountCodeID: prodPoAccountCodeID ?? -1,
      rows: rows.map((r) => ({
        accountCodeID: r.accountCodeID,
        accountCodeName: r.accountCodeName,
        altAccountCodeID: r.altAccountCodeID,
        hours: r.hours,
        amount: r.amount,
        poNumber: r.poNumber,
      })),
    });
  };

  const renderProdEditButton = (date: string, projectID: number, big = false) => {
    if (prodLocked) return null;
    return (
      <button
        type="button"
        className={`etrc-prod-edit${big ? ' lg' : ''}`}
        title="Edit account code, hours or split"
        aria-label="Edit account codes and hours for this day"
        onClick={(e) => { e.stopPropagation(); openProdSplit(date, projectID); }}
      >
        <Pencil size={big ? 15 : 14} />
      </button>
    );
  };

  /** One week-grid cell: an entry block per account code worked that day. */
  const renderProdCell = (col: DayCol): React.ReactNode => {
    const pd = productionModel.byDate[col.date];
    if (!pd || pd.visible.length === 0) {
      // Inert day (weekend, or nothing this employee enters against): the dollars
      // already on the day, or an em dash. Role-OFF employees never see dollars.
      return pd?.hasEntries && prodRoleRequires
        ? <span className="etrc-matrix-val" style={{ color: '#0e7c86' }}>{prodMoney(pd.entryTotal)}</span>
        : <span className="etrc-matrix-empty">—</span>;
    }
    return (
      <div className="etrc-prod-cell">
        {pd.visible.map((seg) => (
          <div className="etrc-prod-seg" key={seg.key}>
            <span className="etrc-prod-sl">
              <span className="etrc-prod-code">
                <b>{seg.accountCodeName}</b>{seg.altAccountCodeID ? ` · ${seg.altAccountCodeID}` : ''}
                {seg.isPO && <span className="etrc-prod-pobadge">PO</span>}
              </span>
              <span className="etrc-prod-hrs"><span className="h">{prodHours(seg.hours)} h</span></span>
              <span className="etrc-prod-job">
                {seg.altProjectID && <span className="no">#{seg.altProjectID}</span>} {seg.projectName}
              </span>
            </span>
            <span className="etrc-prod-segin">
              {prodRoleRequires && renderProdAmount(seg, 'sm', 'grid')}
              {renderProdEditButton(seg.date, seg.projectID)}
            </span>
            {seg.isPO && renderProdPo(seg, 'sm', 'grid')}
            {prodRoleRequires && renderProdFlag(seg, 'sm')}
          </div>
        ))}
        {prodRoleRequires && (
          <div className="etrc-prod-cellfoot">
            {pd.visible.length > 1
              ? <span className="etrc-prod-splitbadge">{pd.visible.length} codes</span>
              : <span>Day</span>}
            <span><b>{prodMoney(pd.entryTotal)}</b></span>
          </div>
        )}
      </div>
    );
  };

  /** Everything a day card needs; built once per day and passed straight down. */
  const dayProductionProps = (col: DayCol): DayProductionProps | undefined => {
    if (!productionPayOn) return undefined;
    const pd = productionModel.byDate[col.date];
    if (!pd) return undefined;
    return {
      day: pd,
      roleRequires: prodRoleRequires,
      rate: prodRate,
      chip: prodDayChip(pd, prodRate, prodRoleRequires),
      renderAmount: (seg, size) => renderProdAmount(seg, size, 'card'),
      renderPo: (seg, size) => renderProdPo(seg, size, 'card'),
      renderFlag: (seg, size) => renderProdFlag(seg, size),
      renderProvenance: renderProdProvenance,
      onSplit: prodLocked ? undefined : (projectID: number) => openProdSplit(col.date, projectID),
    };
  };

  // Auto-expand certifications only when an answer contradicts its question's
  // expected answer; indifferent (no expectation) rows never expand or warn.
  const timesheetAnswers = timesheetData.timesheetAnswers ?? [];
  const certFlagged = timesheetAnswers.some((a) => certVerdict(a) === 'flag');
  const certHasOk = timesheetAnswers.some((a) => certVerdict(a) === 'ok');
  useEffect(() => {
    if (timesheetAnswers.some((a) => certVerdict(a) === 'flag')) {
      setCertOpen(true);
    } else {
      setCertOpen(false);
    }
  }, [timesheetData.timesheetID]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drive-time punch rows only surface while the week is still unsubmitted.
  const isSubmitted = selectedStatus !== 'unsubmitted' && selectedStatus !== null;

  // Payroll Timesheet Modifications (M1-B2). Both indicators render only when the
  // tenant runs the feature — absence must degrade to exactly today's UI.
  // `active` arrives only on the timesheetEdit/state unsubmitted read (false =
  // terminated employee with pending time); `signatureType`/`submittedByAdminName`
  // arrive on the submitted read (PAYROLL = payroll submitted on the employee's
  // behalf — on-screen attribution is allowed; only the PDF says "Unavailable to sign").
  const payrollEditingOn = config.payrollTimesheetEditing?.enabled === true;
  const showInactiveBadge = payrollEditingOn && timesheetData.active === false;
  // Submitted stamp (design View 1). Absorbs the former "Submitted by Payroll ·
  // {admin}" text line — provenance now renders as the marker + name + timestamp.
  const submittedStamp =
    isSubmitted && timesheetData.submittedDate
      ? fmtStamp(timesheetData.submittedDate, timesheetData.submittedDateOffset)
      : null;
  const submittedByPayroll = timesheetData.signatureType === 'PAYROLL';

  // ── Payroll Timesheet Modifications (M1-B3, amended 2026-08-09) ─────────────
  // Edit as payroll lives in the header's small-button row (next to Refresh);
  // Submit for employee stays in the sticky action bar. Rendered only when
  // the board passed the callbacks (feature flag + the caller's permission).
  const showPayrollActions =
    payrollEditingOn && !isSubmitted && selectedStatus === 'unsubmitted' && !noTimesheetForWeek
    && (!!onEditAsPayroll || !!onSubmitForEmployee);
  const payrollSubmitDisabled = !payrollEntryCounts || payrollEntryCounts.total === 0;

  // Change history (post-M1 UX gap fix): unlike the payroll actions above, this is
  // NOT restricted to unsubmitted weeks — after submission "Edit as payroll" (the
  // only prior door to the history panel) stops rendering, which left the audit
  // trail unreachable exactly when reviewers need it. Read-gated by the caller's
  // viewTimesheets-or-admin verdict; flag off / legacy callers render byte-identical.
  const showChangeHistory =
    payrollEditingOn && canViewHistoryPerm && !noTimesheetForWeek && !!currentPayPeriod;

  // Close the panel when the drill-down retargets (week nav / different employee).
  useEffect(() => {
    setOpenPanel(null);
  }, [timesheetData.employeeID, currentPayPeriod?.PayPeriodID]);

  // TAS-365: the week's per-punch history, read alongside the week data — same gate
  // as Change history (the API guards it with the changeLog guard). Refetched whenever
  // the board reloads the week (timesheetData identity), since edits add events.
  // A failure (e.g. 403, or a tenant without the log columns) just hides the affordance.
  const punchHistoryOn = showChangeHistory;
  const punchHistoryPayPeriodID = currentPayPeriod?.PayPeriodID;
  useEffect(() => {
    setPunchHistoryItems(null);
    setPunchHistoryModal(null);
    if (!punchHistoryOn || punchHistoryPayPeriodID == null) return undefined;
    let alive = true;
    TimesheetEditManager.getPunchHistory(timesheetData.employeeID, punchHistoryPayPeriodID)
      .then((res) => { if (alive) setPunchHistoryItems(res.punches ?? []); })
      .catch(() => { if (alive) setPunchHistoryItems(null); });
    return () => { alive = false; };
  }, [punchHistoryOn, timesheetData, punchHistoryPayPeriodID]);
  const punchHistoryMap = useMemo(
    () => (punchHistoryItems ? indexPunchHistory(punchHistoryItems) : undefined),
    [punchHistoryItems]
  );

  // ── Audit log (design_handoff_timesheet_audit_approvals) ────────────────────
  // Tenant flag only — no role permission, v1 parity. Fetched on first open per
  // view; timesheetData identity changes on every refresh (approve / unlock /
  // week nav all refetch), which also invalidates the cached rows since those
  // actions write audit rows.
  const auditLogOn = config.showTimesheetAuditLog === true;
  useEffect(() => {
    setAuditRows(null);
    setAuditError(false);
  }, [timesheetData]);

  const loadAudit = async () => {
    if (!currentPayPeriod) return;
    setAuditLoading(true);
    setAuditError(false);
    try {
      const rows = await AuditManager.getEmployeeAudit(
        String(timesheetData.employeeID),
        currentPayPeriod.PayPeriodID
      );
      setAuditRows(rows ?? []);
    } catch {
      setAuditError(true);
    } finally {
      setAuditLoading(false);
    }
  };

  const openAuditPanel = () => {
    setOpenPanel('audit');
    if (auditRows == null && !auditLoading) void loadAudit();
  };

  // ── Approvals card view-model ───────────────────────────────────────────────
  // The chain (supervisor → payroll) is config-shaped: two-hop tenants always
  // show both hops, single-hop tenants only the hops their data wrote. The
  // remaining sections are purely data-driven and absent when empty. Required-
  // super rows without a requiredEmployee are the WCDP/2020PH bare shape — those
  // duplicate supervisorApprovals, so only genuinely *required* rows render.
  //
  // STAGE SCOPING (design §3, approved global change R2 — all tenants): a section
  // renders only from the stage its slots can be acted on. The payroll hop no
  // longer shows on a Submitted week, and the required-super section no longer
  // shows on a 1st-Approval week, so nothing on the card is unreachable noise.
  const twoHop = config.useSingleHopPayrollApproval !== true;
  const atManagerStage = selectedStatus === 'managerPending';
  const atSubmittedStage = selectedStatus === 'submitted';
  const atPayrollStage = selectedStatus === 'supervisorApproved' || selectedStatus === 'payrollApproved';
  const supApprovalVMs = (timesheetData.supervisorApprovals ?? [])
    .map(normalizeApproval).filter((v): v is ApprovalVM => v != null);
  const payApprovalVMs = (timesheetData.payrollApprovals ?? [])
    .map(normalizeApproval).filter((v): v is ApprovalVM => v != null);
  const chainHops: Array<{ key: string; tile: 'sup' | 'payroll'; role: string; vm: ApprovalVM | null }> = [];
  if (supApprovalVMs.length > 0) {
    supApprovalVMs.forEach((vm, i) => chainHops.push({ key: `sup-${i}`, tile: 'sup', role: 'Supervisor approval', vm }));
  } else if (twoHop) {
    chainHops.push({ key: 'sup-pending', tile: 'sup', role: 'Supervisor approval', vm: null });
  }
  if (payApprovalVMs.length > 0) {
    payApprovalVMs.forEach((vm, i) => chainHops.push({ key: `pay-${i}`, tile: 'payroll', role: 'Payroll approval', vm }));
  } else if (atPayrollStage) {
    // R2(a): the always-pushed pending payroll hop is gone — it only appears once
    // the timesheet is actually queued for payroll.
    chainHops.push({ key: 'pay-pending', tile: 'payroll', role: 'Payroll approval', vm: null });
  }
  const chainDone = chainHops.filter((h) => h.vm != null).length;
  // The chain describes the supervisor→payroll leg; on a 1st-Approval week neither
  // hop has happened or can happen, so the section itself is out of stage.
  const showChainSection = !atManagerStage && chainHops.length > 0;

  // ── Actionable approval slots (design §3) ───────────────────────────────────
  // A slot is actionable only inside its own stage: manager + approval-group slots
  // while the timesheet is at managerPending, required-super slots at submitted.
  // Everywhere else the rows are read-only — which is exactly today's card, so a
  // tenant without allowManagerApproval never sees a change.
  const slotActionsOn = config.allowManagerApproval === true;
  const canActOnSlots = slotActionsOn && canApproveStagePerm && !reviewLoading && !noTimesheetForWeek;

  type SlotKind = ApprovalSlotRequest['kind'];
  interface SlotVM {
    key: string;
    kind: SlotKind;
    /** requiredManagerApprovalsID / requiredApprovalGroupsID / requiredSuperApprovalsID */
    approvalID: number | null;
    /** Person's name, or the approval group's name. */
    ownerName: string;
    /** True when the signed-in admin IS the required approver (or a member of the
     *  required approval group) — those rows pre-check into "Approve N selected". */
    mine: boolean;
    /** null = still outstanding. */
    vm: ApprovalVM | null;
    /** Completed rows where the recorded approver is not the slot's owner. This is
     *  a CLIENT derivation (errata item 5) — the API stores who clicked and whose
     *  slot it was, never an on-behalf marker. */
    onBehalf: boolean;
    /** TAS-447 — the slot's owner cannot sign in, so they can never clear it
     *  themselves. The timesheet cannot advance until somebody with approve rights
     *  does it on their behalf. `'no-account'` = no AdminEmployee record at all;
     *  `'inactive'` = the record exists but has been deactivated. Both are live in
     *  NPM. null = fine, or an API build too old to say. Groups are never flagged:
     *  any member satisfies them. */
    noLogin: 'no-account' | 'inactive' | null;
  }

  /** Which of the two un-approvable shapes a required approver is in, if either. */
  const noLoginReason = (
    required: { hasApproverLogin?: boolean; adminEmployeeID?: number | null } | null | undefined
  ): 'no-account' | 'inactive' | null => {
    // Explicit `!== false`: an older API omits the field, and a missing value must
    // read as "no warning", not as a warning on every row.
    if (required?.hasApproverLogin !== false) return null;
    return required.adminEmployeeID == null ? 'no-account' : 'inactive';
  };

  const meID = adminEmployeeID ?? null;
  const adminIDOf = (e: unknown): number | null =>
    (e as { adminEmployeeID?: number } | null | undefined)?.adminEmployeeID ?? null;

  const managerSlots: SlotVM[] = (timesheetData.managerApprovals ?? []).map((m, i) => {
    const ownerID = adminIDOf(m.requiredEmployee);
    const approverID = adminIDOf(m.approvalEmployee);
    return {
      key: `mgr-${i}`,
      kind: 'manager' as SlotKind,
      approvalID: m.requiredEmployee?.requiredManagersApprovalsID ?? null,
      ownerName: requiredApproverName(m) ?? 'Manager',
      mine: meID != null && ownerID != null && ownerID === meID,
      vm: normalizeApproval(m),
      onBehalf: approverID != null && ownerID != null && approverID !== ownerID,
      noLogin: noLoginReason(m.requiredEmployee),
    };
  });

  // Approval-group membership IS client-visible: the timesheet read sends every
  // group's `adminEmployees[]` with their adminEmployeeID (v1 selects its default
  // checkboxes off the same array). So a group slot can be "yours", and an
  // approval by a non-member reads as on-behalf-of-the-group.
  const groupSlots: SlotVM[] = (timesheetData.approvalGroups ?? []).map((g, i) => {
    const memberIDs = (g.adminEmployees ?? [])
      .map((ae) => adminIDOf(ae))
      .filter((id): id is number => id != null);
    const approverID = adminIDOf(g.approvalEmployee);
    return {
      key: `grp-${i}`,
      kind: 'group' as SlotKind,
      approvalID: g.requiredApprovalGroupsID ?? g.approvalGroupID ?? null,
      ownerName: g.name ?? 'Approval group',
      mine: meID != null && memberIDs.includes(meID),
      vm: normalizeApproval(g),
      onBehalf: approverID != null && memberIDs.length > 0 && !memberIDs.includes(approverID),
      // A group is satisfied by ANY member, so "the owner has no login" cannot apply.
      noLogin: null,
    };
  });

  const requiredSuperSlots: SlotVM[] = (timesheetData.superApprovals ?? [])
    .filter((s) => s.requiredEmployee != null)
    .map((s, i) => {
      const ownerID = adminIDOf(s.requiredEmployee);
      const approverID = adminIDOf(s.approvalEmployee);
      return {
        key: `rsup-${i}`,
        kind: 'requiredSuper' as SlotKind,
        approvalID: s.requiredEmployee?.requiredSuperApprovalsID ?? null,
        ownerName: requiredApproverName(s) ?? 'Required supervisor',
        mine: meID != null && ownerID != null && ownerID === meID,
        vm: normalizeApproval(s),
        onBehalf: approverID != null && ownerID != null && approverID !== ownerID,
        noLogin: noLoginReason(s.requiredEmployee),
      };
    });

  // TAS-679 (WCDP Modified Duty): supervisor + HR, both required. Non-null ONLY when the
  // API sent `requiredSupers` with 2+ slots (WCDP, gate on) — every other tenant and
  // timesheet gets null and the card below is exactly what it was. When present, its own
  // section replaces the read-only "Required super approvals" one (same slots).
  const mdSlots = multiSlotSupers(timesheetData.requiredSupers);
  const mdStamp = (slot: RequiredSuperSlot): React.ReactNode => {
    if (!slot.approvalDateTime) return null;
    // The flattened slot has no offset; take it from the matching superApprovals row.
    const raw = (timesheetData.superApprovals ?? []).find(
      (sa) => sa.requiredEmployee?.requiredSuperApprovalsID === slot.requiredSuperApprovalsID
    );
    const st = fmtStamp(slot.approvalDateTime, raw?.approvalEmployee?.approvalDateTimeOffset ?? null);
    return <>{st.text}{st.utc && <span className="etrc-utc-tag">UTC</span>}</>;
  };

  type SlotSection = { key: string; title: string; tag?: string; rows: SlotVM[]; actionable: boolean };
  const slotSections: SlotSection[] = [
    // §3: the first section is plain "Approvals" once the multi-tier flow is on;
    // tenants without it keep the shipped "Manager approvals" heading.
    {
      key: 'manager',
      title: slotActionsOn ? 'Approvals' : 'Manager approvals',
      rows: managerSlots,
      actionable: canActOnSlots && atManagerStage,
    },
    {
      key: 'group',
      title: 'Approval groups',
      tag: slotActionsOn ? 'Approval group' : undefined,
      rows: groupSlots,
      actionable: canActOnSlots && atManagerStage,
    },
    {
      key: 'requiredSuper',
      title: 'Required super approvals',
      // R2(b): required supers belong to the 2nd-approval leg onward.
      rows: atManagerStage || mdSlots ? [] : requiredSuperSlots,
      actionable: canActOnSlots && atSubmittedStage,
    },
  ].filter((s) => s.rows.length > 0);

  /** Outstanding slots at the CURRENT stage only — what the footer counts and what
   *  the "N still outstanding at this stage" toast reports (design §3.2). */
  const currentStageSlots: SlotVM[] = atManagerStage
    ? [...managerSlots, ...groupSlots]
    : atSubmittedStage
      ? requiredSuperSlots
      : [];
  const outstandingStageSlots = currentStageSlots.filter((s) => s.vm == null);
  const myOutstandingSlots = outstandingStageSlots.filter((s) => s.mine);

  // Closed decision 1 (ITERATION-2, 2026-08-09): the unsubmitted drill-down's
  // Regular/OT/DT numbers come from GET /preview — the server's real calculator —
  // never from client arithmetic. Dashes while the preview is loading. If the
  // preview errored (e.g. the viewer holds neither write permission), the cells
  // fall back to the legacy client display rather than dashing forever.
  const previewMode =
    payrollEditingOn && selectedStatus === 'unsubmitted'
    && (payrollPreviewLoading === true || payrollPreview != null);
  const previewWeek = payrollPreview?.week ?? null;
  const previewByDate = useMemo(() => {
    const m: Record<string, TimesheetPreviewDay> = {};
    (payrollPreview?.days ?? []).forEach((d) => { m[d.date.slice(0, 10)] = d; });
    return m;
  }, [payrollPreview]);
  const previewDayMin = (
    date: string,
    key: 'regularMinutes' | 'overtimeMinutes' | 'doubleTimeMinutes'
  ): number => previewByDate[date.slice(0, 10)]?.[key] ?? 0;

  // Stage-based availability, further gated by the caller's Approval Access permission (WP8).
  // `managerPending` deliberately grants Unlock but NOT Approve Week (design §3.3,
  // locked decision): at the 1st-Approval stage the only way forward is per-slot
  // approval in the Approvals card, so a blanket Approve Week would be a lie.
  const canApprove = !reviewLoading && canApproveStagePerm && (selectedStatus === 'submitted' || selectedStatus === 'supervisorApproved');
  const canUnlock = !reviewLoading && canUnlockStagePerm && (selectedStatus === 'managerPending' || selectedStatus === 'submitted' || selectedStatus === 'supervisorApproved' || selectedStatus === 'payrollApproved');
  // Misc task cost-code edits are allowed on unsubmitted/submitted/supervisor-approved
  // weeks only — once payroll has approved, the codes are locked (matches V1).
  const canEditMiscTaskCode = canEditMiscTaskCodePerm && selectedStatus !== 'payrollApproved';
  // Match the Print permission on the legacy TimesheetPage: shown for any submitted/approved timesheet (not unsubmitted).
  const canPrint = !reviewLoading && selectedStatus !== 'unsubmitted' && selectedStatus !== null;

  const handlePrint = async () => {
    if (!currentPayPeriod) return;
    setPrintLoading(true);
    try {
      const ok = await printTimesheet(timesheetData, currentPayPeriod.PayPeriodID, currentPayPeriod);
      setPrintResult(ok ? 'success' : 'failure');
    } catch {
      setPrintResult('failure');
    } finally {
      setPrintLoading(false);
    }
  };

  // ── Slot approval actions (design §3 / §3.2) ────────────────────────────────
  // "Approve N selected" is a CLIENT LOOP (errata item 6): one POST per slot, each
  // able to refuse with a HTTP-200 { success:false }. Failures are named, never
  // swallowed. The advance is optimistic only when EVERY outstanding slot at this
  // stage landed — the server flips the status on exactly the same condition.
  const [slotBusy, setSlotBusy] = useState(false);
  const [selectedSlotKeys, setSelectedSlotKeys] = useState<Set<string>>(new Set());
  const [onBehalfTarget, setOnBehalfTarget] = useState<SlotVM | null>(null);
  const [slotFailures, setSlotFailures] = useState<{ name: string; reason: string }[] | null>(null);

  // Own slots start checked; re-seeded whenever the drill-down retargets or the
  // payload refreshes (an approval elsewhere can remove a slot from under us).
  const myOutstandingKeys = myOutstandingSlots.map((s) => s.key).join('|');
  useEffect(() => {
    setSelectedSlotKeys(new Set(myOutstandingKeys ? myOutstandingKeys.split('|') : []));
  }, [myOutstandingKeys, selectedStatus]);

  const toggleSlotSelection = (key: string) => {
    setSelectedSlotKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const runSlotApprovals = async (slots: SlotVM[], onBehalf: boolean) => {
    if (slots.length === 0 || slotBusy) return;
    if (!adminEmployeeID) {
      setSlotFailures([{ name: 'Approval', reason: 'Your admin account could not be identified — sign out and back in.' }]);
      return;
    }
    setSlotBusy(true);
    try {
      const outcomes = await approveSlotsSequentially(
        timesheetData.timesheetID,
        adminEmployeeID,
        slots.map((s) => ({ key: s.key, kind: s.kind, approvalID: s.approvalID, ownerName: s.ownerName }))
      );
      const { approved, failed } = summarizeSlotOutcomes(outcomes);
      if (failed.length > 0) {
        setSlotFailures(failed.map((f) => ({ name: f.slot.ownerName, reason: f.reason ?? 'Approval failed.' })));
      }
      if (approved.length === 0) return;

      const approvedKeys = new Set(approved.map((a) => a.slot.key));
      const stillOutstanding = outstandingStageSlots.filter((s) => !approvedKeys.has(s.key)).length;

      if (stillOutstanding === 0 && selectedStatus && onStageAdvanced) {
        // Last slot at this stage — the server has already advanced the timesheet.
        const nextLabel = bucketLabel(selectedStatus === 'managerPending' ? 'submitted' : 'supervisorApproved');
        onToast?.(
          selectedStatus === 'managerPending'
            ? `All 1st-level approvals complete — ${fullName} moved to ${nextLabel}.`
            : `Required super approved — ${fullName} moved to ${nextLabel}.`
        );
        onStageAdvanced(selectedStatus);
        return;
      }

      const who = approved.length === 1
        ? `${approved[0].slot.ownerName}${onBehalf ? ' (on behalf)' : ''}`
        : `${approved.length} slots`;
      onToast?.(
        `Approved ${who} · ${stillOutstanding} still outstanding at this stage.`
      );
      onRefresh();
    } finally {
      setSlotBusy(false);
    }
  };

  const selectedMineSlots = myOutstandingSlots.filter((s) => selectedSlotKeys.has(s.key));
  const approveSelectedSlots = () => runSlotApprovals(selectedMineSlots, false);

  const confirmOnBehalf = () => {
    const target = onBehalfTarget;
    setOnBehalfTarget(null);
    if (target) void runSlotApprovals([target], true);
  };

  // Status display — the shared stage vocabulary (translator keys, npmech overrides).
  const statusLabel = selectedStatus ? bucketLabel(selectedStatus) : '—';

  // Employee info
  const { name, altEmployeeID, jobTitle, phoneNumber, hourlyRate } = timesheetData;
  const initials = `${name.first?.[0] ?? ''}${name.last?.[0] ?? ''}`.toUpperCase();
  const fullName = `${name.first} ${name.last}`;

  // Stored hourly rate in the header meta line — v1's `Rate: $X/hr`, restored behind the
  // tenant gate (WCDP only) and, as in v1, for every role. A missing/zero rate renders
  // nothing rather than v1's "$(N/A)/hr": on a piece tenant no rate is the normal case,
  // and an empty meta slot reads better than an error-looking one. fmtMwMoney is the
  // file's generic 2-decimal money formatter despite the min-wage-era name.
  // Wallcraft production totals: base pay is what an entry is measured against, so the
  // rate belongs on the meta line for this tenant too (design §1).
  const showEmployeeRate =
    (config.showEmployeeHourlyRate === true || productionPayOn)
    && typeof hourlyRate === 'number' && hourlyRate > 0;

  // Handlers
  const toggleDay = (date: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      next.has(date) ? next.delete(date) : next.add(date);
      return next;
    });
  };

  // Days whose card renders AND has something inside it — the set Expand all works on.
  const expandableDates = useMemo(
    () => dayColumns
      .filter((c) => dayCardIsRendered(c) && dayBodySections(c, isSubmitted).hasDetail)
      .map((c) => c.date),
    [dayColumns, isSubmitted]
  );
  const allExpanded = expandableDates.length > 0 && expandableDates.every((d) => expandedDays.has(d));
  // Ignore leftover dates from a previously viewed week — only cards on screen count.
  const noneExpanded = !expandableDates.some((d) => expandedDays.has(d));
  const expandAllDays = () => setExpandedDays(new Set(expandableDates));
  const collapseAllDays = () => setExpandedDays(new Set());

  const jumpToDay = (date: string) => {
    setActiveCol(date);
    if (expandableDates.includes(date)) setExpandedDays((prev) => new Set([...prev, date]));
    setTimeout(() => {
      const card = dayCardRefs.current[date];
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'start' });
        card.classList.add('etrc-flash');
        setTimeout(() => card.classList.remove('etrc-flash'), 1200);
      }
    }, 50);
  };

  const handleApproveClick = () => {
    if (hasPendingTimeOff) {
      setTimeOffBlockOpen(true);
      return;
    }
    // Wallcraft production totals: the same early-return shape as the time-off gate.
    // There is no reviewer override — the server refuses the approve as well.
    if (prodBlocked) {
      setProdBlockOpen(true);
      return;
    }
    // Design §3.3: at the 2nd-Approval stage, Approve Week satisfies the outstanding
    // required-super slot(s) — v1's sequence (TimesheetPage `onApprove`), which sends
    // the requiredSuperApprovalsID rather than a bare supervisor approve. Tenants with
    // no required supers fall through to exactly the shipped behaviour.
    if (atSubmittedStage && canActOnSlots && outstandingStageSlots.length > 0) {
      void runSlotApprovals(outstandingStageSlots, false);
      return;
    }
    onApproveWeek();
  };

  const totalRegularMin = dayColumns.reduce((s, c) => s + Math.max(0, c.totalWorkMin - c.totalOTMin - c.totalDTMin), 0);

  // ── Weekly pieces (`combineTasksInWeek`, design §4) ─────────────────────────
  // On these tenants a piece is claimed FOR THE WEEK, so no per-day dollar figure
  // is meaningful: the matrix row dashes every day, the day cards drop their piece
  // lists, and the whole rollup lives in one Weekly Tasks Summary section.
  const weeklyPiecesOn = config.combineTasksInWeek === true;
  const weeklyProjects: WeeklyProjectTasks[] = useMemo(
    () => (weeklyPiecesOn ? aggregateWeeklyProjectTasks(days) : []),
    [weeklyPiecesOn, days]
  );
  const weeklyRef = useRef<HTMLDivElement | null>(null);
  const jumpToWeekly = () => {
    const el = weeklyRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.classList.add('etrc-flash');
    setTimeout(() => el.classList.remove('etrc-flash'), 1200);
  };

  // ── Piece / Warehouse vs Hourly split (`showTaskAndHourlyMinutes`, design §5) ─
  // Straight off the payload's per-day minute counts (the v1 grid reads the same
  // two fields); the tenant's hours format and the console's zero-is-a-dash rule
  // both come free from fmtHrsOrDash.
  const splitRowsOn = config.showTaskAndHourlyMinutes === true;
  // 2020PH clocks piece work into TimeEntryHourly, so the split has to be re-derived from
  // the account code rather than read off the API day totals. Opt-in per tenant: everyone
  // else keeps the day totals verbatim, byte-for-byte what shipped.
  const use2020Split = config.pieceHourlySplitByAccountCode === true;
  const splitByDate = useMemo(() => {
    const m = new Map<string, { taskMin: number; hourlyMin: number }>();
    for (const c of dayColumns) m.set(c.date, dayPieceHourlyMinutes(c.day, use2020Split));
    return m;
  }, [dayColumns, use2020Split]);
  const splitFor = (col: DayCol) => splitByDate.get(col.date) ?? { taskMin: 0, hourlyMin: 0 };
  const totalTaskMin = dayColumns.reduce((s, c) => s + splitFor(c).taskMin, 0);
  const totalHourlyMin = dayColumns.reduce((s, c) => s + splitFor(c).hourlyMin, 0);
  const pieceWarehouseLabel = (() => {
    const t = translator.translate('PieceWarehouse');
    return typeof t === 'string' && t && t !== 'N/A' ? t : 'Piece / Warehouse';
  })();

  const splitMatrixRows: MatrixRow[] = splitRowsOn ? [
    {
      key: 'taskmin',
      label: pieceWarehouseLabel,
      subLabel: '',
      icon: <Package size={15} color="#2f6fed" />,
      accent: null,
      splitRow: true,
      getValue: (col: DayCol) => {
        const m = splitFor(col).taskMin;
        return m > 0 ? fmtHrsOrDash(m) : null;
      },
      getWeekTotal: () => (totalTaskMin > 0 ? fmtHrsOrDash(totalTaskMin) : '—'),
    },
    {
      key: 'hourlymin',
      label: 'Hourly (Job)',
      subLabel: '',
      icon: <Clock size={15} color="#1f9d6b" />,
      accent: null,
      splitRow: true,
      getValue: (col: DayCol) => {
        const m = splitFor(col).hourlyMin;
        return m > 0 ? fmtHrsOrDash(m) : null;
      },
      getWeekTotal: () => (totalHourlyMin > 0 ? fmtHrsOrDash(totalHourlyMin) : '—'),
    },
  ] : [];

  // Matrix rows data — 7 rows (+2 when the split is on)
  const matrixRows: MatrixRow[] = [
    {
      key: 'hours',
      label: 'Total Hours',
      subLabel: '',
      icon: <Clock size={15} color="#2f6fed" />,
      accent: null,
      getValue: (col: DayCol) => col.totalWorkMin > 0 ? fmtHrsOrDash(col.totalWorkMin) : null,
      getWeekTotal: () => fmtHrsOrDash(totalWorkMin),
    },
    {
      key: 'reg',
      label: 'Regular',
      subLabel: '',
      icon: <Clock size={15} color="#8a93a2" />,
      accent: '#8a93a2',
      // previewMode: the server's split (one overtime policy on screen). While the
      // preview is in flight there is no split to show — dash, never client math.
      getValue: (col: DayCol) => {
        if (previewMode) {
          const v = payrollPreview ? previewDayMin(col.date, 'regularMinutes') : 0;
          return v > 0 ? fmtHrsOrDash(v) : null;
        }
        const reg = Math.max(0, col.totalWorkMin - col.totalOTMin - col.totalDTMin);
        return reg > 0 ? fmtHrsOrDash(reg) : null;
      },
      getWeekTotal: () => previewMode
        ? (previewWeek && previewWeek.regularMinutes > 0 ? fmtHrsOrDash(previewWeek.regularMinutes) : '—')
        : fmtHrsOrDash(totalRegularMin),
    },
    {
      key: 'ot',
      label: 'Overtime',
      subLabel: '',
      icon: <OTBarIcon size={15} color="#d4881a" />,
      accent: '#d4881a',
      getValue: (col: DayCol) => {
        if (previewMode) {
          const v = payrollPreview ? previewDayMin(col.date, 'overtimeMinutes') : 0;
          return v > 0 ? fmtHrsOrDash(v) : null;
        }
        return col.totalOTMin > 0 ? fmtHrsOrDash(col.totalOTMin) : null;
      },
      getWeekTotal: () => previewMode
        ? (previewWeek && previewWeek.overtimeMinutes > 0 ? fmtHrsOrDash(previewWeek.overtimeMinutes) : '—')
        : (totalOTMin > 0 ? fmtHrsOrDash(totalOTMin) : '—'),
    },
    {
      key: 'dt',
      label: 'Double Time',
      subLabel: '',
      icon: <OTBarIcon size={15} color="#b3471c" />,
      accent: '#b3471c',
      getValue: (col: DayCol) => {
        if (previewMode) {
          const v = payrollPreview ? previewDayMin(col.date, 'doubleTimeMinutes') : 0;
          return v > 0 ? fmtHrsOrDash(v) : null;
        }
        return col.totalDTMin > 0 ? fmtHrsOrDash(col.totalDTMin) : null;
      },
      getWeekTotal: () => previewMode
        ? (previewWeek && previewWeek.doubleTimeMinutes > 0 ? fmtHrsOrDash(previewWeek.doubleTimeMinutes) : '—')
        : (totalDTMin > 0 ? fmtHrsOrDash(totalDTMin) : '—'),
    },
    // Design §5 — the Piece/Warehouse ÷ Hourly breakdown sits directly under
    // Double Time and reads as a tinted pair.
    ...splitMatrixRows,
    {
      key: 'holpto',
      label: 'Hol / PTO',
      subLabel: '',
      icon: <Sun size={15} color="#7a5cf0" />,
      accent: '#7a5cf0',
      getValue: (col: DayCol) => col.holPTOEligHours > 0 ? formatDecimalHours(col.holPTOEligHours, 1) : null,
      getWeekTotal: () => totalHolPTOHrs > 0 ? formatDecimalHours(totalHolPTOHrs, 1) : '—',
    },
    // Design §4: on a production tenant the `Piece $` row is REPLACED by `Total $` in
    // the same slot, so the row order (and everything below it) is unchanged.
    ...(productionPayOn ? [{
      key: 'prod',
      label: 'Total $',
      subLabel: '',
      icon: <DollarSign size={15} color="#0e7c86" />,
      accent: '#0e7c86',
      noCellJump: true,
      getValue: () => null,
      getWeekTotal: () => (productionModel.weekTotal > 0 ? prodMoney(productionModel.weekTotal) : '—'),
      getCellNode: renderProdCell,
      getCellClass: (col: DayCol) =>
        (productionModel.byDate[col.date]?.visible.length ?? 0) > 0
          ? 'etrc-prod-td'
          : 'etrc-prod-td inert',
    } as MatrixRow] : [{
      key: 'piece',
      label: 'Piece $',
      subLabel: '',
      icon: <Package size={15} color="#2f6fed" />,
      accent: '#2f6fed',
      // Design §4: on a combine-in-week tenant a per-day piece figure does not
      // exist, so every day column dashes and the week cell becomes the jump
      // button into the Weekly Tasks Summary.
      labelTag: weeklyPiecesOn ? 'weekly' : undefined,
      getValue: (col: DayCol) =>
        weeklyPiecesOn ? null : (col.totalPiece > 0 ? fmtDollar(col.totalPiece) : null),
      getWeekTotal: () => fmtDollar(totalPiece),
      getWeekTotalNode: weeklyPiecesOn && totalPiece > 0
        ? () => (
            <button
              type="button"
              className="etrc-weekly-jump"
              title="Pieces are claimed for the week — jump to the Weekly Tasks Summary"
              onClick={jumpToWeekly}
            >
              {fmtDollar(totalPiece)}
              <ArrowRight size={13} />
            </button>
          )
        : undefined,
      // Misc dollars are folded into totalPiece; the marker calls out which
      // days actually carry misc claims so they get a closer look.
      getMarker: (col: DayCol) => (!weeklyPiecesOn && col.miscCount > 0) ? (
        <span
          className="etrc-matrix-misc"
          title={`${col.miscCount} misc task${col.miscCount === 1 ? '' : 's'} · ${fmtDollar(col.miscAmount)}`}
        >
          <Sparkles size={10} /> Misc
        </span>
      ) : null,
      // Week-level counterpart. Carries the count because it stands for the whole week,
      // and it is the ONLY misc signal on a combine-in-week tenant.
      getWeekMarker: () => totalMiscCount > 0 ? (
        <span
          className="etrc-matrix-misc"
          title={`${totalMiscCount} misc task${totalMiscCount === 1 ? '' : 's'} claimed this week · ${fmtDollar(totalMiscAmount)}`}
        >
          <Sparkles size={10} /> {totalMiscCount} Misc
        </span>
      ) : null,
    } as MatrixRow]),
    // Work orders exist as a feature only where the Work Orders pages are on
    // (WCDP today) — everyone else gets a permanently-dashed row otherwise.
    ...(config.showWorkOrdersPage ? [{
      key: 'workorder',
      label: 'Work Order $',
      subLabel: '',
      icon: <ClipboardList size={15} color="#7c4dcc" />,
      accent: '#7c4dcc',
      getValue: (col: DayCol) => col.totalWorkOrder > 0 ? fmtDollar(col.totalWorkOrder) : null,
      getWeekTotal: () => totalWorkOrder > 0 ? fmtDollar(totalWorkOrder) : '—',
    } as MatrixRow] : []),
    {
      key: 'alerts',
      label: 'Alerts',
      subLabel: '',
      icon: <Bell size={15} color="#6b7686" />,
      accent: '#6b7686',
      isIssueRow: true,
      getValue: (col: DayCol) => col.issues.length > 0 ? String(col.issues.length) : null,
      getWeekTotal: () => totalAlertsWithHoliday > 0 ? String(totalAlertsWithHoliday) : '✓',
    },
  ];

  return (
    <div className="etrc">
      {/* ── Employee Header Card */}
      <div className="etrc-header-card">
        <div className="etrc-header-main">
          <div className="etrc-avatar">{initials}</div>
          <div className="etrc-ee-info">
            <div className="etrc-ee-name">
              {fullName}
              {altEmployeeID && <span className="etrc-ee-id"> #{altEmployeeID}</span>}
              {/* M1-B3: the accepted v2 badge (outlined pill, user-x glyph) replaces
                  the interim plain-text badge from M1-B2. */}
              {showInactiveBadge && <InactiveBadge />}
            </div>
            <div className="etrc-ee-status-row">
              <span className="etrc-ee-status-label">STATUS</span>
              <span className="etrc-ee-status-divider">|</span>
              <span className={`etrc-status-pill ${(selectedStatus ?? 'unsubmitted').toLowerCase()}`}>{statusLabel}</span>
              {submittedStamp && (
                <span className="etrc-submitted-line">
                  <Send size={14} color="#8a93a2" />
                  {submittedByPayroll ? (
                    <>
                      <span className="etrc-src-marker">Submitted by payroll</span>
                      {timesheetData.submittedByAdminName && <b>{timesheetData.submittedByAdminName}</b>}
                    </>
                  ) : (
                    <>Submitted</>
                  )}
                  <span className="dt">
                    {submittedStamp.text}
                    {submittedStamp.utc && <span className="etrc-utc-tag">UTC</span>}
                  </span>
                </span>
              )}
              {/* TAS-679 (WCDP Modified Duty): only on a 2+ slot payload. */}
              {mdSlots && selectedStatus !== 'unsubmitted' && <ModifiedDutyApprovalLine slots={mdSlots} />}
            </div>
            <div className="etrc-ee-meta">
              {jobTitle && <span>{jobTitle}</span>}
              {phoneNumber && <span>{formatPhone(phoneNumber)}</span>}
              {showEmployeeRate && (
                <span>Rate <span style={{ color: 'var(--text)', fontWeight: 600 }}>{fmtMwMoney(hourlyRate)}/hr</span></span>
              )}
              {supervisorName && (
                <span>Supervisor <span style={{ color: 'var(--text)', fontWeight: 600 }}>{supervisorName}</span></span>
              )}
            </div>
          </div>
          <div className="etrc-header-right">
            <div className="etrc-week-nav">
              <WeekPickerNav payPeriods={payPeriods} selectedIdx={reviewWeekIdx} onChange={onSelectWeek} />
            </div>
            {/* M1-B3 (design v2 shots 02/03): the header hint explaining the payroll
                actions — inactive variant when the employee is terminated. */}
            {showPayrollActions && (
              <div className="etrc-payroll-hint">
                {timesheetData.active === false
                  ? 'Inactive employee with pending time. Payroll acts here; the employee cannot.'
                  : 'Never submitted — payroll can submit or edit on this employee’s behalf.'}
              </div>
            )}
            <div className="etrc-header-actions">
              <button type="button" className="etrc-btn small" onClick={onRefresh}>
                <RefreshCw size={13} />Refresh
              </button>
              {/* Moved up from the sticky action bar (product-owner request, 2026-08-09):
                  same gates as before — Change history renders on submitted AND
                  unsubmitted weeks; Edit as payroll on the unsubmitted branch only. */}
              {showChangeHistory && (
                <button type="button" className="etrc-btn small" onClick={() => setOpenPanel('change')}>
                  <History size={13} />Change history
                </button>
              )}
              {/* Audit log (v1 "Audit" modal, redesigned as a side panel). Tenant
                  flag only — no permission gate, all statuses including
                  Unsubmitted: punch edits happen pre-submit. */}
              {auditLogOn && !!currentPayPeriod && (
                <button type="button" className="etrc-btn small" onClick={openAuditPanel}>
                  <ScrollText size={13} />Audit log
                </button>
              )}
              {/* Change Supervisor (v1 port). The board owns the gate and the picker —
                  the callback is passed only when the caller may reassign this
                  timesheet, so there is nothing to check here. */}
              {onChangeSupervisor && !noTimesheetForWeek && (
                <button type="button" className="etrc-btn small" onClick={onChangeSupervisor}>
                  <UserCog size={13} />Change supervisor
                </button>
              )}
              {showPayrollActions && onEditAsPayroll && (
                <button type="button" className="etrc-btn small" onClick={onEditAsPayroll}>
                  <Pencil size={13} />Edit as payroll
                </button>
              )}
              {showPayrollActions && onSubmitForEmployee && (
                <button
                  type="button"
                  className="etrc-btn small primary"
                  onClick={onSubmitForEmployee}
                  disabled={payrollSubmitDisabled}
                  title={payrollSubmitDisabled ? 'No entries to submit' : undefined}
                >
                  <CheckCircle size={13} />Submit for employee
                </button>
              )}
              {canPrint && !noTimesheetForWeek && (
                <button type="button" className="etrc-btn small" onClick={handlePrint} disabled={printLoading}>
                  <Printer size={13} />{printLoading ? 'Printing…' : 'Print'}
                </button>
              )}
              {canUnlock && !noTimesheetForWeek && (
                <button type="button" className="etrc-btn small" onClick={() => onSendBack(timesheetData.timesheetID)}>
                  <Unlock size={13} />Unlock
                </button>
              )}
              {canApprove && !noTimesheetForWeek && (
                <button
                  type="button"
                  className="etrc-btn small primary"
                  onClick={handleApproveClick}
                  disabled={approveLoading || slotBusy}
                >
                  <CheckCircle size={13} />
                  {approveLoading || slotBusy ? 'Approving…' : 'Approve Week'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Week-change loading overlay */}
      {reviewLoading && (
        <div className="etrc-week-loading">
          <div className="etrc-week-spinner" />
          <span>Loading…</span>
        </div>
      )}

      {/* ── No timesheet for this week */}
      {!reviewLoading && noTimesheetForWeek && (
        <div className="etrc-no-timesheet">
          <ClipboardList size={36} />
          <div className="etrc-no-timesheet-title">No Timesheet This Week</div>
          <div className="etrc-no-timesheet-sub">This employee has no timesheet on record for the selected week.</div>
        </div>
      )}

      {/* ── Body (hidden when loading or no timesheet) */}
      {!reviewLoading && !noTimesheetForWeek && <>

      {/* ── KPI Strip */}
      <div className={`etrc-kpi-strip${onCallShown ? ' etrc-kpi-strip-6' : ''}`}>
        <div className="etrc-kpi-tile">
          <div className="etrc-kpi-label"><Clock size={13} />Total Hours</div>
          <div className="etrc-kpi-value">{formatHours(totalWorkMin)}</div>
        </div>
        <div className="etrc-kpi-tile">
          <div className="etrc-kpi-label" style={{ color: '#d4881a' }}><OTBarIcon size={13} color="#d4881a" />OT / DT</div>
          {/* previewMode (M1-B3, closed decision 1): OT/DT from the server preview,
              dash while it loads — same policy as the matrix, one number on screen. */}
          {previewMode ? (
            <>
              <div className="etrc-kpi-value" style={{ color: (previewWeek?.overtimeMinutes ?? 0) > 0 ? '#d4881a' : undefined }}>
                {previewWeek ? fmtHrsOrDash(previewWeek.overtimeMinutes) : '—'}
              </div>
              {(previewWeek?.doubleTimeMinutes ?? 0) > 0 && (
                <div className="etrc-kpi-sub" style={{ color: '#b3471c' }}>{fmtHrsOrDash(previewWeek!.doubleTimeMinutes)} DT</div>
              )}
            </>
          ) : (
            <>
              <div className="etrc-kpi-value" style={{ color: totalOTMin > 0 ? '#d4881a' : undefined }}>
                {fmtHrsOrDash(totalOTMin)}
              </div>
              {totalDTMin > 0 && <div className="etrc-kpi-sub" style={{ color: '#b3471c' }}>{fmtHrsOrDash(totalDTMin)} DT</div>}
            </>
          )}
        </div>
        <div className="etrc-kpi-tile">
          <div className="etrc-kpi-label" style={{ color: '#7a5cf0' }}><Sun size={13} />Hol / PTO</div>
          <div className="etrc-kpi-value" style={{ color: totalHolPTOHrs > 0 ? '#7a5cf0' : undefined }}>
            {totalHolPTOHrs > 0 ? formatDecimalHours(totalHolPTOHrs, 1) : '—'}
          </div>
          {holidays.length > 0 && <div className="etrc-kpi-sub">{holidays.length} holiday{holidays.length !== 1 ? 's' : ''}</div>}
        </div>
        {/* §2: `Piece $` → `Total $` on a production tenant. Same tile, same slot; the
            subline carries the open blockers or says everything is in. */}
        {productionPayOn ? (
          <div className="etrc-kpi-tile">
            <div className="etrc-kpi-label" style={{ color: '#0e7c86' }}><DollarSign size={13} />Total $</div>
            <div className="etrc-kpi-value" style={{ color: productionModel.weekTotal > 0 ? '#0e7c86' : undefined }}>
              {productionModel.weekTotal > 0 ? prodMoney(productionModel.weekTotal) : '—'}
            </div>
            {productionModel.applies && (
              <div className="etrc-kpi-sub">
                {prodActiveBlockers.length > 0
                  ? prodActiveBlockers.join(' · ')
                  : productionModel.hasBlockers
                    ? '—' /* totals still open, but not nagged until submission */
                    : 'all codes entered'}
              </div>
            )}
          </div>
        ) : (
          <div className="etrc-kpi-tile">
            <div className="etrc-kpi-label" style={{ color: '#2f6fed' }}><Package size={13} />Piece $</div>
            <div className="etrc-kpi-value" style={{ color: totalPiece > 0 ? '#2f6fed' : undefined }}>
              {totalPiece > 0 ? `$${totalPiece.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
            </div>
            {totalPieceCount > 0 && (
              <div className="etrc-kpi-sub">
                {totalPieceCount} piece{totalPieceCount !== 1 ? 's' : ''}
                {weeklyPiecesOn ? ' · claimed weekly' : ''}
              </div>
            )}
            {/* Misc dollars are folded into the Piece $ figure above, so without this the
                week gives no sign it carries misc claims at all — on a combine-in-week
                tenant not even the per-day cells do. */}
            {totalMiscCount > 0 && (
              <div
                className="etrc-kpi-misc"
                title={`${totalMiscCount} misc task${totalMiscCount === 1 ? '' : 's'} claimed this week · ${fmtDollar(totalMiscAmount)}`}
              >
                <Sparkles size={11} /> {totalMiscCount} Misc · {fmtDollar(totalMiscAmount)}
              </div>
            )}
          </div>
        )}
        {onCallShown && renderOnCallTile()}
        <div className="etrc-kpi-tile">
          <div className="etrc-kpi-label" style={{ color: '#6b7686' }}><Bell size={13} />Alerts</div>
          <div className="etrc-kpi-value" style={{ color: totalAlertsWithHoliday > 0 ? '#6b7686' : '#1f9d6b' }}>
            {totalAlertsWithHoliday > 0 ? totalAlertsWithHoliday : '✓'}
          </div>
        </div>
      </div>

      {/* ── TAS-514: Cust. service callout (NPM) ─────────────────────────────────
          The week's service orders and their scopes. Not an exception banner: an
          unmatched service order is normal for the ~11 days a residential SO takes to
          reach Vista, so the unvalidated lines are marked, not alarmed. */}
      {serviceOrderSummary.length > 0 && (
        <div className="etrc-so-callout">
          <div className="etrc-so-callout-head">
            <Wrench size={14} />
            <strong>Customer service</strong>
            <span className="etrc-so-callout-count">
              {serviceOrderSummary.length} service order{serviceOrderSummary.length !== 1 ? 's' : ''}
            </span>
          </div>
          <ul className="etrc-so-callout-list">
            {serviceOrderSummary.map((so) => (
              <li key={`${so.number}|${so.scope}`}>
                <span className="etrc-so-badge">{so.number}</span>
                {so.name && <span className="etrc-so-callout-name">{so.name}</span>}
                {so.scope && <span className="etrc-so-callout-scope">{so.scope}</span>}
                {so.state && <span className="etrc-so-state">{so.state}</span>}
                <span className="etrc-so-callout-punches">
                  {so.punches} punch{so.punches !== 1 ? 'es' : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Ineligible Holiday Banner */}
      {ineligibleHolidays.length > 0 && (
        <div className="etrc-holiday-banner">
          <AlertTriangle size={16} color="#e0533f" />
          <span>
            <strong>Holiday eligibility issue:</strong>{' '}
            {ineligibleHolidays
              .flatMap((h) => holidayIneligibilityPhrases(h, holidayEligMap[h.holidayCalendarID]))
              .join('; ')}.{' '}
            Holiday pay will be <strong>$0</strong>.
          </span>
        </div>
      )}

      {/* ── Time Off Gate Banner */}
      {relevantTimeOffRequests.length > 0 && (
        <div className={`etrc-to-banner${hasPendingTimeOff ? '' : ' resolved'}`}>
          <Sun size={15} color={hasPendingTimeOff ? '#d4881a' : '#1f9d6b'} />
          <span>
            {hasPendingTimeOff
              ? <><strong>Pending time-off</strong> for this pay period — resolve all requests before approving the timesheet.</>
              : <><strong>All time-off requests</strong> for this pay period have been resolved.</>
            }
          </span>
        </div>
      )}

      {/* ── Production gate banners (design §3) — directly below the time-off block,
          above the week grid. Only for an employee who actually owes production
          data: a required role, or a purchase-order day. The PO # obligation and
          the totals obligation are SEPARATE warnings: PO #s bind every role and
          show at any stage, while "Production totals incomplete" only nags once
          the week is submitted, and only for a RequireProductionPay role. */}
      {productionPayOn && productionModel.applies && (
        prodLocked ? (
          <div className="etrc-to-banner resolved">
            <CheckCircle size={15} color="#1f9d6b" />
            <span>
              <strong>Production totals are locked.</strong> This week has been payroll approved —
              amounts and PO #s are read-only. Unlock the week to make corrections.
            </span>
          </div>
        ) : (
          <>
            {productionModel.poBlockers.length > 0 && (
              <div className="etrc-to-banner etrc-prod-banner">
                <AlertTriangle size={15} color="#d4881a" />
                <span>
                  <strong>Purchase orders need a PO #.</strong> This week cannot be approved until
                  every purchase order has its PO # entered.
                  <span className="etrc-prod-blk">
                    {productionModel.poBlockers.map((b) => <span key={b}>{b}</span>)}
                  </span>
                </span>
                <button
                  type="button"
                  className="etrc-prod-banact"
                  onClick={() => prodFocusField(prodFirstNeededPo(), 'grid')}
                >
                  Start entering<ChevronRight size={14} />
                </button>
              </div>
            )}
            {isSubmitted && prodRoleRequires && productionModel.productionBlockers.length > 0 && (
              <div className="etrc-to-banner etrc-prod-banner">
                <AlertTriangle size={15} color="#d4881a" />
                <span>
                  <strong>Production totals incomplete.</strong> This week cannot be approved until
                  every worked account code has a total of at least its base pay.
                  <span className="etrc-prod-blk">
                    {productionModel.productionBlockers.map((b) => <span key={b}>{b}</span>)}
                  </span>
                  <span className="etrc-prod-hint">
                    Tab or Enter jumps to the next box that needs a value — straight across the week.
                  </span>
                </span>
                <button
                  type="button"
                  className="etrc-prod-banact"
                  onClick={() => prodFocusField(prodFirstNeeded(), 'grid')}
                >
                  Start entering<ChevronRight size={14} />
                </button>
              </div>
            )}
            {!productionModel.hasBlockers && (
              <div className="etrc-to-banner resolved">
                <CheckCircle size={15} color="#1f9d6b" />
                <span>
                  {prodRoleRequires ? (
                    <>
                      <strong>Production totals complete.</strong> Every worked account code on this
                      week has a total at or above base pay — {prodMoney(productionModel.weekTotal)}{' '}
                      entered.
                    </>
                  ) : (
                    <>
                      <strong>Production totals complete.</strong> Every purchase order on this week
                      has a PO #.
                    </>
                  )}
                </span>
              </div>
            )}
          </>
        )
      )}

      {/* ── Week Matrix */}
      <div className="etrc-matrix-card">
        {/* §4: the production grid gets a floor width so per-code entry blocks are
            never crushed; the card already scrolls horizontally. */}
        <table className={`etrc-matrix${productionPayOn ? ' etrc-matrix-prod' : ''}`}>
          <thead>
            <tr>
              <th className="etrc-matrix-row-label" />
              {dayColumns.map((col) => (
                <th
                  key={col.date}
                  className={`etrc-matrix-col-header${activeCol === col.date ? ' active' : ''}`}
                  onClick={() => jumpToDay(col.date)}
                >
                  <span className="etrc-matrix-dow">{col.dow}</span>
                  <span className="etrc-matrix-date">{col.dateLabel}</span>
                </th>
              ))}
              <th className="etrc-matrix-total-header">Week</th>
            </tr>
          </thead>
          <tbody>
            {matrixRows.map((row) => (
              <tr
                key={row.key}
                className={`${row.isIssueRow ? 'etrc-issues-row' : ''}${row.splitRow ? ' etrc-matrix-split' : ''}`.trim()}
              >
                <td className="etrc-matrix-row-label">
                  <div className="etrc-matrix-label-inner">
                    {row.icon}
                    <span>{row.label}</span>
                    {row.labelTag && <span className="etrc-weekly-tag">{row.labelTag}</span>}
                  </div>
                  {row.subLabel && <span className="etrc-matrix-sublabel">{row.subLabel}</span>}
                </td>
                {dayColumns.map((col) => {
                  const val = row.getValue(col);
                  const sub = row.getSubValue?.(col);
                  return (
                    <td
                      key={col.date}
                      className={`etrc-matrix-cell${activeCol === col.date ? ' active' : ''}${row.isIssueRow && col.issues.length > 0 ? ' issue-cell' : ''}${row.getCellClass ? ` ${row.getCellClass(col)}` : ''}`}
                      onClick={row.noCellJump ? undefined : () => jumpToDay(col.date)}
                    >
                      {row.getCellNode ? (
                        row.getCellNode(col)
                      ) : row.isIssueRow ? (
                        <div className="etrc-matrix-flags">
                          {/* Overnight rides in the same 3-badge budget (so the cell never
                              grows) but is informational: it is not in col.issues, so the
                              counts, the week ✓ and the ALERTS tile ignore it. */}
                          {col.issues.length > 0 || col.overnightLabel
                            ? [...col.issues, ...(col.overnightLabel ? ['overnight' as const] : [])].slice(0, 3).map((issue) => (
                                <IssueFlagBadge
                                  key={issue}
                                  issue={issue}
                                  labelOverride={issue === 'overnight' ? col.overnightLabel : undefined}
                                  onClick={issue === 'minwage' && col.day?.minWageTrueUp
                                    ? (e) => {
                                        e.stopPropagation();
                                        setMinWageModal({ trueUp: col.day!.minWageTrueUp!, dow: col.dow, dateLabel: col.dateLabel });
                                      }
                                    : undefined}
                                />
                              ))
                            : <span className="etrc-matrix-empty">—</span>
                          }
                          {col.issues.length + (col.overnightLabel ? 1 : 0) > 3 && (
                            <span className="etrc-matrix-more">+{col.issues.length + (col.overnightLabel ? 1 : 0) - 3}</span>
                          )}
                        </div>
                      ) : val ? (
                        <>
                          <span
                            className="etrc-matrix-val"
                            style={{ color: row.accent ?? undefined }}
                          >
                            {val}
                          </span>
                          {sub && (
                            <span className="etrc-matrix-subval" style={{ color: '#b3471c' }}>{sub}</span>
                          )}
                          {row.getMarker?.(col)}
                        </>
                      ) : (
                        <span className="etrc-matrix-empty">—</span>
                      )}
                    </td>
                  );
                })}
                <td
                  className={`etrc-matrix-total${row.isIssueRow ? ` issues-total${totalAlertsWithHoliday > 0 ? ' has-issues' : ''}` : ''}`}
                >
                  {row.isIssueRow ? (
                    <span
                      className="etrc-matrix-val"
                      style={{ color: totalAlertsWithHoliday > 0 ? '#6b7686' : '#1f9d6b' }}
                    >
                      {row.getWeekTotal()}
                    </span>
                  ) : row.getWeekTotalNode ? (
                    row.getWeekTotalNode()
                  ) : (
                    <>
                      <span
                        className="etrc-matrix-val"
                        style={{ color: row.accent ?? undefined }}
                      >
                        {row.getWeekTotal()}
                      </span>
                      {row.getWeekSub?.() && (
                        <span className="etrc-matrix-subval" style={{ color: '#b3471c' }}>
                          {row.getWeekSub()}
                        </span>
                      )}
                    </>
                  )}
                  {/* After the value branches, so it shows whether the week cell is a
                      plain total or the weekly-pieces jump button. */}
                  {row.getWeekMarker?.()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Dedicated Time Off Block */}
      {relevantTimeOffRequests.length > 0 && (
        <div className="etrc-to-block">
          <div className="etrc-section-label">
            <Sun size={15} color="#7a5cf0" />
            <span>Time Off Requests</span>
            <span className="etrc-section-sub">
              {relevantTimeOffRequests.filter(r => r.status === 'PENDING').length} pending
            </span>
          </div>
          {relevantTimeOffRequests.map((req) => {
            const isDone = req.status !== 'PENDING';
            return (
              <div key={req.timeOffRequestID} className={`etrc-toreq${isDone ? ' done' : ''}`}>
                <div className="etrc-toreq-info">
                  <span className={`etrc-status-pill ${req.status.toLowerCase()}`}>{req.status}</span>
                  <span className="etrc-toreq-name">{req.employeeName}</span>
                  <span className="etrc-toreq-cat">{req.categoryName}</span>
                  <span className="etrc-toreq-dates">
                    {fmtShortDate(req.startDate.substring(0, 10))}
                    {req.startDate.substring(0, 10) !== req.endDate.substring(0, 10) && (
                      <> – {fmtShortDate(req.endDate.substring(0, 10))}</>
                    )}
                  </span>
                  <span className="etrc-toreq-hrs">{req.totalHours} hrs</span>
                </div>
                {!isDone && (
                  <div className="etrc-toreq-actions">
                    <button
                      className="etrc-btn small danger"
                      onClick={() => onDenyTimeOff(req.timeOffRequestID)}
                    >
                      Deny
                    </button>
                    <button
                      className="etrc-btn small primary"
                      onClick={() => onApproveTimeOff(req.timeOffRequestID)}
                    >
                      Approve
                    </button>
                  </div>
                )}
                {isDone && (
                  <span className="etrc-resolved-tag">
                    <CheckCircle size={13} /> Resolved
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Week Detail */}
      <div className="etrc-week-detail">
        <div className="etrc-week-detail-header">
          <Calendar size={15} color="#8a93a2" />
          <span>Week Detail</span>
          {expandableDates.length > 0 && (
            <div className="etrc-week-detail-actions">
              <button className="etrc-btn small" type="button" onClick={expandAllDays} disabled={allExpanded}>
                <ChevronDown size={14} /> Expand all
              </button>
              <button className="etrc-btn small" type="button" onClick={collapseAllDays} disabled={noneExpanded}>
                <ChevronRight size={14} /> Collapse all
              </button>
            </div>
          )}
        </div>

        {dayColumns.map((col) => {
          if (!dayCardIsRendered(col)) return null;
          return (
            <DayCard
              key={col.date}
              col={col}
              isExpanded={expandedDays.has(col.date)}
              isActive={activeCol === col.date}
              canSendBack={canUnlock}
              isSubmitted={isSubmitted}
              onToggle={() => toggleDay(col.date)}
              onGpsClick={setGpsModal}
              onDayMapClick={() => setDayMapCol(col)}
              onMinWageClick={() => {
                if (col.day?.minWageTrueUp) {
                  setMinWageModal({ trueUp: col.day.minWageTrueUp, dow: col.dow, dateLabel: col.dateLabel });
                }
              }}
              onSendBack={() => onSendBack(timesheetData.timesheetID)}
              onEditMiscTask={canEditMiscTaskCode
                ? (row, group) => setMiscTaskEdit({
                    miscEmployeeTaskID: row.miscEmployeeTaskID!,
                    accountCodeID: row.accountCodeID ?? null,
                    miscDescription: row.description ?? '',
                    projectName: group.projectName,
                    altProjectID: group.altProjectID,
                    amountClaimed: row.amount,
                    dow: col.dow,
                    dateLabel: col.dateLabel,
                  })
                : undefined}
              weeklyPieces={weeklyPiecesOn}
              onJumpToWeekly={weeklyPiecesOn ? jumpToWeekly : undefined}
              production={dayProductionProps(col)}
              punchHistory={punchHistoryMap}
              removedPunches={punchHistoryItems ? removedPunchesForDay(punchHistoryItems, col.date) : undefined}
              onPunchHistory={punchHistoryMap
                ? (items, title, subtitle, note) => setPunchHistoryModal({ items, title, subtitle, note })
                : undefined}
              cardRef={(el) => { dayCardRefs.current[col.date] = el; }}
            />
          );
        })}
      </div>

      {/* ── Weekly Tasks Summary (design §4) — between Week Detail and Approvals.
          `combineTasksInWeek` only; every other tenant renders nothing here. */}
      {weeklyPiecesOn && weeklyProjects.length > 0 && (
        <>
          <div className="etrc-appr-sect">
            <Package size={18} color="#8a93a2" />
            <span className="t">Weekly Tasks Summary</span>
            <span className="s">
              all pieces claimed for this week — not attributable to a single day
            </span>
            {/* The rows' own total, not the Piece $ KPI: operational-task dollars are
                folded into that KPI but keep their per-day "Meetings / Other Tasks"
                section, so they are not listed here and must not be counted here. */}
            <span className="etrc-weekly-total">
              Total Claimed <b>{fmtDollar(weeklyProjectTasksTotal(weeklyProjects))}</b>
            </span>
          </div>
          <div className="etrc-weekly-card" ref={weeklyRef}>
            {weeklyProjects.map((p) => (
              <React.Fragment key={p.project.projectID ?? p.project.altProjectID}>
                <div className="etrc-weekly-pjhead">
                  {p.project.altProjectID && (
                    <span className="etrc-weekly-pjcode">{p.project.altProjectID}</span>
                  )}
                  <span className="etrc-weekly-pjname">{p.project.name}</span>
                  <span className="etrc-weekly-pjtotal">Total Claimed {fmtDollar(p.totalAmountClaimed)}</span>
                </div>
                {p.employeeTasks.map((t) => {
                  const original = t.task.originalAmount ?? 0;
                  const pct = original > 0 ? Math.round((t.amountClaimed / original) * 100) : null;
                  return (
                    <div className="etrc-weekly-row" key={`t-${p.project.projectID}-${t.task.taskID}`}>
                      <span className="etrc-weekly-name">
                        {t.task.taskInfo?.name}
                        {(t.task.taskInfo?.description || t.intermediateName) && (
                          <span className="etrc-weekly-path">
                            {[t.task.taskInfo?.description, t.intermediateName].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </span>
                      <EntryTypeChip type="piece" />
                      <span className="etrc-weekly-val">
                        Claimed <b>{fmtDollar(t.amountClaimed)}</b>
                      </span>
                      <span className="etrc-weekly-val sub">Original {fmtDollar(original)}</span>
                      <span className="etrc-weekly-val sub">
                        Remaining {fmtDollar(t.task.remainingAmount ?? 0)}
                      </span>
                      <span className="etrc-weekly-pct">{pct != null ? `${pct}%` : '—'}</span>
                    </div>
                  );
                })}
                {p.miscEmployeeTasks.map((m, mi) => (
                  <div className="etrc-weekly-row" key={`m-${p.project.projectID}-${mi}`}>
                    <span className="etrc-weekly-name">
                      {m.miscDescription || 'Miscellaneous'}
                      {(m.accountCode?.name || m.intermediateName) && (
                        <span className="etrc-weekly-path">
                          {[
                            m.accountCode
                              ? [m.accountCode.altAccountCodeID, m.accountCode.name].filter(Boolean).join(' ')
                              : null,
                            m.intermediateName,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                    <EntryTypeChip type="misc" />
                    <span className="etrc-weekly-val">
                      Claimed <b>{fmtDollar(m.amountClaimed)}</b>
                    </span>
                    <span className="etrc-weekly-val sub" />
                    <span className="etrc-weekly-val sub" />
                    <span className="etrc-weekly-pct">—</span>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </>
      )}

      {/* ── Approvals card (design_handoff_timesheet_audit_approvals, View 2;
          extended by design_handoff_npm_timesheet_review §3). Read-only exactly
          as shipped, except on tenants with `allowManagerApproval`, where the
          slots of the CURRENT stage become actionable. */}
      {isSubmitted && (
        <>
          <div className="etrc-appr-sect">
            <ClipboardList size={18} color="#8a93a2" />
            <span className="t">Approvals</span>
          </div>
          <div className="etrc-appr">
            {showChainSection && (
              <div className="etrc-apprsec">
                <div className="hd">
                  <CheckCircle size={15} />Approval chain
                  <span className="cnt">{chainDone} of {chainHops.length} complete</span>
                </div>
                <div className="etrc-apprgrid">
                  {chainHops.map((h) => (
                    <ApprHop key={h.key} role={h.role} tile={h.tile} vm={h.vm} />
                  ))}
                </div>
              </div>
            )}
            {slotSections.map((sec) => (
              <div key={sec.key} className="etrc-apprsec">
                <div className="hd">
                  <CheckCircle size={15} />{sec.title}
                  {sec.tag && <span className="etrc-slot-kindtag">{sec.tag}</span>}
                  <span className={`cnt${sec.rows.every((r) => r.vm != null) ? ' ok' : ''}`}>
                    {sec.rows.filter((r) => r.vm != null).length} of {sec.rows.length} complete
                  </span>
                </div>
                {slotActionsOn ? (
                  <div className="etrc-slotlist">
                    {sec.rows.map((r) => (
                      <SlotRow
                        key={r.key}
                        ownerName={r.ownerName}
                        mine={r.mine}
                        vm={r.vm}
                        onBehalf={r.onBehalf}
                        noLogin={r.noLogin}
                        actionable={sec.actionable}
                        selected={selectedSlotKeys.has(r.key)}
                        busy={slotBusy}
                        onToggleSelect={() => toggleSlotSelection(r.key)}
                        onApproveOnBehalf={() => setOnBehalfTarget(r)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="etrc-apprgrid">
                    {sec.rows.map((r) => (
                      <ApprHop
                        key={r.key}
                        role={r.vm != null ? r.ownerName : sec.title.replace(/s$/, '')}
                        tile="sup"
                        vm={r.vm}
                        pendingName={r.ownerName}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
            {/* TAS-679 (WCDP Modified Duty) — supervisor + HR, both required. Rendered
                only for a 2+ slot payload; separate from the NPM slot UI above. */}
            {mdSlots && !atManagerStage && (
              <ModifiedDutyApprovalsSection
                timesheetID={timesheetData.timesheetID}
                slots={mdSlots}
                adminEmployeeID={adminEmployeeID}
                adminName={adminName}
                canApproveOnBehalf={canApproveOnBehalfModifiedDuty}
                actionable={atSubmittedStage && !reviewLoading && !noTimesheetForWeek}
                renderStamp={mdStamp}
                onWaiting={(msg) => { onToast?.(msg); onRefresh(); }}
                onAdvanced={(msg) => {
                  onToast?.(msg);
                  if (onStageAdvanced) onStageAdvanced('submitted');
                  else onRefresh();
                }}
              />
            )}
            {/* One card-level bar for the slots the signed-in admin owns; an admin
                who owns none gets no Approve button at all (design §3). */}
            {slotActionsOn && myOutstandingSlots.length > 0
              && slotSections.some((s) => s.actionable) && (
              <div className="etrc-slotbar">
                <span className="etrc-slotbar-note">
                  <UserCheck size={15} />
                  <b>{myOutstandingSlots.length}</b>&nbsp;slot{myOutstandingSlots.length === 1 ? '' : 's'} on
                  this timesheet {myOutstandingSlots.length === 1 ? 'is' : 'are'} yours — selected by default.
                </span>
                <span className="etrc-slotbar-grow" />
                <button
                  type="button"
                  className="etrc-btn primary"
                  disabled={slotBusy || selectedMineSlots.length === 0}
                  onClick={() => { void approveSelectedSlots(); }}
                >
                  <CheckCircle size={15} />
                  {slotBusy
                    ? 'Approving…'
                    : `Approve${selectedMineSlots.length > 0 ? ` ${selectedMineSlots.length} selected` : ''}`}
                </button>
              </div>
            )}
            <div className="etrc-apprnote">
              <Info size={15} />
              <div>
                This card shows the <b>current</b> approval round only. Unlocking a week deletes
                its approval rows{auditLogOn ? (
                  <> — <button type="button" onClick={openAuditPanel}>see the audit log</button> for the full history.</>
                ) : '.'}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Certifications Card */}
      {timesheetAnswers.length > 0 && (
        <div className="etrc-qa-card">
          <button
            type="button"
            className="etrc-qa-head"
            onClick={() => setCertOpen((o) => !o)}
          >
            <CalendarClock size={15} color="#8a93a2" />
            <span>Certifications</span>
            <span className="etrc-section-sub">{timesheetAnswers.length} question{timesheetAnswers.length !== 1 ? 's' : ''}</span>
            {certFlagged && (
              <span className="etrc-qa-indicator warn">
                <AlertCircle size={11} color="white" />
              </span>
            )}
            {!certFlagged && certHasOk && (
              <span className="etrc-qa-indicator ok">
                <CheckCircle size={11} color="white" />
              </span>
            )}
            <ChevronDown
              size={15}
              color="#8a93a2"
              className={`etrc-qa-chevron${certOpen ? ' open' : ''}`}
            />
          </button>
          {certOpen && (
            <div className="etrc-qa-body">
              {timesheetAnswers.map((a, i) => {
                const verdict = certVerdict(a);
                return (
                  <div key={a.timesheetAnswerID} className={`etrc-qa-row${verdict === 'flag' ? ' no' : ''}`}>
                    <span className="etrc-qa-num">{i + 1}</span>
                    <span className="etrc-qa-question">{a.timesheetQuestion.question}</span>
                    <span className={`etrc-answer-pill ${verdict === 'flag' ? 'no' : verdict === 'ok' ? 'yes' : 'neutral'}`}>
                      {a.answer}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Sticky Action Bar
          M1-B3, amended 2026-08-09 (twice): all three payroll controls — Change
          history, Edit as payroll, Submit for employee — now live in the header's
          small-button row per the product owner. The bar is back to its stock,
          pre-feature contents, so `has-payroll-actions` and its ≤980px
          wrap-and-unstick rule no longer apply here. */}
      <div className="etrc-action-bar">
        <div className="etrc-action-status">
          <ClipboardList size={16} color="var(--text-3)" />
          <span className="etrc-status-label">Status</span>
          <span className={`etrc-status-pill ${(selectedStatus ?? 'unsubmitted').toLowerCase()}`}>{statusLabel}</span>
        </div>

        <div className="etrc-action-note">
          {/* §7: on a production tenant the note lists every open blocker, time-off and
              production together, and reads out the week's total when it is clear. */}
          {productionPayOn && productionModel.applies && !prodLocked && (() => {
            const pending = relevantTimeOffRequests.filter((r) => r.status === 'PENDING').length;
            const reasons = [
              ...(pending > 0
                ? [`${pending} time-off request${pending === 1 ? '' : 's'} must be resolved`]
                : []),
              ...prodActiveBlockers,
            ];
            return reasons.length > 0 ? (
              <span className="etrc-pending-note">
                <AlertCircle size={14} color="#d4881a" />
                {reasons.join(' · ')}
              </span>
            ) : (
              <span className="etrc-pending-note">
                <CheckCircle size={14} color="#1f9d6b" />
                {prodRoleRequires
                  ? `${prodMoney(productionModel.weekTotal)} in production totals · all checks clear`
                  : 'all checks clear'}
              </span>
            );
          })()}
          {!(productionPayOn && productionModel.applies && !prodLocked) && hasPendingTimeOff && (
            <span className="etrc-pending-note">
              <AlertCircle size={14} color="#d4881a" />
              {relevantTimeOffRequests.filter(r => r.status === 'PENDING').length} pending time-off request{relevantTimeOffRequests.filter(r => r.status === 'PENDING').length !== 1 ? 's' : ''} for this pay period — resolve before approving
            </span>
          )}
          {/* Design §3.3: the 1st-Approval footer explains the missing Approve Week
              rather than leaving a bare Unlock. Outstanding slots only, this stage only. */}
          {atManagerStage && !hasPendingTimeOff && (
            outstandingStageSlots.length > 0 ? (
              <span className="etrc-pending-note">
                <Info size={14} color="#8a93a2" />
                <b>{outstandingStageSlots.length} approval{outstandingStageSlots.length === 1 ? '' : 's'} outstanding</b>
                &nbsp;— approve each slot in the Approvals card. There is no blanket Approve Week at this stage.
              </span>
            ) : (
              <span className="etrc-pending-note">
                <CheckCircle size={14} color="#1f9d6b" />
                All 1st-level approvals complete — this timesheet advances to&nbsp;
                <b>{bucketLabel('submitted')}</b>.
              </span>
            )
          )}
        </div>

        <div className="etrc-action-btns">
          {canUnlock && (
            <button
              className="etrc-btn"
              onClick={() => onSendBack(timesheetData.timesheetID)}
            >
              <Unlock size={15} />
              Unlock
            </button>
          )}
          {canApprove && (
            <button
              className="etrc-btn primary"
              onClick={handleApproveClick}
              disabled={approveLoading || slotBusy}
            >
              <CheckCircle size={15} />
              {approveLoading || slotBusy ? 'Approving…' : 'Approve Week'}
            </button>
          )}
        </div>
      </div>

      {/* ── Payroll change-history side panel (submitted AND unsubmitted weeks —
          the one door to the audit trail that survives submission) */}
      {showChangeHistory && (
        <HistoryPanel
          open={openPanel === 'change'}
          employeeID={timesheetData.employeeID}
          payPeriodID={currentPayPeriod.PayPeriodID}
          employeeName={fullName}
          periodLabel={formatPayPeriodRange(currentPayPeriod)}
          onClose={() => setOpenPanel(null)}
        />
      )}

      {/* ── Audit log side panel — the Audit-table event trail, incl. rounds
          cleared by an unlock. Tenant flag only; shares the panel slot with
          Change history. */}
      {auditLogOn && currentPayPeriod && (
        <AuditLogPanel
          open={openPanel === 'audit'}
          onClose={() => setOpenPanel(null)}
          employeeName={fullName}
          periodLabel={formatPayPeriodRange(currentPayPeriod)}
          rows={auditRows}
          loading={auditLoading}
          error={auditError}
          onRetry={() => { void loadAudit(); }}
        />
      )}

      {/* ── Per-punch change history (TAS-365) */}
      {punchHistoryModal && (
        <PunchHistoryModal
          items={punchHistoryModal.items}
          title={punchHistoryModal.title}
          subtitle={punchHistoryModal.subtitle}
          note={punchHistoryModal.note ?? null}
          employeeName={fullName}
          onClose={() => setPunchHistoryModal(null)}
        />
      )}

      {/* ── GPS Map Modal (one flagged punch) */}
      {gpsModal && <GpsMapModal data={gpsModal} onClose={() => setGpsModal(null)} />}

      {/* ── Day Punch Map (every punch on the day) */}
      {dayMapCol?.day && (
        <DayPunchMapModal
          day={dayMapCol.day}
          dow={dayMapCol.dow}
          dateLabel={dayMapCol.dateLabel}
          onClose={() => setDayMapCol(null)}
        />
      )}

      {/* ── Min Wage True-Up Modal */}
      {minWageModal && <MinWageModal data={minWageModal} onClose={() => setMinWageModal(null)} />}
      {miscTaskEdit && (
        <MiscTaskCodeModal
          target={miscTaskEdit}
          onClose={() => setMiscTaskEdit(null)}
          onSaved={onRefresh}
        />
      )}

      {/* ── Edit account codes & hours (design §6) — opened from any week-grid pencil
          or any punch row in a day card. */}
      {prodSplit && (
        <ProductionSplitModal
          target={prodSplit}
          showAmounts={prodRoleRequires}
          onClose={() => setProdSplit(null)}
          onSaved={onRefresh}
        />
      )}

      {/* ── On Call $ amount + Sage coding — from the On Call $ tile (fresh, Change or View). */}
      {onCallModal && onCallShown && currentPayPeriod && (
        <OnCallPayModal
          employeeName={fullName}
          weekLabel={formatPayPeriodRange(currentPayPeriod)}
          initialAmount={onCallModal.amount}
          saved={onCallSaved}
          options={onCallSoOptions}
          costCodes={ONCALL_COST_CODES}
          payType={ONCALL_PAY_TYPE}
          max={onCallMax}
          readOnly={onCallLocked}
          onClose={closeOnCallModal}
          onSave={saveOnCall}
        />
      )}

      {/* ── Print result modal */}
      {printResult && (
        <div className="etrc-overlay" onClick={() => setPrintResult(null)}>
          <div className="etrc-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="etrc-confirm-header">
              {printResult === 'success'
                ? <CheckCircle size={20} color="#1f9d6b" />
                : <AlertCircle size={20} color="#e0533f" />}
              <span>{printResult === 'success' ? 'Timecard ready' : 'Print failed'}</span>
            </div>
            <p className="etrc-confirm-body">
              {printResult === 'success'
                ? 'The timecard PDF was generated successfully. Check your downloads for the file.'
                : 'We couldn’t generate the timecard PDF. Please try again.'}
            </p>
            <div className="etrc-confirm-actions">
              <button className="etrc-btn primary" onClick={() => setPrintResult(null)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Approve-on-behalf confirm (design §3). Names both people and says
          plainly that the record is permanent short of an unlock. */}
      {onBehalfTarget && (
        <div className="etrc-overlay" onClick={() => setOnBehalfTarget(null)}>
          <div className="etrc-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="etrc-confirm-header">
              <User size={20} color="#2f6fed" />
              <span>Approve on behalf of {onBehalfTarget.ownerName}?</span>
            </div>
            <p className="etrc-confirm-body">
              This slot belongs to <b>{onBehalfTarget.ownerName}</b>. You are{' '}
              <b>{adminName || 'the signed-in admin'}</b> — the approval will be recorded as{' '}
              <b>approved by {adminName || 'you'} on behalf of {onBehalfTarget.ownerName}</b> and
              cannot be undone without unlocking the week.
            </p>
            <div className="etrc-confirm-actions">
              <button className="etrc-btn" onClick={() => setOnBehalfTarget(null)} disabled={slotBusy}>
                Cancel
              </button>
              <button className="etrc-btn primary" onClick={confirmOnBehalf} disabled={slotBusy}>
                <CheckCircle size={15} />Approve on behalf
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Per-slot failure report. Each slot is its own POST and can refuse with
          a HTTP-200 body, so failures are named rather than dropped (errata 6). */}
      {slotFailures && slotFailures.length > 0 && (
        <div className="etrc-overlay" onClick={() => setSlotFailures(null)}>
          <div className="etrc-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="etrc-confirm-header">
              <AlertCircle size={20} color="#e0533f" />
              <span>
                {slotFailures.length} approval{slotFailures.length === 1 ? '' : 's'} did not go through
              </span>
            </div>
            <ul className="etrc-confirm-issues">
              {slotFailures.map((f, i) => (
                <li key={i}><b>{f.name}</b> — {f.reason}</li>
              ))}
            </ul>
            <div className="etrc-confirm-actions">
              <button className="etrc-btn primary" onClick={() => setSlotFailures(null)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Pending time-off blocking modal */}
      {timeOffBlockOpen && (
        <div className="etrc-overlay" onClick={() => setTimeOffBlockOpen(false)}>
          <div className="etrc-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="etrc-confirm-header">
              <Sun size={20} color="#7a5cf0" />
              <span>Pending time-off requests</span>
            </div>
            <p className="etrc-confirm-body">
              There {relevantTimeOffRequests.filter(r => r.status === 'PENDING').length === 1 ? 'is' : 'are'}{' '}
              <strong>{relevantTimeOffRequests.filter(r => r.status === 'PENDING').length} pending time-off
              request{relevantTimeOffRequests.filter(r => r.status === 'PENDING').length !== 1 ? 's' : ''}</strong>{' '}
              for this pay period. Approve or deny all requests before approving the timesheet.
            </p>
            <div className="etrc-confirm-actions">
              <button className="etrc-btn primary" onClick={() => setTimeOffBlockOpen(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Production gate block (design §3 / Rule 3 — no reviewer override).
          Titled by what is actually blocking: PO #s bind every role, totals only a
          RequireProductionPay role. */}
      {prodBlockOpen && (
        <div className="etrc-overlay" onClick={() => setProdBlockOpen(false)}>
          <div className="etrc-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="etrc-confirm-header">
              <AlertTriangle size={20} color="#d4881a" />
              <span>
                {prodRoleRequires && productionModel.productionBlockers.length > 0
                  ? 'Production totals incomplete'
                  : 'Purchase orders need a PO #'}
              </span>
            </div>
            <p className="etrc-confirm-body">
              {prodRoleRequires && productionModel.productionBlockers.length > 0
                ? 'This week cannot be approved until every worked account code has a total of at least its base pay'
                  + (productionModel.poBlockers.length > 0 ? ', and every purchase order has a PO #.' : '.')
                : 'This week cannot be approved until every purchase order has its PO # entered.'}
            </p>
            <ul className="etrc-confirm-issues">
              {prodActiveBlockers.map((b) => <li key={b}>{b}</li>)}
            </ul>
            <div className="etrc-confirm-actions">
              <button className="etrc-btn" onClick={() => setProdBlockOpen(false)}>Close</button>
              <button
                className="etrc-btn primary"
                onClick={() => {
                  setProdBlockOpen(false);
                  window.setTimeout(() => prodFocusField(prodFirstNeeded(), 'grid'), 0);
                }}
              >
                Start entering
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Production toast — cap refusals, PO validation and the end of the run */}
      {prodToast && (
        <div className={`etrc-prod-toast${prodToast.bad ? ' bad' : ''}`} role="status">
          {prodToast.bad ? <AlertTriangle size={16} /> : <CheckCircle size={16} />}
          <span>{prodToast.msg}</span>
        </div>
      )}

      </> /* end body fragment */}
    </div>
  );
};

export default EmployeeTimesheetReviewConsole;
